/**
 * useChatThreads
 *
 * Per-instance thread/project store for the Slack-style thread switcher at
 * the bottom of GlobalChatSystem. Each thread owns an isolated message
 * array persisted under its own localStorage key so parallel projects never
 * mix, and so a user can switch between research threads, drafting threads,
 * etc. without losing context.
 *
 * Responsibilities
 * ────────────────
 *  • Maintain a recency-ordered list of `ChatThread` metadata in localStorage.
 *  • Track the active thread id for the current chat instance.
 *  • Continuously mirror the live `state.messages` array of the active
 *    thread into that thread's per-thread key so page reloads restore
 *    the right thread.
 *  • Derive a concise thread title from the first user message.
 *  • Expose `switchThread`, `newThread`, `removeThread`, `markUnread`, and
 *    `setProcessingForActive` helpers for the UI layer.
 *
 * Integration contract
 * ────────────────────
 * The hook does NOT own the messages themselves — it treats `useGlobalChat`'s
 * existing `state.messages` + `SET_MESSAGES` dispatch as the single source
 * of truth for the ACTIVE thread. When the user switches threads:
 *   1. `state.messages` is snapshotted into the outgoing thread's storage key
 *   2. The incoming thread's stored messages are loaded and dispatched via
 *      `SET_MESSAGES` so the chat UI updates atomically.
 *   3. The useGlobalChat persistence effect naturally writes the new active
 *      content back to the global chat history key, preserving existing
 *      cross-tab sync behaviour.
 *
 * This keeps the refactor surface tiny: no changes to useGlobalChat, no
 * changes to how messages stream / are persisted at the message level.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { boundThreadSummaryName } from '../../services/groupThreads/threadSummaryName';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
// MULTI-ORG P4: the workspace separation rule (leaf module, re-exported below).
import { filterThreadsForWorkspace, threadAllowedInWorkspace } from './workspaceThreadFilter';
import { readConfirmedWorkspace } from '../../services/prest0nRuntime/confirmedWorkspaceCache';
import {
  getGroupThreadHistoryFloorsVersion,
  subscribeGroupThreadHistoryFloors,
} from '../../services/groupThreads/historyFloors';
import {
  beginThreadArchiveNotice,
  clearThreadArchiveNotice,
  THREAD_ARCHIVE_NOTICE_HOLD_MS,
} from '../../services/threadArchiveNotice';
import {
  ArchivedThread,
  ChatMessage,
  ChatThread,
  GlobalChatAction,
  THREAD_TITLE_HISTORY_MAX,
  type ThreadTitleHistoryEntry,
  getGuestAdoptionMarkerKey,
  getGuestEraMarkerKey,
  getThreadStorageKeyPrefixes,
  getThreadStorageKeys,
  type TokenUsageSnapshot,
} from './types';
import {
  getFromLocalStorage,
  localStorageHasKey,
  setToLocalStorage,
  removeFromLocalStorage,
  htmlToPlainText,
} from './utils';
import { inferThreadLabel } from '../../services/threadMetadata/inferThreadLabel';
import { defaultThreadTitle, isPlaceholderThreadTitle } from '../../services/groupThreads/canonicalThreadTitle';
import {
  isThreadTombstonedInStorage,
  parseDurableTombstones,
  tombstoneStorageKey,
} from '../../services/groupThreads/archivedTombstones';
import { appendFormerTitle, authoritativeThreadTitle, renamedTitleFromHistory, titleIsUserOwned } from '../../services/threadMetadata/titleHistory';
import { resolveThreadTitleSettleEnabled } from '../../services/threadAddPeople/featureFlags';
import { getActiveWorkspaceAccount, onActiveWorkspaceResolved } from '../../services/activeWorkspace';
import { deterministicThreadSessionId } from '../../services/prest0nSession/client';
import {
  getUserUiLanguage,
  type UiLanguage,
} from '../../services/threadMetadata/uiLanguage';
import { detectTitleLanguage } from '../../services/threadMetadata/titleLanguage';
import { storedGreetingRank, isConvergentGreetingRank } from '../../services/threadMetadata/greetingRank';
import {
  safelyPersistThreadMessages,
  sanitizeMessagesForRehydrate,
  subscribeToThreadSync,
  purgeAllThreadStorage,
  purgeThreadCache,
  purgeThreadStorageForSessionReset,
  readJsonFromIdb,
  rehydrateThreadMessages,
  writeJsonWithIdbFallback,
} from './threadPersist';
import {
  PENDING_SOURCE_TAGS,
  PENDING_ANIMATION_MARKERS,
  isInFlightPlaceholderMessage,
  isOrphanedInFlightMessage,
  liveServerTaskIdOf,
  reconcileInterruptedOnHydrate,
  mergeSyncedMessagesPreservingFinalized,
  mergeRemoteThreadMessagesIntoLocal,
  oldestLoadedMessageTs,
  remoteFinalizeShouldReleaseProcessing,
} from './interruptedTurn';
import { collapseUserRowTwins } from '../../services/groupThreads/userRowTwins';
import { isLiveSendInFlight } from './liveSendRegistry';
import { sweepStorageAtBoot } from './storageQuota';
// TYPE-ONLY, and deliberately so: `import type` is fully erased under
// isolatedModules, so this adds no runtime edge to firebase from a module the
// chat-isolation battery loads (the `firebase/auth`-touches-fetch-at-import
// trap). The cursor is opaque here — it is only ever handed straight back to
// the repository that minted it.
import type { DocumentSnapshot } from 'firebase/firestore';
// From the Firebase-free leaf, NOT from the repository below: several suites
// replace that module with a jest.mock, and a factory that omits these would
// hand the watermark `undefined` — which reads as "this snapshot is full" and
// silently restores the loss. A leaf nobody mocks cannot be erased.
import { THREAD_PAGE_SIZE, WORKING_SET_LIMIT } from '../../repositories/chatThreadWindow';
import {
  acknowledgeRoutineHandoff,
  archiveChatThread,
  chatThreadIndexDocExists,
  getActiveThread,
  getChatThread,
  getChatThreadArchiveInfo,
  isChatThreadArchivedOnServer,
  loadOlderThreads as loadOlderThreadsPage,
  setActiveThread,
  subscribeChatThreadIndex,
  subscribeChatThreadIndexForWorkspace,
  upsertChatThread,
} from '../../repositories/chatThreadIndexRepository';
import {
  getOlderThreadMessages,
  getThreadMessages,
  getThreadMessagesPage,
  hasAnyThreadMessages,
  subscribeThreadMessages,
  upsertThreadMessages,
  type ThreadMessageDocsPageCursor,
} from '../../repositories/chatThreadMessagesRepository';
import { THREAD_MESSAGES_PAGE_SIZE } from '../../repositories/chatThreadMessageDocsRepository';
import { mergeLegacyAndPerDoc, perDocMessageId } from '../../types/chatThreadMessageDocFirestore';
import {
  mergeOwnAndGroupMessages,
  subscribeGroupThreadMessages,
} from '../../repositories/groupThreadMessagesRepository';
import { collectOwnDeliveredIds, deliveryKey, deliveryLedger } from '../../services/messageDelivery';
import { effectiveUnreadCount } from './threadArrival';
import { clearThreadUnseen } from '../../repositories/chatThreadIndexRepository';
import { subscribeThreadReceipts } from '../../repositories/threadReceiptsRepository';
import type { ReceiptsByReader } from '../../services/groupThreads/seenBy';
import { subscribeThreadReactions } from '../../repositories/messageReactionsRepository';
import { subscribeThreadLiveTurns } from '../../repositories/groupThreadLiveTurnsRepository';
import { subscribeThreadTyping } from '../../repositories/groupThreadTypingRepository';
import type { TypingView } from '../../services/groupThreads/typingPresence';
import type { LiveTurnView } from '../../services/groupThreads/liveTurns';
import { shouldListenForReactions } from '../../services/messageReactions/listenGate';
import type { ReactionsByMessage } from '../../services/messageReactions/reactionModel';
import { registerFirestoreListener } from '../../lib/auth/session';

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

// MAX_THREADS (a 24-per-workspace eviction cap) was DELETED on 2026-09-22.
// See `newThread` for why: it threw away thread records — and deleted their
// local transcripts — on every "+", which is the opposite of what the window
// watermark and the load-older page are for. Transcript bytes are bounded by
// `storageQuota.ts`, cold and largest-first.
const TITLE_MAX_LEN = 42;
/** Legacy lowercase default title (kept for backward-compat detection). */
const LEGACY_DEFAULT_TITLE = 'new thread';
/**
 * Current EN / ES default titles. Derived from `defaultThreadTitle` rather than
 * spelled again here: the display rule treats these exact strings as "no name
 * yet" (2026-09-13), so a literal that drifted would render as a real name.
 */
const DEFAULT_TITLE_EN = defaultThreadTitle('en');
const DEFAULT_TITLE_ES = defaultThreadTitle('es');
/** Default fallback when language is unknown. */
const DEFAULT_TITLE = DEFAULT_TITLE_EN;
const ENABLE_SERVER_THREAD_INDEX_DEFAULT = true;
const SERVER_THREAD_INDEX_FLAG_KEY = 'enableServerThreadIndex';
// Tombstones expire ONLY via this TTL (see the subscription handler for why
// snapshot absence must never clear them). 15 minutes: long enough to outlive
// offline/backoff retry windows on the archive write, short enough that a
// permanently-failed archive resurfaces the thread instead of hiding it
// forever while out of sync with the server.
const ARCHIVED_TOMBSTONE_TTL_MS = 15 * 60 * 1000;
/**
 * Expiry stamp for a tombstone whose server archive is CONFIRMED written.
 *
 * The TTL above protects the case where the archive write never lands. Once it
 * HAS landed, the server doc is the durable record and the tombstone exists
 * only to suppress local copies — the persisted thread list, the IDB mirror, a
 * half-landed snapshot — which survive far longer than fifteen minutes. Those
 * are what brought Miles's archived threads back on every reload.
 *
 * A date rather than Infinity: the map is JSON-persisted, and JSON.stringify
 * turns Infinity into null, which reads back as "no tombstone" — the exact bug
 * this is meant to close.
 */
const CONFIRMED_ARCHIVE_TOMBSTONE_AT = Date.UTC(9999, 0, 1);
// Routine-handoff auto-navigation window (Routine Threads, 2026-07-31): a
// server-minted routine thread only auto-navigates the viewer while its
// handoff stamp is this fresh. Older stamps (a hydrate long after the fact, a
// device that was offline) render the new thread in the rail but never yank
// the user out of what they're doing.
const ROUTINE_HANDOFF_WINDOW_MS = 5 * 60 * 1000;
// Wake coordinator (returning-tab reliability, 2026-09-01): a visibility wake
// only re-checks listener health after the tab was hidden at least this long,
// so a quick tab flick never pays a reattach or a hydrate probe.
const WAKE_MIN_HIDDEN_MS = 60_000;
// Min interval between listener-epoch bumps — an epoch bump tears down and
// reattaches EVERY server stream (full first-snapshot re-delivery), so
// repeated 'online' flaps must not turn into a reattach storm.
const WAKE_EPOCH_BUMP_MIN_INTERVAL_MS = 30_000;

/** Set of every default-title literal we've ever shipped — used by isDefaultTitle. */
const DEFAULT_TITLE_LITERALS: ReadonlySet<string> = new Set([
  LEGACY_DEFAULT_TITLE,
  DEFAULT_TITLE_EN,
  DEFAULT_TITLE_ES,
  // Title-case variants manufactured by the thread-label inferrer's prompt
  // (inferThreadLabel low-signal default) — absent from this set, they stuck
  // as "real" titles and fingerprinted the orphan-empty-thread accumulation
  // (five "New Thread" chips, 2026-07-12). Recognized so isDefaultTitle
  // treats labeler-defaulted empties as defaults for reuse/janitor logic.
  'New Thread',
  'Nuevo Hilo',
]);

/** True if `title` is one of the default-title literals (any locale, any era). */
export function isDefaultTitle(title: string | undefined | null): boolean {
  if (!title) return true;
  return DEFAULT_TITLE_LITERALS.has(title);
}

/**
 * Flood-era clobber fingerprint title (2026-07-31 follow-up): the pre-fix
 * recipient-side surfacing loop merge-clobbered REAL index docs with the
 * generic shared-thread fallback title ('Group thread' —
 * SHARED_THREAD_TITLE_FALLBACK in services/groupThreads/sharedThreadSurfacing).
 * Threads carrying it (and NOT user-renamed) qualify for the same organic
 * title healing default-titled threads get — the mirror re-derives on open,
 * and the label backfill re-titles recent ones on mount — so clobbered
 * conversations re-title themselves the moment the account is viewed.
 *
 * Case-insensitive on the trimmed literal. The reserved-title set
 * (canonicalThreadTitle.ts: 'group thread' / 'new thread' / 'nuevo hilo')
 * has no Spanish sibling for 'group thread' — 'nuevo hilo' pairs with
 * 'new thread' and is already covered by isDefaultTitle — so this matches
 * exactly the one literal the flood wrote.
 */
export function isFloodClobberedTitle(title: string | undefined | null): boolean {
  if (!title) return false;
  return title.trim().toLowerCase() === 'group thread';
}

// ── Thread-label inference gating (2026-07-02 request-storm fix) ──────────
// Pure decision core for the label-inference effect, extracted so the storm
// regression stays unit-testable: the old effect abort-and-refired one model
// call per messages churn tick, which stale tabs turned into a flat
// ~750 req/min wall (see INCIDENT-20260702 follow-up).

/** Quiet window the messages array must hold before a label call fires. */
export const LABEL_INFER_DEBOUNCE_MS = 750;
/** Max fired label calls per user-message id before we stop trying. */
export const LABEL_INFER_MAX_ATTEMPTS = 3;
/**
 * How long a new thread's name may WAIT for the model's short label before the
 * first-prompt heuristic fills the placeholder instead (the one-name policy's
 * floor, 2026-09-18).
 *
 * This is the whole failure story of "the name changes once": the label lane is
 * the namer, and `inferThreadLabel` has no timeout of its own — it awaits fetch
 * with only an abort signal — so without a deadline a hung request would leave
 * the row reading "New thread" for as long as the tab is open. Six seconds is
 * comfortably past the observed ~1-2 s call plus the 750 ms debounce and the
 * attempt cap's first retry, so it is reached only when the lane genuinely
 * cannot deliver.
 */
export const TITLE_SETTLE_FALLBACK_MS = 6_000;

export type LabelInferenceDecision =
  /** Nothing to do: already incorporated, or the same id is in flight. */
  | { action: 'skip'; reason: 'incorporated' | 'in-flight-same-id' }
  /** Attempts exhausted — mark the id incorporated so the gate closes. */
  | { action: 'close-gate'; reason: 'attempt-cap' }
  /** (Re)arm the debounce; optionally abort an in-flight older-id call. */
  | { action: 'schedule'; abortInFlight: boolean };

export function decideLabelInference(input: {
  latestUserMsgId: string;
  incorporatedMsgId: string | undefined;
  attemptMsgId: string | undefined;
  attemptCount: number;
  inFlightMsgId: string | undefined;
  hasInFlightController: boolean;
  maxAttempts?: number;
}): LabelInferenceDecision {
  const maxAttempts = input.maxAttempts ?? LABEL_INFER_MAX_ATTEMPTS;
  if (input.incorporatedMsgId === input.latestUserMsgId) {
    return { action: 'skip', reason: 'incorporated' };
  }
  if (input.attemptMsgId === input.latestUserMsgId && input.attemptCount >= maxAttempts) {
    return { action: 'close-gate', reason: 'attempt-cap' };
  }
  // An inference for THIS id is already in flight: leave it alone. Churn
  // ticks must not abort-and-refire — the abort used to emit one
  // billed-but-discarded proxy request per tick.
  if (input.inFlightMsgId === input.latestUserMsgId && input.hasInFlightController) {
    return { action: 'skip', reason: 'in-flight-same-id' };
  }
  // A NEWER user message obsoletes an in-flight call for an older one.
  return {
    action: 'schedule',
    abortInFlight: Boolean(input.inFlightMsgId && input.inFlightMsgId !== input.latestUserMsgId),
  };
}

/**
 * Lightweight Spanish-vs-English heuristic for picking the localized
 * default thread title ("New thread" vs "Nuevo hilo"). We deliberately
 * keep this self-contained — importing the Hidalgo widget's detector
 * would couple the core thread store to widget code. Mirrors the
 * conservative behaviour used elsewhere: if we see Spanish accents OR a
 * couple of common Spanish stop-words, prefer 'es', otherwise 'en'.
 */
export function detectThreadLanguage(messages: ChatMessage[]): 'en' | 'es' {
  // Look at up to the last 5 user messages — enough signal without scanning
  // an entire thread.
  const userMsgs = messages.filter(m => m.isUser).slice(-5);
  if (userMsgs.length === 0) {
    // No user messages yet — fall back to the browser/OS language hint so
    // a fresh Spanish-speaking visitor sees "Nuevo hilo" by default.
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      if (/^es\b/i.test(navigator.language)) return 'es';
    }
    return 'en';
  }
  const text = userMsgs
    .map(m => (m.text && m.text.trim()) || (m.html ? htmlToPlainText(m.html) : ''))
    .join(' \n ');
  if (!text || text.trim().length < 3) return 'en';
  const spanishAccents = /[áéíóúüñ¿¡]/;
  // Common Spanish stop-words / function words. Includes a few legal-domain
  // tokens since this client base is Spanish-speaking immigration / WC.
  const spanishWords = /\b(el|la|los|las|un|una|de|del|y|en|que|es|por|con|para|como|su|se|no|mi|me|te|le|nos|les|si|yo|tu|tengo|tiene|quiero|necesito|puedo|puede|soy|eres|hay|estoy|está|somos|estamos|están|gracias|hola|buenos|días|tardes|favor|cómo|cuándo|dónde|qué|quién|también|pero|porque|muy|más|bien|mucho|asilo|inmigración|abogado|caso)\b/gi;
  let score = 0;
  if (spanishAccents.test(text)) score += 3;
  const wordMatches = text.match(spanishWords);
  if (wordMatches) score += wordMatches.length;
  return score >= 2 ? 'es' : 'en';
}

/** Pick the localized default thread title for the given language. */
export function localizedDefaultTitle(language: 'en' | 'es'): string {
  return language === 'es' ? DEFAULT_TITLE_ES : DEFAULT_TITLE_EN;
}

/**
 * Should the GlobalChatSystem header render the top-level "clear chat"
 * trash button?
 *
 * Per Miles 2026-05-09 (follow-up to PR #68): the button was removed in
 * PR #68 to avoid accidental wipes once chip-level × landed. But when
 * there is only one thread, the chip-level × archives the user's *only*
 * thread instead of giving them a clean "reset and start fresh" path.
 * So we re-introduce the top-level button — but ONLY when threads.length
 * <= 1 (covers the 0-thread cold-boot case and the 1-thread steady state).
 * With 2+ threads, the chip-level × is sufficient and the top-level
 * button stays hidden, preserving PR #68's accidental-wipe fix.
 */
export function shouldShowGlobalClearButton(
  threads: ChatThread[] | null | undefined,
): boolean {
  if (!Array.isArray(threads)) return true;
  return threads.length <= 1;
}

// ── Thread-label inference plumbing ───────────────────────────────
//
// Helpers used by the dynamic chip-label / hover-summary inference
// effect. Pulled out as exported pure functions so they can be unit
// tested without mounting the React tree.
//
// Fingerprinting prefers the explicit `id` field on a ChatMessage when
// present (added by the streaming layer for race-free updates) and
// otherwise builds a stable key from the message's tail-index plus a
// short text/HTML prefix and the timestamp. This lets us answer:
// "is the latest user message already folded into the current
// shortName/topicSummary for this thread?".
// ──────────────────────────────────────────────────────────────────────
export function userMessageFingerprint(
  msg: ChatMessage,
  index: number,
): string {
  if (msg && typeof msg.id === 'string' && msg.id.length > 0) {
    return `id:${msg.id}`;
  }
  const ts = (msg && msg.timestamp instanceof Date && !isNaN(msg.timestamp.getTime()))
    ? msg.timestamp.getTime()
    : 0;
  const rawText = (msg && msg.text && msg.text.trim()) || '';
  const rawHtml = (!rawText && msg && msg.html) ? htmlToPlainText(msg.html).trim() : '';
  const sample = (rawText || rawHtml).slice(0, 80);
  return `idx:${index}|ts:${ts}|s:${sample}`;
}

/**
 * One-line debug log for the thread-label inference path. Gated behind a
 * `DEBUG_THREAD_LABEL` flag exposed via either:
 *   • `process.env.DEBUG_THREAD_LABEL` (truthy)
 *   • `localStorage.DEBUG_THREAD_LABEL` (truthy)
 *
 * Format: `[threadLabel] threadId=<id> trigger=<reason> messageCount=<n> inflight=<bool>`
 *
 * Stays off by default in production; flipping the localStorage flag in
 * the browser console (`localStorage.DEBUG_THREAD_LABEL = '1'`) reveals
 * a complete trace without a redeploy. The format string itself —
 * `[threadLabel]` — doubles as a build-time grep marker so live-bundle
 * verification can confirm the helper shipped.
 */
export function maybeLogThreadLabel(
  threadId: string,
  trigger: string,
  messageCount: number,
  inflight: boolean,
): void {
  let on = false;
  try {
    if (typeof process !== 'undefined' && process.env && process.env.DEBUG_THREAD_LABEL) {
      on = true;
    }
  } catch { /* ignore */ }
  try {
    if (!on && typeof localStorage !== 'undefined') {
      const v = localStorage.getItem('DEBUG_THREAD_LABEL');
      if (v && v !== '0' && v !== 'false') on = true;
    }
  } catch { /* ignore */ }
  if (!on) return;
  // eslint-disable-next-line no-console
  console.debug(
    `[threadLabel] threadId=${threadId} trigger=${trigger} messageCount=${messageCount} inflight=${inflight}`,
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

export interface UseChatThreadsOptions {
  /** GlobalChatSystem `config.instanceId` — keys all thread storage */
  instanceId: string;
  /** Whether the thread switcher is enabled (false → hook is a no-op) */
  enabled: boolean;
  /** Current live messages array (mirrors to active thread's storage) */
  messages: ChatMessage[];
  /** Whether the chat backend is currently processing a response */
  isProcessing: boolean;
  /** Dispatcher from useGlobalChat — used to load a thread's messages */
  dispatch: React.Dispatch<GlobalChatAction>;
  /**
   * Optional synchronous accessor for the current messages array. When
   * provided, `snapshotActive` (called from switchThread / newThread /
   * removeThread) will read from this accessor instead of the
   * effect-tracked `latestMessagesRef`. This is the fix for Bug #1
   * (PR #97): the React-effect-tracked ref lags one commit behind
   * dispatches that just happened in the same tick, causing the
   * snapshot to write stale messages and lose the just-added userMsg
   * when the user sends and switches in rapid succession.
   *
   * Wired by `useGlobalChat.refs.messagesSyncRef`, which is updated
   * synchronously inside the dispatch wrapper.
   */
  getLatestMessages?: () => ChatMessage[];
  /**
   * Personalized new-thread greeting composer (2026-07-13, Option A — pure
   * client composition, zero network). When provided, every thread minted by
   * `newThread()` / `resetAllThreads()` opens with the returned copy seeded
   * as a PERSISTED first assistant message (`id: 'welcome'`,
   * `sourceTag: 'welcome'`), so the greeting stays in the thread across
   * reloads. Returning null falls back to the legacy empty-thread behavior.
   *
   * HARD INVARIANT: a greeting is NOT user content. Greeted threads are
   * stamped `hasUserContent: false` and keep counting as EMPTY drafts for
   * single-empty reuse, deferred server create, and one-click empty delete —
   * otherwise "+" would mint unbounded threads (the five-chip bug class).
   *
   * `tokenUsage` (2026-07-13, greeting-ⓘ directive): when the composer's
   * copy was AI-distilled, the reported usage rides along and is stamped on
   * the seeded welcome message so its mandatory ⓘ shows real compute data.
   */
  composeGreeting?: () => {
    text: string;
    html: string;
    tokenUsage?: TokenUsageSnapshot;
    /** Monotonic copy rank + persisted chips (2026-08-09 greeting parity). */
    greetingRank?: number;
    starters?: string[];
  } | null;
  /**
   * Resolved prest0n-session account id (group-threading read scope), or
   * null while resolution is in flight. The shared-store live listener gates
   * on it AND depends on it: at page load the RESTORED active thread mounts
   * before the async account resolution completes, so a subscribe fired then
   * would read the account-scoped flag as OFF and no-op forever (verified
   * live 2026-07-23 — the owner's restored view never received the member's
   * store-only turns). Null → don't subscribe yet; when the id lands the
   * effect re-runs and attaches with the flag resolvable.
   */
  groupReadAccountId?: string | null;
  /**
   * COLD-BOOT FAIL-CLOSED (2026-08-21, incognito churn diagnosis). True while
   * a SIGNED-IN user's workspace identity is still being resolved and no
   * confirmed-workspace cache could seed groupReadAccountId — i.e. exactly
   * the fresh-profile window where the uid-global subscription would render
   * the UNFILTERED cross-workspace union and then snap strict when the gate
   * lands (threads visibly appear, then vanish). While pending, the server
   * subscriptions WAIT: the first snapshot the session ever sees is already
   * workspace-correct, which also lets the one-shot boot-dedupe and the
   * partial-view guard run against a real view instead of the half view.
   * Never true for guests (no uid) or warm sessions (cache seeds the id).
   */
  workspaceIdentityPending?: boolean;
  /**
   * CENTRAL FOREIGN-THREAD GUARD (multi-org separation round 2, 2026-08-19).
   * switchThread is the ONE router every activation path funnels through —
   * sidebar rows, chips, sheet, routine manager, server-only routine rows,
   * avatar bridge, future callers — but it never workspace-checked its
   * target, so a thread bound to ANOTHER workspace could activate in place
   * (the account-scoped routine list is user-scoped, not workspace-scoped).
   * When the target's binding differs from the active workspace, the store
   * now REFUSES the in-place activation and hands the thread to this
   * callback instead; the host runs the sealed workspace-switch flow
   * (overlay → server-verified switch → navigation → pending-open).
   * Omitted → legacy in-place behavior (isolated tests).
   */
  onForeignThreadSwitch?: (thread: ChatThread) => void;
  /**
   * Thread ids this user is a REGISTERED participant of (from the
   * server-mediated membership reader), or null while unknown.
   *
   * Scale guard for the all-accounts rollout: with the group READ flag on for
   * every account, an unconditional shared-store listen on every thread-open
   * would attempt a rules-denied listener for each SOLO thread across the whole
   * fleet (a wasted participant-doc read + SDK permission noise per open).
   * Known-shared ids come from the same membership snapshot the recipient
   * surfacing uses, and it is refreshed right after a successful add-people
   * write, so a freshly-shared thread still attaches immediately.
   *
   * Fail-OPEN by design: null (never read / read failed) subscribes exactly as
   * before, so a membership miss can never suppress live delivery.
   */
  sharedThreadIds?: ReadonlySet<string> | null;
  /**
   * Every thread the membership read returned a row for — solo rows included.
   * The reactions listener attaches for a registered solo thread too, so its
   * reactions survive a reload (2026-09-09); null = unknown.
   */
  registeredThreadIds?: ReadonlySet<string> | null;
  /**
   * True when the automatic labeler must NOT rewrite this thread's title —
   * a shared thread this user did not create. The canonical title set by the
   * creator is authoritative and propagates to every participant's index; a
   * local labeler writing over it makes the same conversation show a different
   * name to each person (reported 2026-08-05) and then oscillate, because each
   * client re-infers and re-clobbers what the server just propagated.
   *
   * Absent ⇒ label as before. An explicit user rename (`userRenamed`) is a
   * separate, stronger guard and is unaffected.
   */
  isAutoTitleSuppressed?: (threadId: string) => boolean;
  /**
   * Called when the labeler settles on a title. The host publishes it as the
   * canonical shared-thread title when this user is the thread's creator, so
   * every participant converges on one name. Best-effort and non-blocking.
   */
  onAutoTitleResolved?: (threadId: string, title: string) => void;
}

export interface UseChatThreadsResult {
  /** Recency-ordered thread list (most recent first), workspace-separated (P4). */
  threads: ChatThread[];
  /**
   * The UNFILTERED recency-ordered list — every workspace's threads. Sole
   * consumer: the fullscreen sidebar's extended (all-workspaces) view
   * (owner directive 2026-08-19). Foreign rows there always route through
   * the sealed workspace switch, never an in-place open, so containment
   * (strict-containment train, PR #909) is preserved.
   */
  allWorkspaceThreads: ChatThread[];
  /**
   * OLDER THREADS (2026-09-22). The live listeners deliver only the newest
   * WORKING_SET_LIMIT threads by `lastActiveAt`; these three are the door to
   * everything below that window. `olderThreadsAvailable` is false whenever the
   * window was never full — then there is provably nothing older to fetch.
   */
  olderThreadsAvailable: boolean;
  isLoadingOlderThreads: boolean;
  /** Pages in the next slice; resolves with how many NEW records landed. */
  loadOlderThreads: () => Promise<number>;
  /** Currently-active thread id, or null if switcher disabled / no threads */
  activeThreadId: string | null;
  /**
   * SYNCHRONOUS active-thread accessor (cross-thread delivery incident,
   * 2026-08-16 — owner incognito repros: a starter-chip answer delivered into
   * "New thread" while the user bubble landed in the freshly-minted chip
   * thread; with two concurrent turns, a calendar answer appended into the
   * email thread). `activeThreadId` above is React STATE — any host closure
   * over it (the getActiveThreadIdRef bridge) lags every thread transition by
   * one commit, so a send/dispatch/delivery capture inside that window keyed
   * the turn to the PREVIOUS thread while the pane/mirror already belonged to
   * the new one. This accessor reads the internal ref that every transition
   * (bootstrap, switchThread, newThread, removeThreadAndReturnFallback)
   * writes SYNCHRONOUSLY before dispatching state — captures through it can
   * never return the outgoing thread's id.
   */
  getActiveThreadIdSync: () => string | null;
  /** True once the store has hydrated from localStorage */
  ready: boolean;
  /**
   * This hook instance's identity on the thread-sync channel. EVERY persist
   * made on behalf of this view (including host-level write-ref/flush paths
   * outside the hook) must carry it as `writerToken`, so the view never hears
   * its own writes (echo-loop invariant, threadPersist.notifyLocalThreadSync).
   */
  threadSyncWriterToken: string;
  /** Start a brand-new empty thread and make it active */
  newThread: () => string;
  /**
   * Switch to an existing thread by id. When the id has NO local record
   * (a routine thread outside the capped listen window opened from the
   * rail/manager — routine list parity, 2026-08-02), the switch also
   * hydrates the record from its single index doc (getChatThread); see
   * SwitchThreadOptions for the missing/archived-doc fallback.
   */
  switchThread: (threadId: string, options?: SwitchThreadOptions) => void;
  /**
   * Archive a thread: move it + its messages into the per-instance archive
   * (preserved in general memory), remove its chip from the bar, and
   * fall back to the most recent remaining thread. If the removed thread
   * was active, the next most-recent thread becomes active. If no threads
   * remain, a fresh empty default is bootstrapped.
   *
   * Nothing is permanently deleted — archived threads live under the
   * THREAD_ARCHIVE key until explicitly purged.
   */
  removeThread: (threadId: string) => void;
  /**
   * Archive a thread and synchronously return the fallback active thread id.
   * Used by UI code that must keep the chat system open after archive and
   * ensure the next thread is visible immediately.
   */
  removeThreadAndReturnFallback: (threadId: string) => string | null;
  /** Read the current archive (newest-first). */
  getArchivedThreads: () => ArchivedThread[];
  /** Increment (or decrement, floored at 0) a thread's unread counter. */
  markUnread: (threadId: string, delta?: number) => void;
  /**
   * The viewer has dwelt on this thread (PR-3): zero its count, stamp
   * lastViewedAt, and for a shared thread zero the server unseen counter.
   */
  markThreadViewed: (threadId: string, opts?: { shared?: boolean }) => void;
  /**
   * Read receipts of the ACTIVE shared thread (PR-3): reader id (uid or
   * 'assistant') → high-water mark. {} for solo threads and until the first
   * snapshot lands.
   */
  activeThreadReceipts: ReceiptsByReader;
  /**
   * The ACTIVE shared thread's LIVE-TURN docs (§11 WS-2, T-R13): one per turn
   * Prest0n is running for someone in the room, server-written, gone when the
   * turn ends. [] for solo threads, while the server lever is off (nothing is
   * written), and until the first snapshot lands. Read-only: this hook never
   * lights a processing flag from it (streamingFlag.ts).
   */
  activeThreadLiveTurns: LiveTurnView[];
  /**
   * Who is TYPING in the ACTIVE shared thread (§13 T-R15, Miles 2026-09-21):
   * one row per other person whose draft box changed in the last few seconds.
   * Never the viewer's own row, never the draft's text or its length — a
   * typing doc carries a uid, a server stamp and a schema version, nothing
   * else. [] for solo threads, while the lever is off (no listener attaches),
   * and until the first snapshot lands. Read-only, like the live turns above:
   * a teammate's keystroke must never light this tab's processing flag, or
   * their draft would spin the room's widget (streamingFlag.ts).
   */
  activeThreadTyping: TypingView[];
  /**
   * Emoji reactions of the ACTIVE thread (PR-4): message shared id → doc.
   * {} until the first snapshot lands (and for threads with no reactions).
   */
  activeThreadReactions: ReactionsByMessage;
  /**
   * The shared store's orderTs for every message id the overlay has shown
   * for the ACTIVE thread (PR-4): the proof a message is DELIVERED to the
   * store, and the instant a reaction must carry. {} for solo threads.
   */
  activeThreadSharedOrderTs: Record<string, number>;
  /**
   * Mirror the viewer's per-thread bell onto the thread record + its index
   * doc (2026-09-08) so the server push fan-out can honor a mute. The
   * localStorage stamp (services/threadNotifications) stays the client's
   * source of truth; this is the server-visible copy.
   */
  setThreadNotifyMode: (threadId: string, mode: ChatThread['notifyMode']) => void;
  /** Toggle a thread's pinned state (pinned threads order first in every view) */
  togglePinThread: (threadId: string) => void;
  /**
   * Rename a thread (user-set label). PR-5 (2026-09-09): also appends the
   * rename to the record's `titleHistory` (former names stay searchable).
   */
  renameThread: (threadId: string, title: string, opts?: { byUid?: string | null }) => void;
  /**
   * File a thread under one of the viewer's categories (PR-5), or under none
   * (null). Persisted on the viewer's own index doc; never anyone else's.
   */
  setThreadCategory: (threadId: string, categoryId: string | null) => void;
  /**
   * Optimistically patch a routine thread's LOCAL routine mirror (status /
   * next-run / name / schedule) after a successful pause-resume or an
   * authoritative listRoutines() snapshot. Local-only — the mirror stays
   * server-owned and the index subscription reconciles on top; no-op for
   * threads without `routineId`. See the implementation doc for the drift
   * this closes (2026-08-01).
   */
  patchThreadRoutineMirror: (threadId: string, patch: ThreadRoutineMirrorPatch) => void;
  /**
   * Hard-reset the thread store: archive nothing, drop every per-thread
   * message key for this instance, drop the thread list, drop the
   * archive, and re-bootstrap a single empty default thread (localized
   * to the user's UI language).
   *
   * Used by the top-level "clear chat" trash button (Miles 2026-05-12,
   * Slack thread C0AM8U804KZ/1778481193.723179). Unlike the chip-level
   * × (which archives so nothing is lost from general memory), the
   * trash button is the user's explicit "wipe everything for this
   * surface" affordance.
   *
   * Returns the new active thread id.
   */
  resetAllThreads: () => string | null;
  /**
   * I4 ("Load earlier messages", 2026-07-15): true when the ACTIVE thread's
   * server history extends beyond the loaded window (its paged first read
   * returned a full page + cursor). Always false for threads loaded from a
   * full local cache or a pre-migration legacy full read.
   */
  earlierMessagesAvailable: boolean;
  /** I4: an older page is currently being fetched for the active thread. */
  isLoadingEarlierMessages: boolean;
  /**
   * I4: fetch the next older page for the ACTIVE thread and prepend it into
   * the pane + local store (anchor-deduped, chronological). No-op when no
   * older page exists, no uid, or a fetch is already in flight.
   */
  loadEarlierMessages: () => Promise<void>;
  /**
   * Server-hydrate status for the ACTIVE thread (returning-tab reliability,
   * 2026-09-01). Consumers render a retry affordance ONLY when the pane is
   * empty AND the status is 'error' — 'loading' may flash on local-full
   * switches and must be ignored unless the pane is empty.
   */
  activeThreadHydrateStatus: 'idle' | 'loading' | 'error';
  /** Re-run the ACTIVE thread's server hydrate after an 'error' status. */
  retryActiveThreadHydrate: () => void;
}

// ────────────────────────────────────────────────────────────────────────────
// Pure helpers (exported for testing)
// ────────────────────────────────────────────────────────────────────────────

/** RFC4122-ish id without dragging uuid in just for this. */
/**
 * Point 6 (mid-stream hard-refresh data-loss fix): compose the existing
 * rehydrate sanitizer with the interrupted-turn reconciler.
 *
 * By default we reconcile on real hydrate boundaries so a never-completed
 * in-flight assistant widget (deep-research turn killed by a hard refresh) is
 * converted into a clearly-marked, recoverable "interrupted" message instead of
 * rendering as a forever-spinning dead "thinking" widget.
 *
 * Live same-thread storage syncs can opt out via `{ reconcileInterrupted: false }`.
 * That keeps active in-flight turns untouched while other tabs mirror the raw
 * snapshot, avoiding false-positive Interrupted cards during streaming.
 * Pure + synchronous; returns the same array reference when nothing changed
 * (stable React identity).
 */
export function prepareRehydratedMessages<T extends ChatMessage>(
  messages: T[],
  opts: { reconcileInterrupted?: boolean } = {},
): T[] {
  // PR-F0 (2026-09-21, userRowTwins.ts): a persisted list may hold one question
  // twice (the client bubble + the store's twin); every rehydrate — IndexedDB,
  // localStorage, the server read — passes through here, so the pair is
  // collapsed once, on the id link, before anything renders it.
  const sanitized = collapseUserRowTwins(sanitizeMessagesForRehydrate(messages));
  if (opts.reconcileInterrupted === false) return sanitized;
  const reconciled = reconcileInterruptedOnHydrate(sanitized as ChatMessage[]);
  return reconciled.changed ? (reconciled.messages as T[]) : sanitized;
}

export interface ServerBackedInterruptedCandidate {
  index: number;
  messageId?: string;
  regeneratePrompt?: string;
}

function hasPendingAnimation(html?: string): boolean {
  if (!html) return false;
  return PENDING_ANIMATION_MARKERS.some((marker) => html.includes(marker));
}

function isCompletedAssistantAnswer(message: ChatMessage | undefined): boolean {
  if (!message || message.isUser) return false;
  if (message.interrupted || message.status === 'interrupted') return false;
  if (isOrphanedInFlightMessage(message)) return false;

  const text = (message.text || '').trim();
  const html = message.html || '';
  if (html && !hasPendingAnimation(html)) return true;
  return text.length > 0;
}

function normalizeTimestamp(message: ChatMessage): ChatMessage {
  return {
    ...message,
    timestamp: message.timestamp instanceof Date
      ? message.timestamp
      : new Date(message.timestamp as unknown as string),
  };
}

/**
 * Identify orphaned in-flight turns that are eligible for replay against
 * persisted server answers.
 */
export function collectServerBackedInterruptedCandidates(
  originalMessages: ChatMessage[],
): ServerBackedInterruptedCandidate[] {
  if (!Array.isArray(originalMessages) || originalMessages.length === 0) return [];

  const out: ServerBackedInterruptedCandidate[] = [];
  for (let index = 0; index < originalMessages.length; index += 1) {
    const message = originalMessages[index];
    const isOrphan = isOrphanedInFlightMessage(message);
    const isAlreadyInterrupted = message.interrupted || message.status === 'interrupted';
    
    // Any pending/spinner message with a serverTaskId (meaning isOrphan is false
    // but it is a server-backed task that survived hydrate) should also be
    // collectible as a candidate to retrieve its completed result from Firestore.
    const hasServerTask = !message.isUser && typeof message.serverTaskId === 'string' && message.serverTaskId;
    
    if (!isOrphan && !isAlreadyInterrupted && !hasServerTask) continue;
    // 2026-08-11 (Fernando DR two-store divergence): an Interrupted card
    // (sourceTag 'interrupted_turn') was permanently ineligible here, so a
    // thread whose task actually COMPLETED server-side could never self-heal
    // — the user stared at a dead card that refresh couldn't fix while the
    // per-doc store held the finished memo. Anchored interrupted cards are
    // now reconcilable, bounded to recent turns (24h) so old threads don't
    // re-poll the server on every open.
    const cardTimestampMs = message.timestamp instanceof Date
      ? message.timestamp.getTime()
      : (typeof message.timestamp === 'number' ? message.timestamp : Date.parse(String(message.timestamp || '')));
    const cardAgeMs = Date.now() - cardTimestampMs;
    const isRecoverableInterruptedCard =
      isAlreadyInterrupted &&
      message.sourceTag === 'interrupted_turn' &&
      (!!(message as { taskAnchorId?: unknown }).taskAnchorId || !!hasServerTask) &&
      Number.isFinite(cardAgeMs) && cardAgeMs >= 0 && cardAgeMs < 24 * 60 * 60 * 1000;
    if (!message.sourceTag || !(PENDING_SOURCE_TAGS.has(message.sourceTag) || isRecoverableInterruptedCard)) continue;

    let regeneratePrompt: string | undefined;
    for (let j = index - 1; j >= 0; j -= 1) {
      const prior = originalMessages[j];
      if (prior && prior.isUser && (prior.text || '').trim()) {
        regeneratePrompt = (prior.text || '').trim();
        break;
      }
    }

    out.push({
      index,
      messageId: message.id,
      regeneratePrompt,
    });
  }

  return out;
}

function findPersistedAnswerByPrompt(
  serverMessages: ChatMessage[],
  regeneratePrompt: string,
): ChatMessage | null {
  const prompt = regeneratePrompt.trim();
  if (!prompt) return null;

  for (let i = serverMessages.length - 1; i >= 0; i -= 1) {
    const message = serverMessages[i];
    if (!message?.isUser) continue;
    if ((message.text || '').trim() !== prompt) continue;

    for (let j = i + 1; j < serverMessages.length; j += 1) {
      const candidate = serverMessages[j];
      if (candidate?.isUser) break;
      if (isCompletedAssistantAnswer(candidate)) return candidate;
    }
  }

  return null;
}

/**
 * Async-hydrate reconciliation step for Stage 2.
 * Replaces interrupted SERVER-BACKED bubbles with persisted server answers when
 * available, and leaves client-only interrupted bubbles untouched.
 */
export function replayPersistedServerAnswersForInterruptedMessages(
  preparedMessages: ChatMessage[],
  originalMessages: ChatMessage[],
  serverMessages: ChatMessage[],
): { messages: ChatMessage[]; changed: boolean } {
  if (!Array.isArray(preparedMessages) || preparedMessages.length === 0) {
    return { messages: preparedMessages, changed: false };
  }
  const candidates = collectServerBackedInterruptedCandidates(originalMessages);
  if (candidates.length === 0) {
    return { messages: preparedMessages, changed: false };
  }

  let changed = false;
  let out = preparedMessages;

  for (const candidate of candidates) {
    // FROZEN-SNAPSHOT FIX (Symptom 2): locate the swap target by MESSAGE ID
    // in the into-list, not by the candidate's index into the call-time
    // snapshot. `preparedMessages` may be the LIVE list resolved minutes
    // after the candidates were collected — turns appended in the poll
    // window shift positions, and an index-based swap would overwrite the
    // wrong bubble. If the id exists but the bubble is gone from the
    // into-list, skip it (never index-swap a different message). The index
    // fallback remains only for legacy id-less messages, where the pending
    // check below still guards against replacing an unrelated bubble.
    let targetIndex = -1;
    if (candidate.messageId) {
      targetIndex = out.findIndex((m) => m && m.id === candidate.messageId);
      if (targetIndex === -1) continue;
    } else {
      targetIndex = candidate.index;
    }
    const current = out[targetIndex];
    if (!current || current.isUser) continue;

    const isPendingOrInterrupted =
      current.interrupted ||
      current.status === 'interrupted' ||
      (typeof current.serverTaskId === 'string' &&
        current.serverTaskId &&
        (!current.status || current.status === 'streaming'));

    if (!isPendingOrInterrupted) continue;

    let persisted: ChatMessage | null = null;
    if (candidate.messageId) {
      persisted =
        serverMessages.find(
          (m) => m && m.id === candidate.messageId && isCompletedAssistantAnswer(m),
        ) || null;
    }
    
    // Match by serverTaskId also
    if (!persisted && current.serverTaskId) {
      const targetServerTaskId = current.serverTaskId;
      persisted =
        serverMessages.find(
          (m) =>
            m &&
            isCompletedAssistantAnswer(m) &&
            (m.serverTaskId === targetServerTaskId || m.id === `prest0n_task_${targetServerTaskId}`),
        ) || null;
    }
    
    if (!persisted && candidate.regeneratePrompt) {
      persisted = findPersistedAnswerByPrompt(serverMessages, candidate.regeneratePrompt);
    }
    if (!persisted) continue;

    if (!changed) out = [...out];
    changed = true;
    out[targetIndex] = {
      ...persisted,
      id: current.id || persisted.id,
      interrupted: false,
      status: 'complete',
      regeneratePrompt: undefined,
    };
  }

  return changed ? { messages: out, changed: true } : { messages: preparedMessages, changed: false };
}

export function generateThreadId(): string {
  // Use crypto.randomUUID when available (browsers + modern Node), fall back.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `t_${crypto.randomUUID()}`;
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Derive a concise, human title from the first user message in a thread. */
export function deriveThreadTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find(m => m.isUser);
  if (!firstUser) return localizedDefaultTitle(detectThreadLanguage(messages));
  const lang = detectThreadLanguage(messages);
  const raw = (firstUser.text && firstUser.text.trim())
    || (firstUser.html ? htmlToPlainText(firstUser.html).trim() : '')
    || localizedDefaultTitle(lang);
  
  // Strip common conversational prefixes and tool trigger phrases to
  // give the chip label more dense topic signal.
  const stripped = raw
    .replace(/^(?:please|could you|can you|i need|i want to|I'd like to)\s+/i, '')
    .replace(/^(?:deep research|research|translate to [a-zA-Z]+|translate)\s+/i, '')
    .replace(/^(?:draft|write|create)(?:\s+a|\s+an)?\s+/i, '')
    .trim();

  // If the strip leaves nothing (e.g. user just said "deep research"), fallback to original raw
  void lang; // language already informed the empty-fallback above
  const finalStr = stripped.length > 3 ? stripped : raw;
  const oneLine = finalStr.replace(/\s+/g, ' ').trim();
  
  // A machine-made name ends on a WORD, never mid-token (2026-09-20): `…(testin`
  // reads as a broken name rather than a short one. `boundThreadSummaryName`
  // owns that rule for every auto-title on both planes.
  return boundThreadSummaryName(oneLine, { max: TITLE_MAX_LEN });
}

/**
 * Legacy title detector. Matches thread titles that were derived BEFORE the
 * leading-phrase stripper was added (commit fdf689fc). Such titles still
 * start with command boilerplate like "please deep research" or "can you
 * translate", so we invalidate them and let deriveThreadTitle recompute.
 */
export function isLegacyDerivedTitle(title: string): boolean {
  if (!title) return false;
  return /^(?:please|could you|can you|i need|i want to|I'd like to)\s+/i.test(title)
    || /^(?:deep research|translate to [a-zA-Z]+|translate)\s+/i.test(title)
    || /^(?:draft|write|create)\s+(?:a|an)\s+/i.test(title);
}

/**
 * THE ONE AUTOMATIC TITLING DECISION (2026-09-18) — what an automatic pass may
 * call a thread, given the thread and the FIRST USER MESSAGE.
 *
 * Its predecessor is the four-clause `shouldRederive` that used to live inline in
 * the mirror effect. Three of those four clauses never asked whether a PERSON had
 * named the thread, and one of them (`isLegacyDerivedTitle`) treats "Draft a
 * demand letter" as pre-2026 boilerplate — so a name somebody typed was replaced
 * with their first prompt, the next snapshot restored it out of `titleHistory`,
 * and the next streaming token replaced it again. That is the twitch Miles
 * reported, and the corrupted `{title: <auto>, userRenamed: true, titleHistory:
 * [<chosen>]}` rows the 2026-09-11 incident found were its writing.
 *
 * Two properties here are structural rather than merely correct:
 *
 *   • THE STREAM IS NOT IN THE SIGNATURE. This takes one message, never the
 *     array, so it CANNOT depend on a streaming token — a stronger guarantee
 *     than memoizing the old expression, because restoring the dependency now
 *     means changing the type.
 *   • THE TRANSITION IS MONOTONIC. placeholder -> name, with no back edge. An
 *     automatic titler fills a blank; it never replaces a name, not even one
 *     another automatic titler wrote. `isLegacyDerivedTitle` is deliberately not
 *     consulted, which also retires a second live flip-flop: `inferThreadLabel`
 *     returns Title Case, so the label "Draft A Demand Letter" matched it and was
 *     itself overwritten by the first prompt on the next tick, with no rename
 *     involved at all.
 *
 * `settleReleased` is the one-name policy (Miles, 2026-09-18: a new thread's name
 * changes ONCE, straight to its final name). While it is false the placeholder
 * HOLDS and the model's short label gets to be that one name; the labeler
 * releases it when it cannot produce one — empty result, error, abort, attempt
 * cap, or TITLE_SETTLE_FALLBACK_MS elapsed — and this heuristic fills in instead.
 * Either way the row changes exactly once.
 */
export function resolveMirrorThreadTitle(
  thread: Pick<ChatThread, 'title' | 'userRenamed' | 'titleHistory'>,
  firstUserMessage: ChatMessage | null | undefined,
  settleReleased: boolean,
): string {
  // 1. A name a person chose is immutable — and self-healing: a row already left
  //    in the corrupt shape converges on the name in its own history rather than
  //    being re-derived away from it.
  if (titleIsUserOwned(thread)) return authoritativeThreadTitle(thread) || thread.title;
  // 2. Nothing to derive from yet.
  if (!firstUserMessage) return thread.title;
  // 3. Already has a name (its own label, a creator's canonical title, anything
  //    that is not a platform placeholder) — leave it.
  if (!isPlaceholderThreadTitle(thread.title)) return thread.title;
  // 4. A placeholder, and the one name has not been settled yet: HOLD. The label
  //    lane is naming this thread; a heuristic hop here is the second visible
  //    change the one-name policy exists to remove.
  if (!settleReleased) return thread.title;
  // 5. The label lane could not name it. Fill the placeholder, once.
  return deriveThreadTitle([firstUserMessage]);
}

/** Sort threads most-recent-active first. Active thread floats to the top. */
export function sortThreads(threads: ChatThread[], activeId: string | null): ChatThread[] {
  return [...threads].sort((a, b) => {
    if (a.id === activeId) return -1;
    if (b.id === activeId) return 1;
    return new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime();
  });
}

/**
 * T1 (thread-workflow UX) — THE display ordering, used by every thread
 * renderer (chip strip, fullscreen sidebar) so no view can disagree:
 *   1. PINNED threads first, in pin order (pinnedAt ascending — the order the
 *      user pinned them);
 *   2. unpinned threads by real message activity (lastActiveAt descending).
 * Deliberately NOT active-first: switching threads must never reshuffle the
 * strip (position is spatial memory; STYLE marks the active thread). Viewing
 * bumps only lastViewedAt, so clicking never reorders — only genuine message
 * activity moves an unpinned thread up (owner decision 2026-07-12).
 *
 * DETERMINISTIC TIEBREAK (2026-07-31 multi-device directive): convergent
 * lastActiveAt stamps make exact ties the COMMON case (every device derives
 * the same value from the same transcript), and Array.sort gives no ordering
 * guarantee for equal keys across engines/input permutations — two devices
 * holding the same threads could render different orders. Ties break by
 * createdAt desc, then id lexicographic, so every device renders the
 * identical strip from the identical data.
 */
// MULTI-ORG P4: the separation rule lives in its own leaf module
// (workspaceThreadFilter.ts — pure, firebase-free, unit-tested there);
// re-exported here so hook consumers keep one import site.
export { filterThreadsForWorkspace } from './workspaceThreadFilter';

export function orderThreads(threads: ChatThread[]): ChatThread[] {
  return [...threads].sort((a, b) => {
    const aPinned = a.pinned === true;
    const bPinned = b.pinned === true;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (aPinned && bPinned) {
      return new Date(a.pinnedAt || 0).getTime() - new Date(b.pinnedAt || 0).getTime();
    }
    const byActivity = new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime();
    if (byActivity !== 0) return byActivity;
    const byCreated = threadMillis(b.createdAt) - threadMillis(a.createdAt);
    if (byCreated !== 0) return byCreated;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * Per-thread notification badges (2026-09-08) — the server-driven "someone
 * else was here since you last looked" predicate, pure so every renderer
 * (rail rows, chip strip, sheet/panel rows, header dots, the ☰ roll-up) reads
 * the SAME rule:
 *   • never the OPEN thread (the viewer is looking at it);
 *   • lastActiveAt strictly after the viewer's lastViewedAt (switchThread
 *     stamps lastViewedAt on every open, so viewing clears it on every device
 *     through the index doc);
 *   • never the viewer's OWN activity — the server stamps who caused each
 *     bump (lastActivityBy), which covers their sends from other devices.
 * A malformed stamp degrades to "nothing to show", never to a phantom badge.
 */
export function hasUnseenActivity(
  thread: Pick<ChatThread, 'id' | 'lastActiveAt' | 'lastViewedAt' | 'lastActivityBy'>,
  // Kept in the signature for every caller; since PR-3 (2026-09-08) the OPEN
  // thread is no longer exempt — its dot clears when the viewer has dwelt on
  // it for 3 s (markThreadViewed stamps lastViewedAt), not the instant it opens.
  _activeThreadId: string | null,
  uid: string | null | undefined,
): boolean {
  if (!thread) return false;
  const active = threadMillis(thread.lastActiveAt);
  const viewed = threadMillis(thread.lastViewedAt);
  if (!Number.isFinite(active) || active <= 0) return false;
  if (Number.isFinite(viewed) && viewed >= active) return false;
  const by = (thread.lastActivityBy ?? '').trim();
  if (uid && by && by === uid.trim()) return false;
  return true;
}

/**
 * Threads worth a badge from the viewer's seat: a local unread count OR the
 * unseen-activity predicate, never the open thread. Feeds the ☰ trigger's
 * roll-up count and the header dots — the OTHER-threads indicators, so the
 * open thread is skipped here even though its own row may carry a badge for
 * the 3 s before the dwell clears it (PR-3, 2026-09-08).
 */
export function countUnseenThreads(
  threads: readonly ChatThread[],
  activeThreadId: string | null,
  uid: string | null | undefined,
): number {
  let n = 0;
  for (const t of threads) {
    if (t.id === activeThreadId) continue;
    if (effectiveUnreadCount(t) > 0 || hasUnseenActivity(t, activeThreadId, uid)) n += 1;
  }
  return n;
}

/** Build the storage key for a thread's message array. */
export function threadMsgKey(instanceId: string, threadId: string, uid?: string | null): string {
  return `${getThreadStorageKeys(instanceId, uid).THREAD_MSG_PREFIX}${threadId}`;
}

/**
 * Create a fresh empty-thread metadata record.
 *
 * WORKSPACE BINDING AT BIRTH (multi-org separation round 2, 2026-08-19):
 * local records used to be minted UNBOUND — and because empty drafts defer
 * their server create, they never received the server-side stamp either, so
 * every fresh draft floated into EVERY workspace's rail. New records now
 * carry the resolved active workspace (module mirror, published by
 * GlobalChatSystem) unless the caller overrides it; still-unresolved boots
 * mint unbound exactly as before (the resolve listener back-stamps).
 */
export function createEmptyThread(overrides: Partial<ChatThread> = {}): ChatThread {
  const nowIso = new Date().toISOString();
  const workspaceBinding = getActiveWorkspaceAccount();
  return {
    id: generateThreadId(),
    title: DEFAULT_TITLE,
    createdAt: nowIso,
    lastActiveAt: nowIso,
    lastViewedAt: nowIso,
    unreadCount: 0,
    isProcessing: false,
    messageCount: 0,
    ...(workspaceBinding ? { accountId: workspaceBinding } : {}),
    ...overrides,
  };
}

function parseBooleanFlag(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return null;
}

function resolveServerThreadIndexFlag(): boolean {
  if (typeof localStorage === 'undefined') return ENABLE_SERVER_THREAD_INDEX_DEFAULT;
  const raw = localStorage.getItem(SERVER_THREAD_INDEX_FLAG_KEY);
  const parsed = parseBooleanFlag(raw);
  return parsed == null ? ENABLE_SERVER_THREAD_INDEX_DEFAULT : parsed;
}

export function canUseServerThreadIndex(enableServerThreadIndex: boolean, uid: string | null): boolean {
  return enableServerThreadIndex && !!uid;
}

function threadMillis(iso: string): number {
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * CONVERGENT lastActiveAt (2026-07-31 multi-device directive) — derive the
 * activity stamp from the TRANSCRIPT, never from this device's wall clock.
 *
 * The mirror effect used to stamp `new Date().toISOString()` on every
 * non-empty messages dispatch, so merely VIEWING a thread bumped its
 * lastActiveAt and fanned a server write out to every device — two devices
 * active in different threads made the top rows leapfrog on every write
 * alternation. Deriving `max(existing, newest message timestamp)` instead
 * makes every device compute the IDENTICAL value from the identical
 * transcript: view-only runs produce no change (dirty check stays false → no
 * write, no reorder), while genuine new messages still bump recency because
 * their timestamps are newer than the stored stamp.
 *
 * Identity-stable: returns `existingIso` unchanged unless a message is
 * STRICTLY newer, so equal-millisecond stamps with different string forms
 * can never manufacture a spurious dirty flag. Timestamp shapes are handled
 * robustly (Date in live state; ISO strings/numbers from storage rehydrates
 * that bypassed normalizeTimestamp); unparseable values are skipped.
 *
 * SETTLED-ONLY (2026-07-31 one-reposition-per-turn fix): in-flight
 * placeholders — the immediate thinking widget, the tool handler's swapped
 * streaming widget, any pending-tagged/animated/streaming bubble — are
 * EXCLUDED via the shared `isInFlightPlaceholderMessage` predicate. Those
 * placeholders are minted with a fresh wall-clock `new Date()`, so counting
 * them stepped the sort key (and fired an index write) once per mint/swap on
 * top of the user-message step. Deriving from settled messages only, the
 * user bubble sets the turn's key once at send time and placeholder churn
 * produces zero steps. Convergent: every device excludes the SAME
 * placeholders from the same transcript. Once a placeholder settles
 * (finalize flips it to status:'complete' and strips the pending shape,
 * preserving its MINT timestamp — the finalize payload no longer re-dates
 * it to stream-end), it participates like any other settled message.
 */
export function deriveConvergentLastActiveAt(
  existingIso: string,
  messages:
    | ReadonlyArray<
        Pick<ChatMessage, 'timestamp'>
        & Partial<Pick<ChatMessage, 'isUser' | 'status' | 'interrupted' | 'html' | 'sourceTag'>>
      >
    | null
    | undefined,
): string {
  let bestIso = existingIso;
  let bestMs = threadMillis(existingIso);
  if (Array.isArray(messages)) {
    for (const message of messages) {
      if (message && isInFlightPlaceholderMessage(message as ChatMessage)) continue;
      const raw = message ? (message.timestamp as unknown) : null;
      const ms = raw instanceof Date
        ? raw.getTime()
        : (typeof raw === 'string' || typeof raw === 'number')
          ? new Date(raw).getTime()
          : NaN;
      if (!Number.isFinite(ms) || ms <= bestMs) continue;
      bestMs = ms;
      bestIso = new Date(ms).toISOString();
    }
  }
  return bestIso;
}

/**
 * SETTLED-ONLY message count (owner feedback, Miles 2026-08-01): the number
 * of messages in a transcript that are NOT in-flight assistant placeholders,
 * via the ONE shared `isInFlightPlaceholderMessage` predicate (the same
 * predicate `deriveConvergentLastActiveAt` above uses — count and recency
 * are derived from the same transcript with the same in-flight definition,
 * so they can never drift).
 *
 * The row meta showed "3 messages" while the third was still GENERATING;
 * counts must reflect settled messages only (2 until the reply lands). The
 * settled count still includes the seeded greeting, user messages (user
 * bubbles are never in-flight), interrupted cards, and finalized answers —
 * ONLY in-flight assistant placeholders (streaming/pending/tool-start
 * shapes) are excluded. During a turn the count stays N; the finalize that
 * flips the placeholder to status:'complete' (stripping the pending shape)
 * moves it to N+1 — exactly one step per turn.
 *
 * Convergent by construction: every device excludes the SAME placeholders
 * from the same transcript, so the mirror effect's convergent-stamp
 * discipline (dirty-check no-echo — view-only dispatches produce no record
 * change and no server write) is preserved. Applied at EVERY
 * `messageCount` producer that derives from a transcript: the mirror
 * effect (which is also the flood-`Group thread` heal site), the
 * switch-boundary record catch-up, the hydrate-time
 * `reconcileThreadCounts`, the bootstrap seed, and the G3 adoption push.
 */
export function settledMessageCount(
  messages: ReadonlyArray<ChatMessage> | null | undefined,
): number {
  if (!Array.isArray(messages)) return 0;
  let count = 0;
  for (const message of messages) {
    if (!message) continue;
    if (isInFlightPlaceholderMessage(message)) continue;
    // PR-5 (2026-09-09): a lifecycle row (rename, people in/out) is a record
    // of a change, not a message anyone sent — "N messages" never counts it.
    if (message.sourceTag === 'system:lifecycle') continue;
    count += 1;
  }
  return count;
}

/**
 * 2026-07-31 multi-device directive: on an EXACT lastActiveAt tie the winner
 * decision excludes VIEW STATE. Convergent stamps make ties the common case
 * (every device derives the same value from the same transcript), and letting
 * the server echo win wholesale transiently clobbered this device's
 * just-reset unreadCount:0 / fresh lastViewedAt on every snapshot (the write
 * is 150ms-debounced, so the echo lags). The server copy stays authoritative
 * for CONTENT facts (title/counts/routine mirror — e.g. a reload zeroes the
 * local messageCount of never-hydrated threads via reconcileThreadCounts, and
 * the server copy must heal that); only the viewer-owned fields are kept
 * local. Returns the server reference untouched when nothing differs.
 */
/**
 * A name the viewer chose survives a server row that does not carry it.
 *
 * INCIDENT 2026-09-11. A rename does NOT bump `lastActiveAt`, and
 * `mergeThreadListsNewestWins` replaces the local row wholesale whenever the
 * server's stamp is newer — title included. So in the window between a rename
 * and that rename's own write echoing back, ANY snapshot carrying a newer
 * activity stamp (the turn that was just sent is enough) reverted the title to
 * the auto label. The client then mirrored that back up, which is how a thread
 * came to be stored as `title: <auto label>` with `userRenamed: true` and a
 * history entry naming the rename that had just been lost.
 *
 * The server already refuses this exact write — `shouldWriteParticipantIndexTitle`
 * declines to propagate a canonical title over `userRenamed`. The merge on this
 * side simply never learned the same rule, so the two halves disagreed about who
 * owns the name. They agree now.
 *
 * A rename from ANOTHER device still wins: it arrives as a server row whose own
 * history is at least as new as this one's, which is the comparison made here —
 * never the `userRenamed` flag alone, because the corrupted rows carry that flag
 * with the wrong title and would otherwise keep overwriting the good name.
 *
 * READ IT AS `(winner, renameSource)` — it is DIRECTION-AGNOSTIC (2026-09-18).
 * Every branch keys off the rename SOURCE's own flag and history, so the same
 * function serves "the server won but the local rename stands" and "the local
 * row won but the server carries a newer rename". It is now applied on EVERY arm
 * of both mergers; until 2026-09-18 the reconcile — the merger the live listener
 * actually uses — had it on its tie arm alone, which is why a rename could still
 * be lost while the incident that produced this function was considered closed.
 */
export function carryLocalRename(winner: ChatThread, renameSource: ChatThread): ChatThread {
  if (renameSource.userRenamed !== true) return winner;
  const chosen = authoritativeThreadTitle(renameSource);
  if (!chosen || chosen === (winner.title ?? '').trim()) return winner;
  const newest = (list: ChatThread['titleHistory']): number => {
    if (!Array.isArray(list) || list.length === 0) return 0;
    const at = Number(list[list.length - 1]?.at);
    return Number.isFinite(at) ? at : 0;
  };
  // The WINNER carries a rename the source has not seen → the winner's name is
  // the newer one and stands (this is what makes a rename from another device
  // land, in whichever direction the recency comparison happened to go).
  if (winner.userRenamed === true && newest(winner.titleHistory) > newest(renameSource.titleHistory)) return winner;
  return {
    ...winner,
    title: chosen,
    userRenamed: true,
    ...(renameSource.titleHistory ? { titleHistory: renameSource.titleHistory } : {}),
  };
}

export function carryLocalViewState(server: ChatThread, local: ChatThread): ChatThread {
  if (server.sharedThread === true) {
    // CONVERGED UNREAD (PR-3, 2026-09-08): a shared thread's count is the
    // server's (unseenCount → unreadCount in fromChatThreadDoc) — the one
    // number every device agrees on — so only the VIEW stamp is carried, and
    // only when this device's is newer (the dwell's write echo lags 150 ms;
    // effectiveUnreadCount then reads 0 for a thread viewed since its last
    // activity, whatever the echoed count still says).
    const localViewed = threadMillis(local.lastViewedAt);
    const serverViewed = threadMillis(server.lastViewedAt);
    if (Number.isFinite(localViewed) && (!Number.isFinite(serverViewed) || localViewed > serverViewed)) {
      return { ...server, lastViewedAt: local.lastViewedAt };
    }
    return server;
  }
  if (server.unreadCount === local.unreadCount && server.lastViewedAt === local.lastViewedAt) {
    return server;
  }
  return { ...server, unreadCount: local.unreadCount, lastViewedAt: local.lastViewedAt };
}

export function mergeThreadListsNewestWins(localThreads: ChatThread[], serverThreads: ChatThread[]): ChatThread[] {
  const byId = new Map<string, ChatThread>();
  for (const thread of localThreads) {
    byId.set(thread.id, thread);
  }
  for (const thread of serverThreads) {
    const existing = byId.get(thread.id);
    if (!existing) {
      byId.set(thread.id, thread);
      continue;
    }
    const serverMs = threadMillis(thread.lastActiveAt);
    const localMs = threadMillis(existing.lastActiveAt);
    if (serverMs > localMs) {
      // A newer stamp makes the server row the content authority — but never
      // the authority on a name this viewer chose (carryLocalRename).
      byId.set(thread.id, carryLocalRename(thread, existing));
    } else if (serverMs === localMs) {
      // Tie → server content facts + local view state (see
      // carryLocalViewState; kept consistent with
      // reconcileThreadListsWithServerSnapshot's tie rule).
      byId.set(thread.id, carryLocalRename(carryLocalViewState(thread, existing), existing));
    } else {
      // Local copy wins on recency — and keeps its content, but a rename the
      // SERVER carries is still the newer name (parity with the reconcile's
      // local-wins arm, 2026-09-18). carryLocalRename decides by history, so
      // this is a no-op for every row the server has not renamed.
      byId.set(thread.id, carryLocalRename(existing, thread));
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => threadMillis(b.lastActiveAt) - threadMillis(a.lastActiveAt)
  );
}

/**
 * The key and the parser now live in services/groupThreads/archivedTombstones —
 * a Firebase-free leaf, so the bell's three surfaces can ask the same question
 * without importing this module (2026-09-21). ONE parser serves both readers,
 * which is what keeps them from drifting into disagreeing about the stored map.
 * The WRITE side stayed here deliberately: it goes through the chat's
 * quota-safe setter, and moving a write path to get a read is how a behaviour
 * changes by accident.
 */
function loadDurableTombstones(instanceId: string, uid: string | null): Map<string, number> {
  return parseDurableTombstones(
    getFromLocalStorage<Record<string, number>>(tombstoneStorageKey(instanceId, uid), {}),
    Date.now(),
  );
}

function persistDurableTombstones(instanceId: string, uid: string | null, tombstones: Map<string, number>): void {
  if (tombstones.size === 0) {
    removeFromLocalStorage(tombstoneStorageKey(instanceId, uid));
    return;
  }
  const payload: Record<string, number> = {};
  tombstones.forEach((expiresAt, threadId) => {
    payload[threadId] = expiresAt;
  });
  setToLocalStorage(tombstoneStorageKey(instanceId, uid), payload);
}

/**
 * 2026-07-31 multi-device directive: the read-only DURABLE-tombstone probe for
 * write paths that live OUTSIDE this hook (the recipient-side shared-thread
 * surfacing effect in GlobalChatSystem). Its body moved to the leaf above on
 * 2026-09-21 so the bell can ask the same question without importing this
 * module; it is re-exported under the same name so every existing import site
 * — and every pin on them — is untouched.
 */
export { isThreadTombstonedInStorage };

/**
 * Union-merge tombstones arriving from another tab (via the `storage` event)
 * into this tab's map. Additive only: entries missing from `incoming` are
 * KEPT (another tab persisting its own map must not clear tombstones this
 * tab just added), and an entry is only overwritten by a later expiry.
 * Returns the ids that are new to this tab so the caller can drop those
 * threads from its rail immediately.
 */
export function mergeTombstoneMaps(
  current: ReadonlyMap<string, number>,
  incoming: ReadonlyMap<string, number>,
): { tombstones: Map<string, number>; added: Set<string>; changed: boolean } {
  const tombstones = new Map(current);
  const added = new Set<string>();
  let changed = false;
  incoming.forEach((expiresAt, threadId) => {
    const existing = tombstones.get(threadId);
    if (existing !== undefined && existing >= expiresAt) return;
    if (existing === undefined) added.add(threadId);
    tombstones.set(threadId, expiresAt);
    changed = true;
  });
  return { tombstones, added, changed };
}

/**
 * ROUTINE MIRROR fields on ChatThread (Routine Threads, 2026-07-31) — the
 * server-owned set toChatThreadDoc omits. One list so the reconcile carry and
 * any future consumer can never drift from the mapper contract.
 */
export const ROUTINE_MIRROR_FIELDS = [
  'routineId',
  'routineName',
  'routineStatus',
  'routineScheduleSummary',
  'routineNextRunAt',
  'routineCount',
  'routineLastRunAt',
  'routineLastRunStatus',
  'routineHandoffFromThreadId',
  'routineHandoffAt',
  'routineHandoffAckAt',
] as const;

/**
 * Fields a CLIENT-side optimistic routine-mirror patch may touch (routine
 * header chip + manager panel, 2026-08-01). Deliberately a subset of
 * ROUTINE_MIRROR_FIELDS: identity (`routineId`) and handoff stamps are never
 * client-patched — only the presentation state a pause/resume visibly flips.
 */
export type ThreadRoutineMirrorPatch = Partial<Pick<ChatThread,
  'routineStatus' | 'routineNextRunAt' | 'routineName' | 'routineScheduleSummary'
>>;

/**
 * Options for switchThread (navigation hydration, routine list parity
 * 2026-08-02). `fallbackTitle` labels the MINIMAL local record minted when
 * the target id has no local record AND its index doc is missing or archived
 * (a server-only routine row's Open/click passes the routine's name) — the
 * user still lands somewhere coherent. Ignored whenever a local record
 * exists or the index doc is fetched successfully.
 */
export interface SwitchThreadOptions {
  fallbackTitle?: string;
}

/**
 * EVERY server-owned index field the reconcile carries onto a locally-newer
 * record: the routine mirror plus the shared-thread membership marker
 * (live-membership surfacing, 2026-08-16 — the server stamps `sharedThread`
 * when a thread gains other participants; a locally-newer copy predating the
 * stamp must not erase it from the merged view, or the group-overlay gate and
 * sidebar sectioning lose the thread's group-ness until the next server win).
 * `lastActivityBy` (2026-08-27) rides the same rail: the notification watcher
 * suppresses self-notifies by it, and a locally-newer copy that dropped it
 * would let the viewer's own cross-device send chime at them.
 */
const SERVER_OWNED_INDEX_FIELDS = [...ROUTINE_MIRROR_FIELDS, 'sharedThread', 'lastActivityBy'] as const;

/**
 * Fields the server owns but where ABSENCE is not authoritative (multi-org
 * separation round 2, 2026-08-19): the workspace binding is immutable once
 * stamped server-side, yet the index doc can legitimately lack it (created
 * pre-binding, M4 backfill not landed). The local-newer reconcile branch
 * used to run the binding through the delete-on-absence rule above via NOT
 * carrying it at all — a locally-newer copy simply LOST its `accountId`, the
 * thread flipped to "visible in every workspace", and the rail flapped until
 * the next server win. Rule here: server value wins when PRESENT; a
 * server-absent binding leaves the local stamp untouched.
 */
const SERVER_WINS_WHEN_PRESENT_FIELDS = ['accountId'] as const;

/**
 * Carry the SERVER's routine mirror onto a locally-newer thread record (pure).
 *
 * Reconcile keeps the LOCAL copy of a thread whose lastActiveAt is newer than
 * the server's — but the routine mirror is server-owned (the client never
 * writes it), so the local copy's routine fields are at best a stale echo of
 * an earlier snapshot. Replace them wholesale with the server's, INCLUDING
 * absence: a routine cancel deletes the fields server-side, and a local copy
 * that kept them would show a ghost routine until the next full server win.
 * Returns the local reference untouched when nothing differs (identity-stable
 * for React diffing, like every other pure helper here).
 * Also carries `sharedThread` (see SERVER_OWNED_INDEX_FIELDS).
 */
export function carryServerRoutineMirror(local: ChatThread, server: ChatThread): ChatThread {
  let changed = false;
  const merged: ChatThread = { ...local };
  // Index through Record views: assigning across the ROUTINE_MIRROR_FIELDS
  // key union would otherwise require the intersection of all field types.
  const mergedRecord = merged as unknown as Record<string, unknown>;
  const serverRecord = server as unknown as Record<string, unknown>;
  for (const field of SERVER_OWNED_INDEX_FIELDS) {
    const serverValue = serverRecord[field];
    const localValue = mergedRecord[field];
    if (serverValue === undefined) {
      if (localValue !== undefined) {
        delete mergedRecord[field];
        changed = true;
      }
    } else if (localValue !== serverValue) {
      mergedRecord[field] = serverValue;
      changed = true;
    }
  }
  // Workspace binding: server wins when present, absence never deletes
  // (see SERVER_WINS_WHEN_PRESENT_FIELDS).
  for (const field of SERVER_WINS_WHEN_PRESENT_FIELDS) {
    const serverValue = serverRecord[field];
    if (serverValue !== undefined && mergedRecord[field] !== serverValue) {
      mergedRecord[field] = serverValue;
      changed = true;
    }
  }
  return changed ? merged : local;
}

/**
 * M1 repair-candidate selection (INCIDENT-20260818) — pure. A CONTENT-BEARING
 * local thread absent from the server snapshot and not already pending-create
 * is either top-40 aging (server doc exists) or an update-shaped-create
 * victim (doc missing / `archived`-less — index-invisible). The caller probes
 * each candidate's server doc to tell the two apart; only genuinely missing
 * docs are re-created. Drafts (no user content) are the deferred-create
 * design and are never candidates.
 */
export function selectMissingDocRepairCandidates(args: {
  localThreads: ChatThread[];
  serverIds: Set<string>;
  pendingCreateIds: Set<string>;
}): ChatThread[] {
  return args.localThreads.filter((t) =>
    !args.serverIds.has(t.id)
    && !args.pendingCreateIds.has(t.id)
    && threadRecordHasUserContent(t));
}

/**
 * What ONE live stream's snapshot actually proves (2026-09-22).
 *
 * Every thread listener is `orderBy('lastActiveAt','desc').limit(WORKING_SET_LIMIT)`,
 * so a FULL snapshot is a window, not a census: threads older than its oldest
 * row exist and are simply out of view. Absence from a window says nothing
 * about them — and the reconcile below used to read it as "deleted", drop the
 * row, and let the persist effect write the loss to localStorage and the IDB
 * mirror. Nothing re-stamps `lastActiveAt` except real activity, and an
 * invisible thread can never get any, so that loss was permanent.
 */
export interface ServerWindowCoverage {
  /**
   * The oldest `lastActiveAt` the window delivered.
   * `-Infinity` — the window was UNSATURATED, so it covered its whole scope and
   *   absence anywhere in that scope is authoritative (today's behaviour).
   * `Infinity` — saturated but no row carried a readable stamp: it proves
   *   nothing at all, the conservative direction.
   */
  floorMs: number;
  /** null = workspace-blind (the global stream covers every binding). */
  accountId: string | null;
}

/**
 * `rows === null` means the stream has not delivered — that proves nothing, so
 * callers pass null through rather than treating it as an empty census.
 */
export function windowCoverageOf(
  rows: readonly ChatThread[] | null,
  windowLimit: number,
  accountId: string | null,
): ServerWindowCoverage | null {
  if (rows === null) return null;
  // A nonsense limit must never read as "this window is full". It did once:
  // a suite mocked the repository module without the constant, `rows.length <
  // undefined` was false, and a two-row snapshot was judged saturated — so a
  // thread archived elsewhere stopped being dropped. Unknown limit ⇒ unknown
  // coverage ⇒ this window proves nothing, which fails loudly instead.
  if (!Number.isFinite(windowLimit) || windowLimit <= 0) return null;
  if (rows.length < windowLimit) return { floorMs: -Infinity, accountId };
  let floor = Infinity;
  for (const row of rows) {
    const ms = threadMillis(row.lastActiveAt);
    if (ms > 0 && ms < floor) floor = ms;
  }
  return { floorMs: floor, accountId };
}

/**
 * May a thread's ABSENCE from these windows be read as "it is gone"?
 *
 * Only when some window both COVERS it (scope) and REACHES it (strictly newer
 * than that window's floor). A tie with the floor is deliberately NOT
 * authoritative: several docs can share a stamp and the one past the limit is
 * cut arbitrarily by `__name__`, so a tie straddling the boundary proves
 * nothing either.
 *
 * An unbound thread never matches a workspace window — that companion query is
 * an equality on `accountId`, and a doc lacking the field is not in its index
 * at all.
 */
export function absenceIsAuthoritative(
  thread: Pick<ChatThread, 'lastActiveAt' | 'accountId'>,
  windows: readonly (ServerWindowCoverage | null)[],
): boolean {
  const ms = threadMillis(thread.lastActiveAt);
  for (const window of windows) {
    if (!window) continue;
    if (window.accountId !== null && (thread.accountId || '') !== window.accountId) continue;
    if (ms > window.floorMs) return true;
  }
  return false;
}

export function reconcileThreadListsWithServerSnapshot(args: {
  localThreads: ChatThread[];
  serverThreads: ChatThread[];
  pendingCreateIds: ReadonlySet<string>;
  /**
   * 2026-07-31 multi-device directive: the ACTIVE thread's chip must never
   * vanish just because it aged out of the 40-doc server window. Under the
   * old wall-clock view bump the active thread was accidentally protected
   * (viewing kept re-stamping it to the top of the window); with convergent
   * stamps a long-idle-but-open conversation can legitimately leave the
   * window, so the reconcile keeps the local record explicitly.
   */
  activeThreadId?: string | null;
  /**
   * PARTIAL-VIEW GUARD (owner report 2026-08-19 "threads are disappearing"):
   * true while one of the two merged snapshot streams (global top-40 +
   * workspace window) has not delivered yet — a half view must never DROP
   * local threads (and then have the persist effect make the loss durable).
   * Server copies still win field-wise; only the drop branch is held.
   */
  suppressDrops?: boolean;
  /**
   * WINDOW WATERMARK (2026-09-22): what the live streams actually PROVE, one
   * entry per stream — never one entry over the merged union, because the
   * global stream is workspace-blind while the companion is accountId-scoped,
   * and a thread inherits a stream's authority only inside that stream's scope.
   *
   * ABSENT ⇒ the caller asserts this snapshot is the COMPLETE set, which is the
   * pre-2026-09-22 behaviour and what every existing test relies on. The
   * production caller always passes them; a test whose denominator is every
   * call site enforces that, because chat-isolation transpiles without type
   * checking and a required parameter would not turn it red.
   */
  windows?: readonly (ServerWindowCoverage | null)[];
}): ChatThread[] {
  const { localThreads, serverThreads, pendingCreateIds, activeThreadId, suppressDrops, windows } = args;
  const byId = new Map<string, ChatThread>();
  for (const thread of serverThreads) {
    byId.set(thread.id, thread);
  }

  // Workspace binding survival (multi-org separation round 2, 2026-08-19):
  // whichever copy wins, a binding present on EITHER side survives — the
  // server's when present (immutable, authoritative), else the local stamp
  // (a new thread whose deferred/unstamped index doc hasn't caught up).
  const withBinding = (winner: ChatThread, other: ChatThread): ChatThread =>
    winner.accountId === undefined && other.accountId !== undefined
      ? { ...winner, accountId: other.accountId }
      : winner;
  // USER-CONTENT FLAG CARRY (2026-08-20): hasUserContent has round-tripped
  // through the index doc since 2026-08-16, but docs written before that (or
  // by writers that omit it) come back flag-less — and a flag-less winner
  // over a local greeted draft (explicit false) degrades to the legacy
  // messageCount>0 fallback, reclassifying the draft as HAS-CONTENT and
  // knocking it out of + reuse, blank-slate protection, and greet-on-focus.
  // Same shape as withBinding: an explicit local flag survives a flag-less
  // server winner; an explicit server flag always wins.
  //
  // ONE-DIRECTIONAL GUARD (2026-08-21 regression fix): the carry was
  // symmetric, but a local `false` is routinely STALE — reconcileThreadCounts
  // recomputes the flag from the LOCAL transcript, and on a device where that
  // transcript is absent (fresh incognito boot, quota eviction, purge) every
  // real conversation gets stamped {messageCount:0, hasUserContent:false}.
  // Carrying that false onto a content-bearing server record reclassified
  // real threads as empty drafts: "+ reuse" handed back a live conversation
  // instead of minting a greeted thread (no intro, no chips) and the rail
  // printed "· empty" on threads full of messages. A greeted draft's index
  // doc carries at most the welcome (messageCount <= 1), so the greeted-draft
  // case this carry exists for stays fully protected.
  const withUserContentFlag = (winner: ChatThread, other: ChatThread): ChatThread =>
    winner.hasUserContent === undefined
    && other.hasUserContent !== undefined
    && !(other.hasUserContent === false && (winner.messageCount || 0) > 1)
      ? { ...winner, hasUserContent: other.hasUserContent }
      : winner;

  for (const thread of localThreads) {
    // AN ARCHIVE IS A DECISION, NOT A RACE (Miles, QA account, 2026-08-27).
    //
    // He archived a conversation, confirmed the dialog, and the row never left
    // the rail — not "left and came back", never left. The stored records
    // showed 131 of 223 thread documents already carrying archived:true while
    // 41 rows were still on screen, so the write was never the problem.
    //
    // Nothing here filtered on `archived`. Hiding an archived thread relied
    // entirely on it being absent from the server snapshot (the subscription
    // queries archived == false), and the clauses below treat absence as "the
    // server has not caught up" rather than "this is gone". Two of them then
    // keep it indefinitely: suppressDrops holds the whole list while a
    // workspace snapshot is half-landed, and the repair probe marks
    // content-bearing threads missing from the snapshot as pending creates —
    // which is exactly the shape a just-archived thread has.
    //
    // Absence is ambiguous; the flag is not. A thread the user archived is
    // dropped here before any of the survival clauses get a say, so no
    // half-landed view, repair probe or active-thread exemption can preserve
    // it. Nothing is lost: the archive itself is what holds the conversation,
    // and it is reversible through the archive store.
    if (thread.archived === true) continue;
    const serverThread = byId.get(thread.id);
    if (serverThread) {
      const localMs = threadMillis(thread.lastActiveAt);
      const serverMs = threadMillis(serverThread.lastActiveAt);
      if (localMs > serverMs) {
        // Local copy wins on recency, but the routine mirror is
        // SERVER-authoritative — carry it (including deletions) so a
        // locally-active thread still reflects live routine state.
        // ...and a rename made on ANOTHER DEVICE is still the newer NAME
        // (2026-09-18): a rename does not bump `lastActiveAt`, so this arm used
        // to drop it wholesale and then mirror the stale name back up.
        // carryLocalRename compares the two histories rather than the flag, so
        // it returns the winner by reference whenever the server row carries no
        // rename — every other row is byte-identical.
        byId.set(thread.id, carryLocalRename(carryServerRoutineMirror(thread, serverThread), serverThread));
      } else if (localMs === serverMs) {
        // 2026-07-31 multi-device directive: convergent stamps make exact
        // equality the COMMON case, and the strict local-loses rule let the
        // (150ms-debounce-lagged) server echo transiently clobber this
        // device's unreadCount:0 / fresh lastViewedAt on every snapshot.
        // Ties exclude view state from the winner decision: server content
        // facts + local view state (see carryLocalViewState; consistent
        // with mergeThreadListsNewestWins' tie rule).
        byId.set(thread.id, withUserContentFlag(withBinding(carryLocalRename(carryLocalViewState(serverThread, thread), thread), thread), thread));
      } else {
        // Server copy wins outright — but never at the cost of a local-only
        // binding (an unstamped index doc must not unbind the thread), and
        // never at the cost of a name this viewer chose (2026-09-18). This arm
        // was the live half of the 2026-09-11 hole: a rename does not bump
        // `lastActiveAt`, so the turn the user had just sent was enough to make
        // the server row win the title too. The tie arm above had the carry from
        // the start; this one and the local-wins arm did not.
        byId.set(thread.id, withUserContentFlag(withBinding(carryLocalRename(serverThread, thread), thread), thread));
      }
      continue;
    }
    // Local-only threads survive the union when they are pending creates,
    // empty default DRAFTS, or the ACTIVE thread. Drafts are deliberately
    // never persisted to the index (deferred create), and the pending set is
    // in-memory — after a reload it is empty, so without this clause the
    // first snapshot would silently drop the user's draft thread. "Empty" =
    // no USER content: a greeting-seeded draft (messageCount 1,
    // hasUserContent:false) is still a draft and must survive the union too.
    // The ACTIVE thread is kept regardless (see activeThreadId doc above);
    // a genuine remote archive still closes it via the confirm-gated
    // remote-archive fallback, which checks the server doc, not the window.
    const isLocalDraft = !titleIsUserOwned(thread)
      && !threadRecordHasUserContent(thread)
      && isDefaultTitle(thread.title);
    const isActiveThread = !!activeThreadId && thread.id === activeThreadId;
    // The window watermark (2026-09-22). Without `windows` this is `true` and
    // the branch below is byte-identical to what it has always been; with them,
    // a thread that sits BELOW every covering window's floor is kept, because
    // its absence is a fact about the window's size and not about the thread.
    // For any snapshot smaller than the limit the floor is -Infinity, so every
    // existing suite and the whole lifecycle harness are unaffected.
    const absenceProvesGone = !windows || absenceIsAuthoritative(thread, windows);
    if (!suppressDrops && absenceProvesGone
      && !pendingCreateIds.has(thread.id) && !isLocalDraft && !isActiveThread) continue;
    byId.set(thread.id, thread);
  }

  return Array.from(byId.values()).sort(
    (a, b) => threadMillis(b.lastActiveAt) - threadMillis(a.lastActiveAt)
  );
}

/**
 * USER-CONTENT SEMANTICS (personalized greeting, 2026-07-13) — pure helper:
 * does a transcript hold any USER message? A seeded assistant greeting bumps
 * `messages.length` but NOT this predicate, so greeted threads still count as
 * empty drafts for every lifecycle decision.
 */
export function threadHasUserContent(
  messages: ReadonlyArray<Pick<ChatMessage, 'isUser'>> | null | undefined,
): boolean {
  if (!Array.isArray(messages)) return false;
  return messages.some((m) => !!m && m.isUser === true);
}

/**
 * POLLUTED-DRAFT GUARD (owner incognito repro 2026-08-16): does a transcript
 * hold any assistant content BEYOND the seeded welcome? The cross-thread
 * misdelivery incident left a default-titled, hasUserContent:false thread
 * holding the welcome PLUS a misdelivered assistant answer — and "+" then
 * re-served that polluted transcript as the reusable "fresh" draft
 * (SINGLE-EMPTY reuse keys on USER content only, by design for greetings).
 * A greeting is the ONLY assistant content an empty draft may carry; anything
 * else (a delivered answer, an Interrupted card, a system notice) makes the
 * thread a conversation the user must not be dropped back into as "new".
 */
export function threadHasForeignAssistantContent(
  messages: ReadonlyArray<Pick<ChatMessage, 'isUser' | 'id' | 'sourceTag'>> | null | undefined,
): boolean {
  if (!Array.isArray(messages)) return false;
  return messages.some(
    (m) => !!m && m.isUser !== true && !(m.id === 'welcome' || m.sourceTag === 'welcome'),
  );
}

/**
 * Record-level companion to `threadHasUserContent`: does this thread RECORD
 * represent user content? Uses the stamped `hasUserContent` flag when
 * present; legacy records (flag absent) fall back to `messageCount > 0`,
 * which is exactly the pre-greeting behavior (a greeting could not exist
 * before the flag did, so the fallback can never misread one).
 */
export function threadRecordHasUserContent(
  thread: Pick<ChatThread, 'messageCount' | 'hasUserContent'>,
): boolean {
  if (thread.hasUserContent !== undefined) return thread.hasUserContent === true;
  return (thread.messageCount || 0) > 0;
}

// ── Title-backfill candidacy (pure core for the Bug G backfill effect) ──────

/**
 * Cap on flood-title ('Group thread') candidates per session pass. The
 * backfill loop is strictly SERIAL (one awaited inferThreadLabel call at a
 * time), so it can never burst; this cap additionally bounds a flood-damaged
 * account's TOTAL calls per pass well under the server-side thread-label
 * token bucket (12/min). Remaining flood titles heal organically as their
 * threads are opened (mirror re-derive) or on later session passes.
 */
export const FLOOD_TITLE_BACKFILL_MAX_PER_PASS = 8;

export interface TitleBackfillCandidate {
  thread: ChatThread;
  messages: ChatMessage[];
  reason: 'ui-language' | 'flood-title';
}

/**
 * Select the threads the one-shot label backfill should re-title (pure).
 * Two candidate classes:
 *   • 'ui-language' (Bug G, unchanged): auto-generated title whose detected
 *     language mismatches the UI language;
 *   • 'flood-title' (2026-07-31): title is the flood-era 'Group thread'
 *     clobber fingerprint — healed exactly like default-titled threads,
 *     bounded to the `maxFloodTitle` MOST-RECENT (lastActiveAt desc)
 *     qualifying threads per pass.
 * Shared discipline for both: never user-renamed, never the active thread
 * (the live labeler owns it), and a stored transcript with ≥1 USER message
 * must exist to feed the labeler.
 */
export function selectTitleBackfillCandidates(args: {
  threads: readonly ChatThread[];
  activeThreadId: string | null;
  uiLanguage: UiLanguage;
  readStoredMessages: (threadId: string) => ChatMessage[];
  maxFloodTitle?: number;
}): TitleBackfillCandidate[] {
  const maxFloodTitle = args.maxFloodTitle ?? FLOOD_TITLE_BACKFILL_MAX_PER_PASS;
  const uiLanguage: TitleBackfillCandidate[] = [];
  const floodTitle: TitleBackfillCandidate[] = [];
  for (const t of args.threads) {
    if (!t || titleIsUserOwned(t)) continue;
    if (t.id === args.activeThreadId) continue;
    const isFlood = isFloodClobberedTitle(t.title);
    if (!isFlood) {
      const titleLang = detectTitleLanguage(t.title);
      if (titleLang === 'unknown') continue;
      if (titleLang === args.uiLanguage) continue;
    }
    const stored = args.readStoredMessages(t.id);
    if (!Array.isArray(stored) || stored.length === 0) continue;
    if (!threadHasUserContent(stored)) continue;
    (isFlood ? floodTitle : uiLanguage).push({
      thread: t,
      messages: stored,
      reason: isFlood ? 'flood-title' : 'ui-language',
    });
  }
  const boundedFlood = [...floodTitle]
    .sort((a, b) => threadMillis(b.thread.lastActiveAt) - threadMillis(a.thread.lastActiveAt))
    .slice(0, Math.max(0, maxFloodTitle));
  return [...uiLanguage, ...boundedFlood];
}

/**
 * Find an existing reusable EMPTY default thread (pure). The bootstrap-dedupe
 * rule (SINGLE-EMPTY invariant): no creation path may mint a second empty
 * default-titled thread — it must activate the existing one. A thread is
 * reusable when it still wears a default title (any locale/era, including
 * labeler-defaulted ones), has no USER content, and was never renamed by the
 * user. "No USER content" (not "no messages"): a greeting-seeded draft holds
 * one assistant message yet is still an empty draft — see
 * `threadRecordHasUserContent` for the legacy `messageCount` fallback.
 *
 * MIRROR-LAG GUARD (cross-thread fix, Miles 2026-07-12): the record's
 * counts are mirrored from live state one commit AFTER a message lands,
 * so for the ACTIVE thread the record can say "empty" while the pane already
 * holds a conversation. Pressing + in that window made the guard hand back the
 * ACTIVE thread as "a reusable empty draft" — the user stayed in the old
 * conversation believing it was fresh, and messages cross-threaded. For the
 * active thread the LIVE pane count is the truth; non-active threads keep the
 * record-based check (their live state isn't loaded — the record IS the truth).
 */
/**
 * STALE-DRAFT RULE (owner directive 2026-08-22, the recurring dead-thread
 * incident: "the dead thread issue keeps coming back until I clear all the
 * threads"): an empty draft older than this is NOT a fresh slate — it is
 * yesterday's abandoned draft, very possibly wearing a corrupt record the
 * greeting can't seed. Reuse must never resurrect it; "+" tidies it away
 * (auto-archive — server copy retained) and mints a genuinely fresh greeted
 * draft instead. This makes the wedge class self-cleaning: the ONE empty
 * draft the surface keeps is always the newest, seedable one.
 */
export const REUSABLE_DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export function findReusableEmptyThread(
  threads: ReadonlyArray<ChatThread>,
  live?: {
    activeThreadId: string | null;
    /**
     * Live USER-message count for the active pane (callers pass
     * `liveMessages.filter(m => m.isUser).length`). A seeded greeting must
     * not disqualify the active draft from reuse.
     */
    activeThreadLiveMessageCount: number;
    /**
     * POLLUTED-DRAFT GUARD (owner incognito repro 2026-08-16): injectable
     * transcript probe — true when the candidate's stored messages hold any
     * NON-welcome assistant content (see threadHasForeignAssistantContent).
     * The record-level hasUserContent check cannot see a misdelivered answer
     * or a system notice sitting in a "user-empty" thread, so "+" kept
     * re-serving the polluted transcript as the fresh draft. Optional and
     * injected (not read here) so this helper stays pure/storage-free.
     */
    threadHasForeignContent?: (threadId: string) => boolean;
    /**
     * M9 (INCIDENT-20260818): the ACTIVE WORKSPACE. A draft BOUND to another
     * organization must never be handed back as "the fresh thread" — the
     * user's message would land in a thread the current org's rail filters
     * out. Unbound drafts stay reusable (they bind to the current org at
     * create-flush). Null/absent → no workspace filtering (guests).
     * (Both #909 and #925 implemented this guard independently — main's
     * parameter name won the reconciliation.)
     */
    activeWorkspaceAccountId?: string | null;
    /**
     * STALE-DRAFT RULE (2026-08-22): when provided, candidates whose latest
     * activity is older than REUSABLE_DRAFT_MAX_AGE_MS are SKIPPED (and
     * collected into `staleEmptiesOut` for the caller to tidy). Opt-in via
     * nowMs so legacy callers/tests keep the exact prior contract; a
     * candidate with NO parseable timestamps counts as stale — that shape
     * IS the corrupt-record class this rule exists for.
     */
    nowMs?: number;
    staleEmptiesOut?: ChatThread[];
  },
): ChatThread | null {
  const activeWorkspace = (live?.activeWorkspaceAccountId || '').trim();
  for (const t of threads) {
    if (!t) continue;
    if (titleIsUserOwned(t)) continue;
    if (threadRecordHasUserContent(t)) continue;
    if (!isDefaultTitle(t.title)) continue;
    if (activeWorkspace && t.accountId && t.accountId !== activeWorkspace) continue;
    if (
      live
      && live.activeThreadId
      && t.id === live.activeThreadId
      && live.activeThreadLiveMessageCount > 0
    ) continue;
    if (live?.threadHasForeignContent && live.threadHasForeignContent(t.id)) continue;
    if (live?.nowMs) {
      const freshestMs = Math.max(threadMillis(t.lastActiveAt), threadMillis(t.createdAt));
      if (live.nowMs - freshestMs > REUSABLE_DRAFT_MAX_AGE_MS) {
        live.staleEmptiesOut?.push(t);
        continue;
      }
    }
    return t;
  }
  return null;
}

/**
 * DELETION RULE (owner directive 2026-08-19: "should be able to delete all
 * but the last (1) new thread user hasn't sent a message on yet") — pure.
 * The ONE protected row is the NEWEST blank slate: default title, never
 * renamed, no user content. Everything else — including the last remaining
 * CONTENT thread — is deletable (deleting the last content thread mints a
 * fresh blank slate via the removal fallback). No blank slate in the list ⇒
 * nothing is protected. Replaces the old `visible.length > 1` gates, which
 * hid the delete affordance entirely in any workspace holding exactly one
 * row — precisely the blank "New thread" the user was trying to remove.
 */
/**
 * ARCHIVE-ALWAYS (owner directive 2026-08-20 — supersedes the 2026-08-19
 * newest-blank-slate protection, whose hidden affordance left the owner
 * unable to archive either "New thread" row in an org): every thread row
 * and the header archive control stay archivable, in every workspace,
 * consistently. Archiving the LAST thread boots a fresh greeted draft
 * (removeThreadAndReturnFallback's empty-fallback path), so the protection
 * the old rule bought — never stranding the user threadless — holds by
 * construction. Named constant so the rule is greppable and test-pinned
 * rather than four scattered bare `true`s.
 */
export const ARCHIVE_ALWAYS_AVAILABLE = true;

/**
 * Deferred-create rule (pure): an EMPTY default-titled thread that the server
 * has not acknowledged yet must NOT be persisted to the thread index — it
 * exists only locally until the user gives it content (message or rename).
 * This single predicate, applied at the writeThreadToServer choke point,
 * prevents every creation path (boot, +, fallbacks, avatar recovery, reset)
 * from accumulating orphan empty docs server-side — the five-chip bug.
 *
 * "Content" means USER content: a greeting-seeded draft (messageCount 1,
 * hasUserContent:false) stays LOCAL-UNTIL-TOUCHED exactly like a bare empty
 * draft. Legacy records without the flag keep the messageCount>0 behavior.
 */
export function shouldDeferServerCreate(
  thread: Pick<ChatThread, 'id' | 'title' | 'messageCount' | 'userRenamed' | 'hasUserContent'>,
  pendingCreateIds: ReadonlySet<string>,
): boolean {
  if (!pendingCreateIds.has(thread.id)) return false;  // already on server → normal update
  if (titleIsUserOwned(thread)) return false;          // named by the user → real
  if (threadRecordHasUserContent(thread)) return false; // has USER content → real
  return isDefaultTitle(thread.title);
}

/**
 * Detect a REMOTE archive of the active thread (pure). The index snapshot only
 * contains unarchived threads, so the active thread disappearing between two
 * consecutive snapshots means either (a) it was archived from another
 * window/device, or (b) it merely fell out of the 40-doc working set. Returns
 * the thread id to CONFIRM against its server doc (never act on absence
 * alone — case (b) must not close the user's open conversation), or null when
 * there is nothing to check. Pending-create ids are exempt: a freshly created
 * thread is legitimately absent until its create write round-trips.
 */
export function planRemoteArchiveCheck(args: {
  prevServerIds: ReadonlySet<string> | null;
  currentServerIds: ReadonlySet<string>;
  activeThreadId: string | null;
  pendingCreateIds: ReadonlySet<string>;
}): string | null {
  const { prevServerIds, currentServerIds, activeThreadId, pendingCreateIds } = args;
  if (!activeThreadId || !prevServerIds) return null;
  if (pendingCreateIds.has(activeThreadId)) return null;
  if (!prevServerIds.has(activeThreadId)) return null;
  if (currentServerIds.has(activeThreadId)) return null;
  return activeThreadId;
}

/**
 * Reconcile persisted thread metadata counts against per-thread message stores.
 *
 * Read-only: this helper never writes to storage.
 */
export function reconcileThreadCounts(threads: ChatThread[], instanceId: string, uid?: string | null): ChatThread[] {
  let changed = false;
  const reconciled = threads.map((raw) => {
    // BOOT HEAL (2026-09-18) — FIRST, above every early return below.
    //
    // The stored thread list is read RAW: unlike the Firestore boundary
    // (chatThreadIndexFirestore's fromChatThreadDoc, which runs
    // authoritativeThreadTitle on every parse), nothing applies the rename
    // history to a localStorage row. So a row this device wrote while the
    // mirror effect was still overwriting chosen names keeps the wrong title
    // until a snapshot with a non-newer stamp happens to arrive. Reading the
    // history back here makes "every row's stored title equals its
    // authoritative title" true at boot, on every path, for rows already in
    // that state — `resolveMirrorThreadTitle` is what stops new ones being
    // made. Identity for every healthy row, which is all of them once nothing
    // is overwriting renames any more.
    const authoritative = titleIsUserOwned(raw) ? authoritativeThreadTitle(raw) : '';
    const thread = authoritative && authoritative !== raw.title
      ? { ...raw, title: authoritative }
      : raw;
    if (thread !== raw) changed = true;
    const storageKey = threadMsgKey(instanceId, thread.id, uid);
    // ABSENT IS NOT EMPTY (QA series, 2026-08-25: two finished conversations
    // vanished from the rail after switching threads in an incognito window).
    //
    // getFromLocalStorage returns [] both when the transcript is genuinely
    // empty and when this device has simply never stored one. Reconciling
    // against that second case stamps every real conversation
    // {messageCount: 0, hasUserContent: false} — and a record wearing that
    // stamp is, to the rest of the rail, a blank draft: "+" reuse claims it
    // and tidyStaleEmptyDrafts archives it. Incognito, a second browser, a
    // storage eviction and a fresh device all present exactly this way.
    //
    // The healing case this function exists for — the tab dying between the
    // send path's store write and the mirror commit — leaves the key PRESENT
    // with a transcript to count, so it is untouched by this guard.
    if (!localStorageHasKey(storageKey)) return thread;
    const stored = getFromLocalStorage<ChatMessage[]>(storageKey, []);
    // SETTLED-ONLY count (owner feedback, Miles 2026-08-01): the on-disk
    // transcript can hold a persisted in-flight placeholder (e.g. a
    // resumable server task surviving a refresh) — the reconciled count must
    // exclude it, exactly like the mirror effect's stamp, so a hydrate never
    // bumps a row back to N+1 while the turn is still running.
    const nextMessageCount = settledMessageCount(stored);
    const hasLastActiveAt = typeof thread.lastActiveAt === 'string' && thread.lastActiveAt.length > 0;
    const nextLastActiveAt = hasLastActiveAt
      ? thread.lastActiveAt
      : (thread.createdAt || new Date().toISOString());
    // USER-CONTENT SEMANTICS: heal a stale stamped flag from the on-disk
    // transcript (e.g. the tab died between the send path's synchronous
    // store write and the mirror commit that stamps the record). Records
    // that never carried the flag (legacy) are left alone — consumers fall
    // back to messageCount, exactly as before this field existed.
    const nextHasUserContent = thread.hasUserContent === undefined
      ? undefined
      : threadHasUserContent(stored);

    if (
      nextMessageCount === thread.messageCount
      && nextLastActiveAt === thread.lastActiveAt
      && nextHasUserContent === thread.hasUserContent
    ) {
      return thread;
    }

    changed = true;
    const updated: ChatThread = {
      ...thread,
      messageCount: nextMessageCount,
      lastActiveAt: nextLastActiveAt,
    };
    if (nextHasUserContent !== undefined) updated.hasUserContent = nextHasUserContent;
    return updated;
  });

  return changed ? reconciled : threads;
}

/**
 * What a `migrateLegacyThreadStorageForUid` run actually moved into the uid's
 * namespace — the exact adoption set for the G3 server push. Empty on every
 * no-op run (no legacy data, cross-account guard purge, keys already scoped),
 * so callers can key server adoption on "this run migrated guest data" without
 * re-deriving ownership.
 */
export interface LegacyThreadMigrationResult {
  /** Thread ids whose per-thread transcripts were copied into the uid namespace by THIS run. */
  migratedThreadIds: string[];
  /** Active-thread pointer copied into the uid namespace by THIS run (null when none/kept). */
  migratedActiveThreadId: string | null;
}

export function migrateLegacyThreadStorageForUid(instanceId: string, uid: string): LegacyThreadMigrationResult {
  const result: LegacyThreadMigrationResult = { migratedThreadIds: [], migratedActiveThreadId: null };
  if (!uid || typeof localStorage === 'undefined') return result;
  const legacy = getThreadStorageKeyPrefixes(instanceId);
  const scoped = getThreadStorageKeys(instanceId, uid);
  const migrationOwnerKey = `prest0_thread_legacy_owner__${instanceId || 'global_chat'}`;

  // A uid-SCOPED msg key EXTENDS the legacy prefix (`<legacyPrefix>u_<uid>__…`,
  // see getThreadStorageKeys), so any startsWith(legacy prefix) scan matches
  // scoped keys too. Scoped keys are the DESTINATION namespace of this
  // migration — never legacy data — and must be excluded from the legacy
  // detection AND the legacy purge. Without this, every hydrate with scoped
  // data present read `hasLegacyData=true`, failed the ownership check
  // (ownerUid unset), and purgeLegacyBaseKeys swept the CURRENT user's
  // per-thread transcripts on each remount (2026-07-13; masked for
  // server-synced threads by hydrateThreadMessagesFromServerIfEmpty, fatal
  // for local-only drafts, e.g. greeting-seeded ones). Legacy thread ids are
  // `t_…` (generateThreadId), so a `u_…__` remainder is unambiguously scoped.
  const isScopedMsgKey = (key: string): boolean => {
    if (!key.startsWith(legacy.THREAD_MSG_PREFIX)) return false;
    const rest = key.slice(legacy.THREAD_MSG_PREFIX.length);
    return rest.startsWith('u_') && rest.indexOf('__', 2) > 0;
  };

  const purgeLegacyBaseKeys = () => {
    try { localStorage.removeItem(legacy.THREAD_LIST); } catch { /* ignore */ }
    try { localStorage.removeItem(legacy.ACTIVE_THREAD); } catch { /* ignore */ }
    try { localStorage.removeItem(legacy.THREAD_ARCHIVE); } catch { /* ignore */ }
    const keys: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(legacy.THREAD_MSG_PREFIX)) continue;
        if (isScopedMsgKey(key)) continue; // never purge the scoped namespace
        keys.push(key);
      }
    } catch {
      return;
    }
    for (const key of keys) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  };

  const hasLegacyData = (() => {
    try {
      if (localStorage.getItem(legacy.THREAD_LIST) !== null) return true;
      if (localStorage.getItem(legacy.ACTIVE_THREAD) !== null) return true;
      if (localStorage.getItem(legacy.THREAD_ARCHIVE) !== null) return true;
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(legacy.THREAD_MSG_PREFIX) && !isScopedMsgKey(key)) return true;
      }
    } catch {
      return false;
    }
    return false;
  })();
  if (!hasLegacyData) return result;

  let ownerUid: string | null = null;
  try {
    ownerUid = localStorage.getItem(migrationOwnerKey);
  } catch {
    ownerUid = null;
  }

  const parseUidFromScopedKey = (key: string): string | null => {
    const marker = '__u_';
    const idx = key.indexOf(marker);
    if (idx < 0) return null;
    const start = idx + marker.length;
    const end = key.indexOf('__', start);
    if (end < 0) {
      const parsed = key.slice(start);
      return parsed || null;
    }
    const parsed = key.slice(start, end);
    return parsed || null;
  };

  const scopedPrefixesForInstance = [
    `${legacy.THREAD_LIST}__u_`,
    `${legacy.ACTIVE_THREAD}__u_`,
    `${legacy.THREAD_ARCHIVE}__u_`,
    `${legacy.THREAD_MSG_PREFIX}u_`,
  ];

  let hasScopedForOtherUid = false;
  let hasScopedForCurrentUid = false;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!scopedPrefixesForInstance.some((prefix) => key.startsWith(prefix))) continue;
      const keyUid = parseUidFromScopedKey(key);
      if (!keyUid) continue;
      if (keyUid === uid) {
        hasScopedForCurrentUid = true;
      } else {
        hasScopedForOtherUid = true;
      }
    }
  } catch {
    // ignore scan failure and fall through to conservative legacy purge
    hasScopedForOtherUid = true;
  }

  // Cross-account safety guard:
  // only migrate unscoped legacy keys when we can reasonably attribute them
  // to this uid. Otherwise purge legacy keys so a subsequent account cannot
  // adopt stale data into its namespace.
  if (
    hasScopedForOtherUid
    || (ownerUid && ownerUid !== uid)
    || (hasScopedForCurrentUid && ownerUid !== uid)
  ) {
    // M2 diagnosability (INCIDENT-20260818): this purge used to be silent —
    // guest-era threads deleted here were undiagnosable. The DECISION is
    // unchanged (it is a cross-account-leak guard); it just says what it did.
    try {
      const legacyList = getFromLocalStorage<ChatThread[]>(legacy.THREAD_LIST, []);
      console.warn('[useChatThreads] purging unscoped legacy thread storage (cross-account guard)', {
        uid,
        reason: hasScopedForOtherUid ? 'scoped-keys-for-other-uid'
          : (ownerUid && ownerUid !== uid) ? 'legacy-owner-mismatch'
          : 'scoped-exists-without-owner-stamp',
        legacyThreadIds: (Array.isArray(legacyList) ? legacyList : []).map((t) => t?.id).filter(Boolean),
      });
    } catch { /* logging only */ }
    purgeLegacyBaseKeys();
    return result;
  }

  const pairs: Array<[legacyKey: string, scopedKey: string]> = [
    [legacy.THREAD_LIST, scoped.THREAD_LIST],
    [legacy.ACTIVE_THREAD, scoped.ACTIVE_THREAD],
    [legacy.THREAD_ARCHIVE, scoped.THREAD_ARCHIVE],
  ];

  for (const [legacyKey, scopedKey] of pairs) {
    try {
      const scopedExists = localStorage.getItem(scopedKey) !== null;
      if (scopedExists) continue;
      const legacyValue = localStorage.getItem(legacyKey);
      if (legacyValue === null) continue;
      localStorage.setItem(scopedKey, legacyValue);
      localStorage.removeItem(legacyKey);
      if (legacyKey === legacy.ACTIVE_THREAD) {
        // Stored via setToLocalStorage → JSON-encoded id; tolerate raw ids.
        let activeId: unknown = legacyValue;
        try { activeId = JSON.parse(legacyValue); } catch { activeId = legacyValue; }
        result.migratedActiveThreadId = (typeof activeId === 'string' && activeId) ? activeId : null;
      }
    } catch {
      // ignore storage failures
    }
  }

  const legacyMsgPrefix = legacy.THREAD_MSG_PREFIX;
  const scopedMsgPrefix = scoped.THREAD_MSG_PREFIX;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(legacyMsgPrefix)) continue;
      if (isScopedMsgKey(key)) continue; // already in the scoped namespace
      keys.push(key);
    }
    for (const key of keys) {
      const threadId = key.slice(legacyMsgPrefix.length);
      if (!threadId) continue;
      const scopedKey = `${scopedMsgPrefix}${threadId}`;
      const scopedExists = localStorage.getItem(scopedKey) !== null;
      if (scopedExists) continue;
      const legacyValue = localStorage.getItem(key);
      if (legacyValue === null) continue;

      // GREETING-ONLY TRANSCRIPTS ARE NOT ADOPTED (2026-08-03, owner report:
      // "a brand-new trial account was greeted with the generic marketing
      // greeting instead of its personalized welcome").
      //
      // Root cause: the visitor browses prest0.ai signed out, the guest gate
      // persists GUEST_WELCOME as the id:'welcome' first message of the
      // unscoped thread, and sign-in adopted that thread — transcript
      // included. Every re-greeting path bails when a thread's store is
      // non-empty, so the stale guest bubble outlived the sign-in and the
      // personalized welcome (already loaded into config) never rendered.
      //
      // A transcript with NO user message carries nothing the user authored,
      // so dropping it is lossless: the thread record still migrates,
      // reconcileThreadCounts recomputes messageCount to 0, and the existing
      // greet-on-focus effect reseeds the correct personalized greeting.
      // Unparseable values keep the old copy behavior — never lose real data
      // to an unexpected shape.
      let isGreetingOnly = false;
      try {
        const parsed = JSON.parse(legacyValue);
        isGreetingOnly = Array.isArray(parsed) && !threadHasUserContent(parsed);
      } catch {
        isGreetingOnly = false;
      }
      if (isGreetingOnly) {
        localStorage.removeItem(key);
        continue;
      }

      localStorage.setItem(scopedKey, legacyValue);
      localStorage.removeItem(key);
      result.migratedThreadIds.push(threadId);
    }
  } catch {
    // ignore storage failures
  }

  try {
    localStorage.setItem(migrationOwnerKey, uid);
  } catch {
    // ignore storage failures
  }
  return result;
}

/**
 * G3 adoption once-marker STATE (review finding 2, 2026-07-15). The marker
 * used to be a bare timestamp set BEFORE the fire-and-forget upserts, which
 * made adoption one-shot with no failure recovery: an offline sign-in or a
 * tab closed inside the 150ms write debounce lost the push forever (the next
 * reload's migration finds no legacy data, the reconcile drops the chips,
 * and the transcripts strand in scoped storage). The marker now carries the
 * per-thread outcome so the next hydrate can finish the job:
 *   • `pending`  — ids whose index+transcript upserts have NOT been confirmed
 *     fulfilled (recorded durably BEFORE the upserts fire, so even a killed
 *     tab leaves a to-do); re-fired by `resumePendingGuestAdoption`.
 *   • `adopted`  — ids whose upserts all fulfilled (write acked by the repo).
 * Re-fires are safe: both upserts are id-keyed setDoc merges fed from the
 * uid-scoped store, so convergence is idempotent.
 */
export interface GuestAdoptionMarkerState {
  adopted: string[];
  pending: string[];
}

/**
 * Read the adoption marker. Returns null when the marker is absent (no
 * adoption ran this sign-in era). A legacy pre-state marker (bare ISO
 * timestamp) parses as "complete, nothing pending" — exactly its old meaning.
 */
export function readGuestAdoptionMarker(markerKey: string): GuestAdoptionMarkerState | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(markerKey);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as { adopted?: unknown; pending?: unknown };
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.adopted) && Array.isArray(parsed.pending)) {
      return {
        adopted: parsed.adopted.filter((id): id is string => typeof id === 'string'),
        pending: parsed.pending.filter((id): id is string => typeof id === 'string'),
      };
    }
  } catch { /* legacy bare-timestamp marker */ }
  return { adopted: [], pending: [] };
}

/** Write the adoption marker (with a timestamp for debuggability). */
export function writeGuestAdoptionMarker(markerKey: string, state: GuestAdoptionMarkerState): void {
  try {
    localStorage.setItem(markerKey, JSON.stringify({
      adopted: state.adopted,
      pending: state.pending,
      at: new Date().toISOString(),
    }));
  } catch { /* best-effort — an unwritable marker degrades to the pre-fix behavior */ }
}

export interface PlannedThreadRemoval {
  removedThread: ChatThread | null;
  remaining: ChatThread[];
  fallbackThreadId: string | null;
}

/**
 * Point 2 T3 fix (Malaika 2026-06-02). Pure predicate for the message
 * mirror effect: should we DEFER persisting `state.messages` under the
 * current `activeThreadId` for this commit?
 *
 * Returns true when the active thread changed since the last time the
 * mirror effect persisted (`prevMirroredActiveTid !== activeThreadId`).
 * On that transitional commit `activeThreadId` (a useState value) may
 * already be the new thread while `messages` (a separate reducer store
 * updated via dispatch(SET_MESSAGES)) still holds the OUTGOING thread's
 * content — persisting would write the wrong thread's messages under the
 * new thread's per-thread key + server doc, producing a permanent
 * label↔content mismatch on the chip. The effect re-fires once messages
 * catch up, so deferring one commit is safe and lossless (the outgoing
 * thread was already snapshotted before the switch).
 */
export function shouldDeferMirrorPersist(
  prevMirroredActiveTid: string | null,
  activeThreadId: string | null,
): boolean {
  return !!prevMirroredActiveTid
    && !!activeThreadId
    && prevMirroredActiveTid !== activeThreadId;
}

export function planThreadRemoval(
  threads: ChatThread[],
  threadId: string,
  activeThreadId: string | null,
  /**
   * WORKSPACE-SCOPED FALLBACK (multi-org separation round 2, 2026-08-19):
   * the store's list spans EVERY workspace (one localStorage list per
   * device), but the rail shows only the active workspace's threads — so
   * picking the globally newest remaining thread could silently activate a
   * FOREIGN workspace's conversation the rail doesn't even display
   * (Fernando's empty-org repro). When provided, the fallback considers
   * only threads visible in this workspace (bound to it, or unbound);
   * none → null, and the caller mints a fresh in-workspace draft.
   */
  activeAccountId?: string | null,
): PlannedThreadRemoval {
  const removedThread = threads.find((thread) => thread.id === threadId) || null;
  if (!removedThread) {
    return {
      removedThread: null,
      remaining: threads,
      fallbackThreadId: activeThreadId,
    };
  }

  const remaining = threads.filter((thread) => thread.id !== threadId);
  if (threadId !== activeThreadId) {
    return {
      removedThread,
      remaining,
      fallbackThreadId: activeThreadId,
    };
  }

  const active = (activeAccountId || '').trim();
  const eligible = active
    ? remaining.filter((t) => !t.accountId || t.accountId === active)
    : remaining;
  const nextActive = sortThreads(eligible, null)[0] || null;
  return {
    removedThread,
    remaining,
    fallbackThreadId: nextActive ? nextActive.id : null,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────────────

/** Monotonic id source for thread-sync writer tokens (one per hook instance). */
let threadSyncWriterSeq = 0;

export function useChatThreads(opts: UseChatThreadsOptions): UseChatThreadsResult {
  // `opts.isProcessing` intentionally not destructured — the per-thread map
  // (state.processingByThread) is the sole spinner source of truth (Phase 2).
  const { instanceId, enabled, messages, dispatch, getLatestMessages, composeGreeting, groupReadAccountId, workspaceIdentityPending, onForeignThreadSwitch, sharedThreadIds, registeredThreadIds, isAutoTitleSuppressed, onAutoTitleResolved } = opts;
  // Read through refs so the labeler effects don't re-fire (and re-infer) every
  // time the host re-creates these callbacks.
  const isAutoTitleSuppressedRef = useRef(isAutoTitleSuppressed);
  isAutoTitleSuppressedRef.current = isAutoTitleSuppressed;
  const onAutoTitleResolvedRef = useRef(onAutoTitleResolved);
  onAutoTitleResolvedRef.current = onAutoTitleResolved;
  const autoTitleSuppressed = useCallback((threadId: string): boolean => {
    try {
      return isAutoTitleSuppressedRef.current?.(threadId) === true;
    } catch {
      return false; // fail-open: a broken predicate must not stop labeling
    }
  }, []);
  /** Mirror the optional accessor in a ref so callbacks read the latest. */
  const getLatestMessagesRef = useRef<typeof getLatestMessages>(getLatestMessages);
  useEffect(() => { getLatestMessagesRef.current = getLatestMessages; }, [getLatestMessages]);
  /** Mirror the optional greeting composer in a ref (same pattern as above). */
  const composeGreetingRef = useRef<typeof composeGreeting>(composeGreeting);
  useEffect(() => { composeGreetingRef.current = composeGreeting; }, [composeGreeting]);

  /**
   * Build the seed messages for a freshly-minted thread: the personalized
   * greeting as a persisted first assistant message when the host wired a
   * composer (and it yields copy), else the legacy empty array. The composer
   * is host code — any throw degrades to the legacy behavior, never blocks
   * thread creation.
   */
  const composeGreetingSeed = useCallback((): ChatMessage[] => {
    const compose = composeGreetingRef.current;
    if (!compose) return [];
    let greeting: {
      text: string;
      html: string;
      tokenUsage?: TokenUsageSnapshot;
      greetingRank?: number;
      starters?: string[];
    } | null = null;
    try {
      greeting = compose() || null;
    } catch {
      greeting = null;
    }
    if (!greeting || !greeting.text) return [];
    return [{
      id: 'welcome',
      isUser: false,
      sourceTag: 'welcome',
      timestamp: new Date(),
      text: greeting.text,
      html: greeting.html,
      // Multi-tab greeting parity (2026-08-09): the copy's monotonic rank and
      // its one-click starters persist WITH the greeting, so (a) the ranked
      // reconciliation below can refuse downgrades and (b) every tab renders
      // the same chips for the same thread.
      ...(typeof greeting.greetingRank === 'number' ? { greetingRank: greeting.greetingRank } : {}),
      ...(greeting.starters?.length ? { starters: [...greeting.starters] } : {}),
      // AI-distilled greeting: carry the distillation usage so the welcome
      // bubble's mandatory ⓘ shows real model/token/cost data.
      ...(greeting.tokenUsage ? { tokenUsage: greeting.tokenUsage } : {}),
    }];
  }, []);
  // Identity of THIS hook instance on the thread-sync channel. Every persist
  // this hook makes carries the token, and its own subscription registers with
  // it, so a write can never bounce back into this instance as a "foreign
  // tab" notification (same-tab echo loop → OOM incident, Miles 2026-07-12).
  const threadSyncWriterTokenRef = useRef<string>('');
  if (threadSyncWriterTokenRef.current === '') {
    threadSyncWriterSeq += 1;
    threadSyncWriterTokenRef.current = `chat-threads-writer-${threadSyncWriterSeq}`;
  }
  const [currentUid, setCurrentUid] = useState<string | null>(() => getAuth().currentUser?.uid || null);
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || null);
    });
  }, []);
  const storageKeys = useMemo(() => getThreadStorageKeys(instanceId, currentUid), [instanceId, currentUid]);

  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  /**
   * OLDER THREADS (2026-09-22). The live listeners deliver only the newest
   * WORKING_SET_LIMIT threads, so a saturated window is the ONLY evidence that
   * older ones exist at all. `windowSaturated` is set from the same raw stream
   * arrays the reconcile watermark reads, which is why a small account never
   * sees the affordance: its window is never full, so there is nothing below it.
   */
  const [windowSaturated, setWindowSaturated] = useState<boolean>(false);
  const [isLoadingOlderThreads, setIsLoadingOlderThreads] = useState<boolean>(false);
  const [olderThreadsExhausted, setOlderThreadsExhausted] = useState<boolean>(false);
  // A DocumentSnapshot is heavy and non-serializable: it must never enter React
  // state or the persisted thread list.
  const olderThreadsCursorRef = useRef<DocumentSnapshot | null>(null);
  const loadingOlderRef = useRef(false);
  /** uid + surface + workspace: the scope a cursor is only valid within. */
  const olderThreadsScopeRef = useRef<string>('');
  const enableServerThreadIndex = useMemo(() => resolveServerThreadIndexFlag(), []);
  const latestThreadsRef = useRef<ChatThread[]>(threads);
  const latestActiveThreadIdRef = useRef<string | null>(activeThreadId);
  const activeThreadIdRef = latestActiveThreadIdRef;
  // See UseChatThreadsResult.getActiveThreadIdSync (2026-08-16 cross-thread
  // delivery incident): the SYNC accessor hosts must capture through instead
  // of closing over the lagging `activeThreadId` state. Stable identity so
  // the host bridge effect assigns once and never re-opens a stale window.
  const getActiveThreadIdSync = useCallback(
    (): string | null => latestActiveThreadIdRef.current,
    [],
  );
  const prevAuthedUidRef = useRef<string | null>(null);
  const latestAuthedUidRef = useRef<string | null>(currentUid);

  useEffect(() => { latestThreadsRef.current = threads; }, [threads]);
  useEffect(() => { latestActiveThreadIdRef.current = activeThreadId; }, [activeThreadId]);
  useEffect(() => { latestAuthedUidRef.current = currentUid; }, [currentUid]);

  useEffect(() => {
    const prev = prevAuthedUidRef.current;
    if (prev && currentUid && prev !== currentUid) {
      purgeThreadCache({ keepUid: currentUid });
    }
    prevAuthedUidRef.current = currentUid;
  }, [currentUid]);

  // Blip-safe sign-out flush (2026-08-21 regression fix): a DELIBERATE
  // session reset (sign-out / account switch via resetSession) stamps this
  // ref; the full-flush effect below treats a member→null uid transition as
  // destructive ONLY when it is deliberate or when the null state persists.
  // Raw onAuthStateChanged can emit uid→null→uid during a token refresh
  // (incognito's session-persistence makes this common), and the 2026-08-21
  // flush keyed on that raw flip destroyed a live member's rendered threads,
  // localStorage AND the IndexedDB recovery mirrors mid-conversation.
  const deliberateResetAtRef = useRef<number>(0);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onSessionReset = (e: Event) => {
      // Round P (both halves): resetSession('no_user') fires on EVERY
      // signed-out page boot and still dispatches this event — nothing
      // departed, so purging here would wipe the GUEST's unscoped history
      // (the session.ts direct purge carries the same gate).
      const reason = (e as CustomEvent)?.detail?.reason;
      if (reason === 'no_user') return;
      deliberateResetAtRef.current = Date.now();
      const departingUid =
        latestAuthedUidRef.current
        || prevAuthedUidRef.current
        || getAuth().currentUser?.uid
        || null;
      purgeThreadStorageForSessionReset(instanceId, departingUid);
      prevAuthedUidRef.current = null;
    };
    window.addEventListener('app:session_reset', onSessionReset as EventListener);
    return () => {
      window.removeEventListener('app:session_reset', onSessionReset as EventListener);
    };
  }, [enabled, instanceId]);

  const getServerSyncUid = useCallback((): string | null => {
    const uid = getAuth().currentUser?.uid || null;
    return canUseServerThreadIndex(enableServerThreadIndex, uid) ? uid : null;
  }, [enableServerThreadIndex]);

  // BUG-THREAD-RESURRECTION (2026-06-11 backlog item 2.2): session-scoped
  // tombstones for threads the user archived/deleted on THIS client. Two
  // resurrection vectors both flow through here:
  //   (a) The Firestore archive write is debounced (150ms) + queued, so a
  //       subscription snapshot arriving in that window still contains the
  //       thread (archived:false) and the union-style merge re-adds the chip
  //       — permanently, because the merge never removes local entries when
  //       later snapshots drop them.
  //   (b) toChatThreadDoc() hardcodes archived:false, so ANY later upsert of
  //       an archived thread (count reconcile, lastActiveAt bump) silently
  //       un-archives it server-side.
  // Tombstoned ids are excluded from server snapshots before merging and
  // block all future upserts for the rest of the session. There is no
  // restore-from-archive flow today; if one is added it must clear the
  // thread's tombstone here.
  const archivedTombstonesRef = useRef<Map<string, number>>(new Map());
  const pendingServerCreateIdsRef = useRef<Set<string>>(new Set());
  // M1 repair probe (INCIDENT-20260818): thread ids already probed for a
  // missing/broken server index doc this session — once each.
  const missingDocProbedIdsRef = useRef<Set<string>>(new Set());
  // Remote-archive detection (cross-window): ids seen in the PREVIOUS index
  // snapshot, and the deferred confirm/fallback handler (assigned in an effect
  // below, after switchThread/newThread exist).
  const prevServerSnapshotIdsRef = useRef<Set<string> | null>(null);
  const confirmRemoteArchiveRef = useRef<((threadId: string) => Promise<void>) | null>(null);
  /** Shared threads whose remote archive was already announced this session. */
  const remoteArchiveNoticeShownRef = useRef<Set<string>>(new Set());
  /**
   * Vanished SHARED thread ids still awaiting their archive probe. Kept across
   * snapshots because the "was in the previous snapshot" test is consumed the
   * moment prevServerSnapshotIds is overwritten — a candidate deferred by the
   * per-snapshot cap would otherwise never be probed, announced or tombstoned.
   */
  const pendingArchiveProbeIdsRef = useRef<Set<string>>(new Set());
  /** Handle for the 5s remote-archive hold, so teardown can cancel it. */
  const archiveHoldTimerRef = useRef<number | null>(null);
  // BOOT-DEDUPE: first-snapshot marker + the handler that adopts a server
  // thread when the local bootstrap draft is dropped (assigned below).
  const firstSnapshotConsumedRef = useRef<boolean>(false);
  const adoptServerThreadRef = useRef<((threadId: string) => void) | null>(null);
  // Draft ids MINTED BY THIS SESSION'S hydrate bootstrap (i.e. created
  // speculatively before the first server snapshot could tell us the account
  // already has threads). Only THESE are eligible for the boot-dedupe drop:
  // a draft RESTORED from storage is a deliberate user artifact (the user
  // pressed + and may be looking at its persisted greeting right now) and
  // must survive reload — see the first-snapshot handler below.
  const sessionBootstrapDraftIdsRef = useRef<Set<string>>(new Set());
  // WORKSPACE-CONTAINMENT exemption ledger (2026-08-20, owner repro: the
  // typed first message vanished mid-send). Every thread id THIS SESSION
  // minted — newThread, hydrate bootstrap, boot containment. The live
  // containment effect must never close these under the user, and
  // pendingServerCreateIds cannot carry that exemption: it is re-armed at
  // hydrate for OLD stored drafts (which must NOT count as session-minted)
  // and the first send's own create echo deletes the id from it — the
  // exemption evaporated exactly when the send promoted the draft. Nothing
  // snapshot-driven ever clears this set; it resets only at the auth
  // boundary (same effect that clears the pending-create set).
  const sessionMintedThreadIdsRef = useRef<Set<string>>(new Set());

  const pruneExpiredTombstones = useCallback(() => {
    const now = Date.now();
    let changed = false;
    archivedTombstonesRef.current.forEach((expiresAt, threadId) => {
      if (expiresAt > now) return;
      archivedTombstonesRef.current.delete(threadId);
      changed = true;
    });
    if (changed) {
      persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
    }
  }, [instanceId, currentUid]);

  const isThreadTombstoned = useCallback((threadId: string): boolean => {
    const expiresAt = archivedTombstonesRef.current.get(threadId);
    if (!expiresAt) return false;
    if (expiresAt > Date.now()) return true;
    archivedTombstonesRef.current.delete(threadId);
    persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
    return false;
  }, [instanceId, currentUid]);

  const addArchivedTombstone = useCallback((threadId: string) => {
    archivedTombstonesRef.current.set(threadId, Date.now() + ARCHIVED_TOMBSTONE_TTL_MS);
    persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
  }, [instanceId, currentUid]);

  useEffect(() => {
    if (!enabled) return;
    archivedTombstonesRef.current = loadDurableTombstones(instanceId, currentUid);
    pruneExpiredTombstones();
  }, [enabled, instanceId, currentUid, pruneExpiredTombstones]);

  // BUG-THREAD-RESURRECTION (multi-tab vector): archiving in tab A must
  // immediately block tab B's upserts for that thread — otherwise B's next
  // count-reconcile / lastActiveAt bump re-stamps archived:false onto the
  // server doc and resurrects the chip everywhere. Tombstones are already
  // persisted under a shared localStorage key; the `storage` event (which
  // fires only in OTHER tabs) is the sync channel. Additive union-merge:
  // this never clears tombstones this tab added (see mergeTombstoneMaps).
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    const watchedKey = tombstoneStorageKey(instanceId, currentUid);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== watchedKey) return;
      const incoming = loadDurableTombstones(instanceId, currentUid);
      const merged = mergeTombstoneMaps(archivedTombstonesRef.current, incoming);
      if (!merged.changed) return;
      archivedTombstonesRef.current = merged.tombstones;
      // Persist the union so the on-disk map converges across tabs (the
      // `changed` guard above terminates the persist→event ping-pong).
      persistDurableTombstones(instanceId, currentUid, merged.tombstones);
      if (merged.added.size > 0) {
        // Drop newly-tombstoned threads from this tab's rail right away;
        // the server snapshot converges too, but only after the archive
        // write lands remotely.
        setThreads((prev) => prev.filter((thread) => !merged.added.has(thread.id)));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [enabled, instanceId, currentUid]);

  const writeThreadToServer = useCallback((thread: ChatThread) => {
    const uid = getServerSyncUid();
    if (!uid) return;
    if (isThreadTombstoned(thread.id)) {
      // eslint-disable-next-line no-console
      console.warn('chatThreadIndex.upsert blocked for archived thread (tombstoned)', thread.id);
      return;
    }
    // BOOTSTRAP-DEDUPE (five-chip bug): an empty default-titled thread the
    // server hasn't acked stays LOCAL-ONLY — it reaches the index only once
    // the user gives it content (message/rename), at which point this same
    // choke point performs the create. Until then no device can accumulate
    // orphan empty docs, and reconcile keeps the local draft alive via the
    // pendingServerCreateIds exemption.
    if (shouldDeferServerCreate(thread, pendingServerCreateIdsRef.current)) return;
    // BUG-THREAD-RESURRECTION (root fix): only a CREATE stamps archived:false
    // (the index query needs the field); every update omits it so this write
    // can never un-archive a thread that another window archived. The pending
    // set holds exactly the ids created locally and not yet server-acked.
    const isCreate = pendingServerCreateIdsRef.current.has(thread.id);
    void upsertChatThread(uid, instanceId, thread, { isCreate }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('chatThreadIndex.upsert failed', error);
    });
  }, [getServerSyncUid, instanceId, isThreadTombstoned]);

  const writeActiveThreadToServer = useCallback((threadId: string) => {
    const uid = getServerSyncUid();
    if (!uid) return;
    void setActiveThread(uid, instanceId, threadId).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('chatThreadIndex.setActiveThread failed', error);
    });
  }, [getServerSyncUid, instanceId]);

  const archiveThreadOnServer = useCallback((threadId: string) => {
    const uid = getServerSyncUid();
    if (!uid) return;
    void archiveChatThread(uid, instanceId, threadId).then(() => {
      // A CONFIRMED archive outlives the retry window (Miles, 2026-08-27).
      //
      // He archived a thread, the row left the rail, he refreshed, and it was
      // back — repeatedly, 28 -> 27 -> 28. The server write was never the
      // problem: his two clicks are in the index as archived:true, stamped
      // 12:13:39 and 12:14:04.
      //
      // What brought them back is the tombstone expiring. Its 15-minute TTL
      // is deliberate and the reasoning is sound: a tombstone is the only
      // thing suppressing a thread between the click and the write landing,
      // so if the write PERMANENTLY fails the thread must resurface rather
      // than stay hidden with no record anywhere that it was archived. The
      // TTL is the safety net for that case.
      //
      // But it was applied to every archive, confirmed or not. Once the write
      // has landed the server doc is the durable record — the subscription
      // filters archived == false and stops sending it — and the tombstone's
      // only remaining job is to suppress the local rehydration paths
      // (localStorage list, IDB mirror, half-landed snapshots) that still
      // hold a pre-archive copy. Those outlive fifteen minutes; a reload an
      // hour later still reads that stale local list.
      //
      // So a CONFIRMED archive gets a tombstone that does not expire, and an
      // unconfirmed one keeps the original TTL and the original safety
      // property. The failure case is unchanged; only the success case stops
      // forgetting.
      archivedTombstonesRef.current.set(threadId, CONFIRMED_ARCHIVE_TOMBSTONE_AT);
      persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
    }).catch((error) => {
      // Left on the 15-minute TTL on purpose: an archive that never landed
      // must resurface rather than hide behind a permanent tombstone.
      // eslint-disable-next-line no-console
      console.error('chatThreadIndex.archive failed', error);
    });
  }, [getServerSyncUid, instanceId, currentUid]);

  /** Latest messages ref so switch/remove can snapshot synchronously. */
  const latestMessagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => { latestMessagesRef.current = messages; }, [messages]);

  /**
   * STALE-CLOBBER GUARD (cross-device sync, 2026-07-14): thread ids for which
   * this device has observed at least one authoritative server snapshot this
   * session (via the live message listener below). Until then the mirror
   * effect must NOT blind-replace the server transcript with a possibly-stale
   * localStorage rehydrate — the legacy array doc write is a whole-array
   * last-writer-wins replace, so a tab reopened with an hour-old local copy
   * used to immediately rewrite the cloud doc back to that hour-old state,
   * clobbering every newer turn made from other devices. Fails CLOSED on
   * listener ERROR (2026-09-01, supersedes the 2026-07-14 fail-open): an
   * errored listener has observed nothing, so the gate opens only via the
   * one-shot authoritative read in verifyTranscriptGateOnce. The two SYNC
   * unavailability paths (non-function subscribe / subscribe throw) stay
   * fail-open — no retry loop runs behind them, so fail-closed there would
   * deadlock sync-up in stubbed environments.
   */
  const serverSnapshotSeenThreadsRef = useRef<Set<string>>(new Set());
  // Wake coordinator + fail-closed gate plumbing (returning-tab reliability,
  // 2026-09-01). Per-thread stamps: last authoritative snapshot delivery and
  // last listener error — compared against the hidden-at stamp to decide
  // whether a wake must reattach. The epoch state is a forced full
  // teardown+reattach of every server-stream effect that lists it.
  const lastTranscriptSnapshotAtRef = useRef<Map<string, number>>(new Map());
  const transcriptListenerErrorAtRef = useRef<Map<string, number>>(new Map());
  const transcriptVerifyInFlightRef = useRef<Set<string>>(new Set());
  const lastHiddenAtRef = useRef<number | null>(null);
  const lastEpochBumpAtRef = useRef<number>(0);
  const [listenerEpoch, setListenerEpoch] = useState(0);

  // G3 identity-transition hygiene: per-identity SERVER-VIEW session state
  // must reset at every auth boundary (guest→member, member→guest, A→B).
  // These refs survive the component across sign-out/sign-in; without the
  // reset, a consumed first-snapshot marker from the previous identity
  // permanently disables BOOT-DEDUPE for the next one — a re-sign-in landed
  // on a stray bootstrap draft instead of the account's restored threads +
  // active pointer. pendingServerCreateIds is re-armed by the same commit's
  // hydrate (drafts) and adoption (guest content threads), both of which run
  // AFTER this effect. sessionBootstrapDraftIdsRef is deliberately KEPT: a
  // hydrate-minted draft crossing the boundary (the fresh guest draft present
  // at sign-in) must stay boot-dedupe-eligible.
  const prevServerViewUidRef = useRef<string | null>(currentUid);
  useEffect(() => {
    if (prevServerViewUidRef.current === currentUid) return;
    prevServerViewUidRef.current = currentUid;
    firstSnapshotConsumedRef.current = false;
    prevServerSnapshotIdsRef.current = null;
    serverSnapshotSeenThreadsRef.current.clear();
    // Wake-coordinator stamps are per-identity SERVER-VIEW state too: a
    // stale "fresh snapshot" stamp crossing the boundary would let a wake
    // skip the reattach the new identity's streams need.
    lastTranscriptSnapshotAtRef.current.clear();
    transcriptListenerErrorAtRef.current.clear();
    transcriptVerifyInFlightRef.current.clear();
    pendingServerCreateIdsRef.current.clear();
    sessionMintedThreadIdsRef.current.clear();
  }, [currentUid]);

  // ── SIGN-OUT FULL FLUSH (owner security report 2026-08-21) ──────────────
  //
  // After sign-out the chat kept rendering the departed member's THIRTEEN
  // threads — titles and cached transcripts — on the signed-out surface of a
  // shared browser. Storage was being purged (session.ts + the
  // app:session_reset listener), but nothing ever flushed the RENDERED
  // state: this component never unmounts (App.tsx P3), and the storage keys
  // flip to the guest namespace one commit before `threads` does, so the
  // unguarded persist effects below re-wrote the member's list into the
  // guest keys (and the IDB mirror resurrected it from there).
  //
  // This effect closes the state half: when a signed-in identity DEPARTS
  // (member → guest, or member A → member B), clear every piece of rendered
  // thread state in the same commit the uid flips. The empty `threads` array
  // is what routes the same-commit hydrate into its bootstrap branch, which
  // mints exactly ONE fresh empty thread — the signed-out surface the owner
  // specified. Deliberately NOT fired on guest → member sign-in (prev null):
  // that boundary is the guest-thread ADOPTION path and must keep its
  // content.
  const prevFlushUidRef = useRef<string | null | undefined>(undefined);
  // QUOTA HEADROOM SWEEP (2026-08-22, b815 incident): once per uid per mount,
  // when the origin's localStorage usage is over the soft limit, evict cold
  // per-thread caches (server store is the durable copy) down to the target.
  // This is the only protection the Firestore SDK's own raw setItem calls
  // have — when the bin is full, ITS write throws, asserts (b815), and the
  // whole surface crashes to the error boundary. The active thread's cache
  // is protected; everything the sweep removes re-hydrates from the server
  // on next visit. Deferred a tick so boot hydration reads run first.
  const storageSweepDoneForUidRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    if (storageSweepDoneForUidRef.current === currentUid) return;
    storageSweepDoneForUidRef.current = currentUid;
    const t = setTimeout(() => {
      try {
        const activeTid = activeThreadIdRef.current;
        sweepStorageAtBoot(activeTid ? [threadMsgKey(instanceId, activeTid, currentUid)] : []);
      } catch { /* sweep is best-effort — never block boot */ }
    }, 0);
    return () => clearTimeout(t);
  }, [enabled, instanceId, currentUid]);
  // A member→null transition awaiting confirmation (blip vs real sign-out).
  const pendingDepartedUidRef = useRef<string | null>(null);
  /** How long a deliberate resetSession stamp vouches for a uid→null flip. */
  const DELIBERATE_RESET_WINDOW_MS = 10_000;
  /** How long a bare uid→null flip must persist before it counts as sign-out. */
  const AUTH_BLIP_DEBOUNCE_MS = 2_500;
  useEffect(() => {
    const prev = prevFlushUidRef.current;
    prevFlushUidRef.current = currentUid;
    const runFlush = (departedUid: string) => {
      pendingDepartedUidRef.current = null;
      // Disk first (departing uid's namespace + the unscoped/guest namespace,
      // localStorage AND the IndexedDB mirrors), then rendered state.
      try {
        purgeThreadStorageForSessionReset(instanceId, departedUid);
        purgeThreadCache({ keepUid: latestAuthedUidRef.current });
      } catch { /* best-effort — state flush below must still run */ }
      setThreads([]);
      setActiveThreadId(null);
      activeThreadIdRef.current = null;
      dispatch({ type: 'SET_MESSAGES', payload: [] });
      archivedTombstonesRef.current = new Map();
      // Remote-archive announcement bookkeeping is per IDENTITY: the arriving
      // member's threads must be probed and announced on their own terms, and
      // a pending hold from the departing identity must not fire against them.
      remoteArchiveNoticeShownRef.current = new Set();
      pendingArchiveProbeIdsRef.current = new Set();
      if (archiveHoldTimerRef.current !== null) {
        window.clearTimeout(archiveHoldTimerRef.current);
        archiveHoldTimerRef.current = null;
        clearThreadArchiveNotice();
      }
      prevMirroredActiveTidRef.current = null;
      // Re-arm the one-shot IDB list recovery for the (now purged) guest key so
      // it cannot merge a pre-purge read back into the fresh state.
      idbListRecoveryDoneRef.current = storageKeys.THREAD_LIST;
    };
    // A member departed while a null-window was pending and a DIFFERENT
    // member arrived: that is a real A→(null)→B switch — flush A now.
    if (currentUid && pendingDepartedUidRef.current && pendingDepartedUidRef.current !== currentUid) {
      runFlush(pendingDepartedUidRef.current);
      return;
    }
    if (currentUid && pendingDepartedUidRef.current === currentUid) {
      // The uid came back: an auth-token blip, not a sign-out. Keep everything.
      pendingDepartedUidRef.current = null;
      return;
    }
    if (prev === undefined || prev === currentUid || !prev) return;
    if (currentUid) {
      // member A → member B in one commit: a real account switch — flush now.
      runFlush(prev);
      return;
    }
    // member → null. Deliberate sign-out (resetSession stamped within the
    // window) flushes immediately — the owner's 2026-08-21 security report
    // stands. A bare flip is treated as a token blip until it PERSISTS:
    // destroying a live member's threads on a refresh hiccup was the
    // 2026-08-21 "messages disappear" regression (fernando, incognito).
    if (Date.now() - deliberateResetAtRef.current <= DELIBERATE_RESET_WINDOW_MS) {
      runFlush(prev);
      return;
    }
    pendingDepartedUidRef.current = prev;
    const timer = window.setTimeout(() => {
      if (pendingDepartedUidRef.current !== prev) return;
      if (latestAuthedUidRef.current === null) {
        runFlush(prev);
      } else {
        pendingDepartedUidRef.current = null;
      }
    }, AUTH_BLIP_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid]);

  /**
   * G3 guest→member SERVER adoption (owner directive: guest threads "need to
   * be appended to the user's internal Prest0n account after sign in").
   *
   * `migrateLegacyThreadStorageForUid` moves guest transcripts into the uid's
   * LOCAL namespace only — nothing reached the users/{uid} server store, so
   * adopted threads were dropped by the first index snapshot (local-only,
   * not pending-create, not drafts) and never survived a sign-out/sign-in
   * round-trip. For every thread migrated THIS run that holds REAL user
   * content:
   *   • create its index doc (isCreate stamps archived:false — an
   *     update-shaped first write omits the field and the index query never
   *     sees the doc);
   *   • persist its transcript (legacy array doc + per-message fan-out) with
   *     ids/timestamps/sourceTags verbatim so dedupe layers stay stable;
   *   • seed pendingServerCreateIds (reconcile keeps the chip until the
   *     create echoes back, then the snapshot handler clears the id — normal
   *     sync from there) and serverSnapshotSeenThreads (this write IS the
   *     thread's authoritative server state; the mirror's stale-clobber gate
   *     may open immediately).
   * Greeting-only/empty drafts stay local (deferred-create semantics — never
   * pollute the account with empty shells). Guarded by a per-uid once-marker
   * (cleared at the sign-out purge) so a double hydrate or a second tab
   * cannot re-push an already-adopted transcript over newer server state.
   * The marker CARRIES STATE (see GuestAdoptionMarkerState): pending ids are
   * recorded before the upserts fire and re-fired by the next hydrate's
   * `resumePendingGuestAdoption`, so a failed/interrupted push converges
   * instead of stranding the transcripts in scoped storage.
   */
  /** Build the index record for an adoption push (list record or synthesized orphan). */
  const buildAdoptionRecord = useCallback((
    threadId: string,
    stored: ChatMessage[],
    list: ChatThread[],
  ): ChatThread => {
    const fromList = list.find((t) => t.id === threadId) || null;
    if (fromList) {
      // Counts stamped from the transcript (the guest mirror can lag one
      // commit); title/lastActiveAt/createdAt kept from the local record.
      // SETTLED-ONLY (2026-08-01): same discipline as every other producer —
      // an adopted transcript's in-flight placeholder never inflates the row.
      return { ...fromList, messageCount: settledMessageCount(stored), hasUserContent: true };
    }
    // Orphan transcript (no list record) — synthesize one so the
    // conversation still reaches the account.
    const lastMs = new Date(stored[stored.length - 1].timestamp as unknown as string).getTime();
    return {
      ...createEmptyThread({ title: deriveThreadTitle(stored) }),
      id: threadId,
      messageCount: settledMessageCount(stored),
      hasUserContent: true,
      lastActiveAt: Number.isFinite(lastMs) ? new Date(lastMs).toISOString() : new Date().toISOString(),
    };
  }, []);

  /**
   * Fire the id-keyed index + transcript upserts for a batch of adoption
   * entries, seeding pendingServerCreateIds (reconcile keeps the chip until
   * the create echoes back) and serverSnapshotSeenThreads (this write IS the
   * thread's authoritative server state) exactly like first-pass adoption.
   * Resolves with the per-thread outcome — `ok` only when BOTH upserts
   * fulfilled — so callers can persist pending/adopted marker state.
   */
  const fireAdoptionUpserts = useCallback((
    uid: string,
    entries: Array<{ threadId: string; record: ChatThread; stored: ChatMessage[] }>,
  ): Promise<Array<{ threadId: string; ok: boolean }>> => {
    return Promise.all(entries.map(({ threadId, record, stored }) => {
      pendingServerCreateIdsRef.current.add(threadId);
      serverSnapshotSeenThreadsRef.current.add(threadId);
      const index = upsertChatThread(uid, instanceId, record, { isCreate: true }).then(
        () => true,
        (error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadAdoption.index upsert failed', { threadId, error });
          return false;
        },
      );
      const transcript = upsertThreadMessages(uid, instanceId, threadId, stored).then(
        () => true,
        (error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadAdoption.messages upsert failed', { threadId, error });
          return false;
        },
      );
      return Promise.all([index, transcript]).then(([a, b]) => ({ threadId, ok: a && b }));
    }));
  }, [instanceId]);

  const adoptGuestThreadsToServer = useCallback((migration: LegacyThreadMigrationResult): void => {
    if (migration.migratedThreadIds.length === 0) return;
    const uid = getServerSyncUid();
    if (!uid) return;
    // PRE-G3 RESIDUE GUARD (review finding 1, 2026-07-15): the server push is
    // gated on the guest-era stamp, written at a guest session's first
    // unscoped persist (threadPersist.stampGuestEraForUnscopedWrite). Pre-G3
    // builds could leak a member's pane into the unscoped store at sign-out
    // WITHOUT the legacy-owner marker (that marker is only written when a
    // migration run finds legacy data), so "no marker + no scoped keys" — the
    // cross-account guard's pass condition — is not proof of guest ownership.
    // Un-stamped content still migrates locally exactly as before, but is
    // never durably attributed to the signing-in uid's account.
    let hasGuestEraStamp = false;
    try {
      hasGuestEraStamp = localStorage.getItem(getGuestEraMarkerKey(instanceId)) !== null;
    } catch { /* unreadable → treat as unstamped (never push blind) */ }
    if (!hasGuestEraStamp) {
      // eslint-disable-next-line no-console
      console.warn(
        'chatThreadAdoption.skipped: unscoped store lacks the guest-era stamp (pre-G3 residue) — migrated locally, no server push',
        { migratedThreadIds: migration.migratedThreadIds },
      );
      return;
    }
    const markerKey = getGuestAdoptionMarkerKey(instanceId, uid);
    try {
      if (localStorage.getItem(markerKey) !== null) return;
    } catch {
      // storage unreadable → adopt anyway; upserts are id-keyed and converge
    }

    const list = getFromLocalStorage<ChatThread[]>(storageKeys.THREAD_LIST, []);
    const entries: Array<{ threadId: string; record: ChatThread; stored: ChatMessage[] }> = [];
    for (const threadId of migration.migratedThreadIds) {
      const stored = getFromLocalStorage<ChatMessage[]>(threadMsgKey(instanceId, threadId, uid), []);
      if (!threadHasUserContent(stored)) continue; // empty/greeting-only → stays a local draft
      entries.push({ threadId, record: buildAdoptionRecord(threadId, stored, list), stored });
    }
    const adoptedIds = new Set(entries.map((e) => e.threadId));

    // Once-marker, phase 1 (BEFORE the upserts, preserving the double-hydrate
    // / second-tab once-guard): every attempted id is recorded PENDING, so a
    // tab killed inside the write debounce window leaves a durable to-do for
    // the next hydrate. Phase 2 (after all upserts settle) moves fulfilled
    // ids to ADOPTED; failures stay pending for resumePendingGuestAdoption.
    writeGuestAdoptionMarker(markerKey, { adopted: [], pending: entries.map((e) => e.threadId) });
    void fireAdoptionUpserts(uid, entries).then((results) => {
      writeGuestAdoptionMarker(markerKey, {
        adopted: results.filter((r) => r.ok).map((r) => r.threadId),
        pending: results.filter((r) => !r.ok).map((r) => r.threadId),
      });
    });

    // Seamless pick-up: the guest's active CONVERSATION becomes the account's
    // active pointer (users/{uid}/chat_threads_meta/active). Gated on an
    // ADOPTED id — pointing the account at an empty local draft would clobber
    // the remembered pointer on every pre-auth-bootstrap page load.
    if (migration.migratedActiveThreadId && adoptedIds.has(migration.migratedActiveThreadId)) {
      writeActiveThreadToServer(migration.migratedActiveThreadId);
    }
  }, [
    getServerSyncUid,
    instanceId,
    storageKeys.THREAD_LIST,
    writeActiveThreadToServer,
    buildAdoptionRecord,
    fireAdoptionUpserts,
  ]);

  /**
   * FINDING-2 recovery (2026-07-15): finish any adoption a previous load left
   * incomplete. Ids recorded PENDING in the once-marker (offline sign-in, tab
   * closed inside the write debounce, index-or-transcript upsert rejected)
   * are re-armed and re-fired from the uid-scoped store. Runs at hydrate
   * BEFORE `adoptGuestThreadsToServer`, so a phase-1 marker written later in
   * the same hydrate is never double-fired; on the recovery reload the
   * migration finds no legacy data and adoption no-ops, making this the sole
   * push path. Re-seeding pendingServerCreateIds is what keeps the adopted
   * chips alive through the first index snapshot (reconcile's pending-create
   * exemption) until the re-fired create echoes back and the snapshot handler
   * clears the id — the exact mechanics first-pass adoption uses.
   */
  const resumePendingGuestAdoption = useCallback((): void => {
    const uid = getServerSyncUid();
    if (!uid) return;
    const markerKey = getGuestAdoptionMarkerKey(instanceId, uid);
    const marker = readGuestAdoptionMarker(markerKey);
    if (!marker || marker.pending.length === 0) return;

    const list = getFromLocalStorage<ChatThread[]>(storageKeys.THREAD_LIST, []);
    const entries: Array<{ threadId: string; record: ChatThread; stored: ChatMessage[] }> = [];
    const dropped: string[] = [];
    for (const threadId of marker.pending) {
      const stored = getFromLocalStorage<ChatMessage[]>(threadMsgKey(instanceId, threadId, uid), []);
      if (!threadHasUserContent(stored)) {
        // The scoped transcript vanished (eviction/corruption) — nothing left
        // to recover; drop the id so the marker converges instead of retrying
        // forever against an empty store.
        dropped.push(threadId);
        continue;
      }
      entries.push({ threadId, record: buildAdoptionRecord(threadId, stored, list), stored });
    }
    if (dropped.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('chatThreadAdoption.resume dropped unrecoverable pending ids', { dropped });
    }
    if (entries.length === 0) {
      writeGuestAdoptionMarker(markerKey, { adopted: marker.adopted, pending: [] });
      return;
    }
    void fireAdoptionUpserts(uid, entries).then((results) => {
      const okIds = results.filter((r) => r.ok).map((r) => r.threadId);
      const failedIds = results.filter((r) => !r.ok).map((r) => r.threadId);
      writeGuestAdoptionMarker(markerKey, {
        adopted: Array.from(new Set([...marker.adopted, ...okIds])),
        pending: failedIds,
      });
    });
  }, [getServerSyncUid, instanceId, storageKeys.THREAD_LIST, buildAdoptionRecord, fireAdoptionUpserts]);

  const reconcileInterruptedServerBackedMessages = useCallback(async (
    threadId: string,
    originalMessages: ChatMessage[],
    preparedMessages: ChatMessage[],
  ): Promise<void> => {
    const candidates = collectServerBackedInterruptedCandidates(originalMessages);
    if (candidates.length === 0) return;

    // The server-owned task may still be RUNNING when the page (re)hydrates —
    // a multi-PDF turn takes ~40-120s. On the first pass the completed answer
    // often isn't persisted yet, or Firebase Auth hasn't finished initializing 
    // on page load (so uid is null initially). Poll the server a bounded number 
    // of times so the finished answer is pulled in and rendered without a reload.
    const RECONCILE_POLL_DELAYS_MS = [0, 2000, 3000, 5000, 10000, 15000, 20000, 30000, 30000, 30000, 30000];
    for (let attempt = 0; attempt < RECONCILE_POLL_DELAYS_MS.length; attempt += 1) {
      const delay = RECONCILE_POLL_DELAYS_MS[attempt];
      if (delay > 0) {
        // Stop polling if the user navigated away from this thread.
        if (activeThreadIdRef.current !== threadId) return;
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (activeThreadIdRef.current !== threadId) return;
      }

      const uid = getServerSyncUid();
      if (!uid) continue; // Keep polling until Auth initializes

      // I4: NEWEST-WINDOW read, deliberately. The candidates are recent
      // in-flight turns (they were streaming at the hydrate boundary), so
      // their server copies land in the newest page by construction — polling
      // the full uncapped history here billed O(history) reads per attempt
      // (×11 attempts) for answers that are always at the tail.
      const serverPage = await getThreadMessagesPage(uid, threadId).catch(() => null);
      const serverMessages = serverPage ? serverPage.messages : null;
      if (!serverMessages || serverMessages.length === 0) continue;

      const normalizedServerMessages = serverMessages.map(normalizeTimestamp);

      // FROZEN-SNAPSHOT FIX (Symptom 2, report-vanish family): never replay
      // into the call-time snapshot. A poll can succeed minutes after this
      // function was invoked; any turn the user sent — or a live finalize
      // that landed — in that window is absent from `preparedMessages`, and
      // dispatching the frozen list would wipe it. Resolve the freshest
      // known list for this thread at RESOLUTION time instead:
      //   active thread  → the live pane (messagesSyncRef is updated
      //                    synchronously on dispatch, so this is fresh even
      //                    on the attempt-0 same-tick path);
      //   inactive thread → the current per-thread store (which routed
      //                    background writes may have advanced).
      // Candidates keep coming from `originalMessages` (the pre-conversion
      // shapes carry the pending sourceTags) and are located in the
      // into-list by message id, so list drift cannot misplace a swap.
      const isActiveHere = activeThreadIdRef.current === threadId;
      let freshest: ChatMessage[];
      if (isActiveHere) {
        const syncLatest = getLatestMessagesRef.current;
        freshest = (syncLatest ? syncLatest() : latestMessagesRef.current) || [];
      } else {
        freshest = getFromLocalStorage<ChatMessage[]>(
          threadMsgKey(instanceId, threadId, currentUid),
          [],
        );
      }
      const replayInto = freshest.length > 0 ? freshest : preparedMessages;

      const reconciled = replayPersistedServerAnswersForInterruptedMessages(
        replayInto,
        originalMessages,
        normalizedServerMessages,
      );
      if (!reconciled.changed) {
        // Distinguish "answer not on the server yet" (keep polling) from
        // "nothing left to reconcile" (every candidate bubble finalized
        // live or was removed during the poll window — stop).
        const stillAwaitingServer = candidates.some((candidate) => {
          if (!candidate.messageId) return true; // can't verify by id — keep polling
          const target = replayInto.find((m) => m && m.id === candidate.messageId);
          if (!target || target.isUser) return false;
          return !!target.interrupted
            || target.status === 'interrupted'
            || (typeof target.serverTaskId === 'string'
              && !!target.serverTaskId
              && (!target.status || target.status === 'streaming'));
        });
        if (!stillAwaitingServer) return;
        continue;
      }

      safelyPersistThreadMessages({
        instanceId,
        threadId,
        uid: currentUid,
        messages: reconciled.messages,
        source: 'replay-reconcile',
        writerToken: threadSyncWriterTokenRef.current,
      });
      if (activeThreadIdRef.current !== threadId) return;
      dispatch({ type: 'SET_MESSAGES', payload: reconciled.messages });
      return;
    }
  }, [getServerSyncUid, instanceId, currentUid, dispatch, activeThreadIdRef]);

  // ── I4 ("Load earlier messages", 2026-07-15): per-thread paging state ─────
  //
  // POLICY (page size / local cache / affordance):
  //  • Server reads are PAGED (the newest per-doc window merged with the
  //    capped legacy array doc) — pagination bounds SERVER reads and the
  //    first-load cost of threads this device has no local copy of
  //    (cross-device / fresh-sign-in restore).
  //  • The THREAD-OPEN window is MAX_THREAD_MESSAGES (100) per-doc messages,
  //    not the 60-message walking page (P0 thread-durability, 2026-09-02):
  //    the legacy array doc holds the newest 100 and its card-html backstop
  //    blanks the OLDEST of them first, while a 60-message per-doc window
  //    left legacy positions 61–100 without their intact per-doc partner —
  //    so a stripped legacy-only copy was the only copy the merge could
  //    show. Paging the per-doc store to the legacy window makes every
  //    legacy-window message meet its intact partner in the merge
  //    (chatThreadMessagesRepository.THREAD_OPEN_WINDOW_SIZE). Older pages
  //    keep THREAD_MESSAGES_PAGE_SIZE (60), as does the optimistic-affordance
  //    threshold below (a 60-message local copy is still "window-sized").
  //  • The lazy cursor re-establishment (loadEarlierMessages) ALSO merges the
  //    fresh newest window it reads, so a local copy persisted by an older,
  //    smaller window can never leave a gap between itself and the older
  //    page the cursor points past — contiguity by construction.
  //  • The LOCAL store stays FULL for threads the device already has: hydrate
  //    and switchThread keep rendering everything in localStorage, exactly as
  //    before. Paging never trims a local transcript.
  //  • `olderAvailable` (the affordance signal) is set ONLY by a paged server
  //    read that returned a full page + cursor. Cursors are opaque Firestore
  //    snapshots — ref-held (non-serializable), keyed by uid+thread so an
  //    identity transition can never walk another account's cursor.
  const earlierPagingRef = useRef<Map<string, {
    cursor: ThreadMessageDocsPageCursor | null;
    olderAvailable: boolean;
  }>>(new Map());
  const loadingEarlierKeysRef = useRef<Set<string>>(new Set());
  const [earlierAvailableByKey, setEarlierAvailableByKey] = useState<Record<string, boolean>>({});
  const [loadingEarlierByKey, setLoadingEarlierByKey] = useState<Record<string, boolean>>({});

  const earlierPagingKey = useCallback(
    (threadId: string): string => `${currentUid || 'anon'}::${threadId}`,
    [currentUid],
  );

  const setEarlierPaging = useCallback((
    threadId: string,
    entry: { cursor: ThreadMessageDocsPageCursor | null; olderAvailable: boolean } | null,
  ): void => {
    const key = earlierPagingKey(threadId);
    if (entry) earlierPagingRef.current.set(key, entry);
    else earlierPagingRef.current.delete(key);
    const next = !!entry?.olderAvailable;
    setEarlierAvailableByKey((prev) => {
      if ((prev[key] || false) === next) return prev;
      return { ...prev, [key]: next };
    });
  }, [earlierPagingKey]);

  /**
   * FAIL-CLOSED gate verify (2026-09-01, supersedes the 2026-07-14 fail-open
   * markSeen-on-error): the transcript listener's error path must not open
   * the stale-clobber gate — an errored listener has OBSERVED nothing, and
   * opening the gate authorized the mirror's whole-array LWW upsert to
   * overwrite newer turns from other devices with a stale local rehydrate.
   * Sync-up durability (the property the old fail-open protected) rides this
   * one-shot authoritative READ instead: a healthy read is the same
   * authoritative observation a first snapshot provides, so IT may open the
   * gate. Server content merges in with the snapshot consumer's reduced
   * discipline — window-bounded merge + finalized-preserving guard, dispatch
   * only on change; no gate-flush/H1-release (listener-path-only concerns).
   * An empty/nonexistent server doc opens the gate immediately (nothing to
   * clobber; the next mirror pass syncs local content up). A failed read
   * leaves the gate CLOSED: local persistence still holds the transcript,
   * the repository's capped-backoff resubscribe keeps trying, and the wake
   * coordinator re-pings on the next return.
   */
  const verifyTranscriptGateOnce = useCallback(async (uid: string, threadId: string): Promise<void> => {
    if (transcriptVerifyInFlightRef.current.has(threadId)) return; // single-flight
    transcriptVerifyInFlightRef.current.add(threadId);
    try {
      const server = await getThreadMessages(uid, threadId); // healthy read = authoritative observation
      if (!server || server.length === 0) {
        serverSnapshotSeenThreadsRef.current.add(threadId);
        return;
      }
      if (activeThreadIdRef.current !== threadId) {
        // Server HAS content but the pane moved on: leave the gate closed —
        // re-activation attaches a fresh listener whose first snapshot
        // merges the server state in and opens the gate properly.
        return;
      }
      const localMessages = ((getLatestMessagesRef.current
        ? getLatestMessagesRef.current()
        : latestMessagesRef.current) || []) as ChatMessage[];
      const normalizedRemote = server.map(normalizeTimestamp);
      const prepared = prepareRehydratedMessages(normalizedRemote, { reconcileInterrupted: false });
      const pagingEntry = earlierPagingRef.current.get(earlierPagingKey(threadId));
      const windowBoundaryTs = pagingEntry?.olderAvailable
        ? oldestLoadedMessageTs(localMessages)
        : undefined;
      const merged = mergeRemoteThreadMessagesIntoLocal(
        localMessages,
        prepared,
        typeof windowBoundaryTs === 'number' ? { dropForeignOlderThanMs: windowBoundaryTs } : {},
      );
      if (merged.changed) {
        const guarded = mergeSyncedMessagesPreservingFinalized(merged.messages, localMessages);
        dispatch({ type: 'SET_MESSAGES', payload: guarded });
      }
      // Server state is now reflected locally — the gate may open, and the
      // mirror's next pass flushes any held-back local sends.
      serverSnapshotSeenThreadsRef.current.add(threadId);
    } catch {
      // Gate stays CLOSED — never opened on an unverified server state.
    } finally {
      transcriptVerifyInFlightRef.current.delete(threadId);
    }
  }, [dispatch, earlierPagingKey, activeThreadIdRef]);

  const hydrateThreadMessagesFromServerIfEmpty = useCallback(async (threadId: string): Promise<void> => {
    const key = threadMsgKey(instanceId, threadId, currentUid);
    const local = getFromLocalStorage<ChatMessage[]>(key, []);
    if (local.length > 0) {
      // TRUNCATION RECOVERY (owner report 2026-08-19 "messages start
      // disappearing"): a paged first read persisted only the newest window,
      // and the opaque cursor died with that session — so on every later
      // visit the thread rendered its last page with NO "load earlier"
      // affordance, reading as permanent message loss. When the local copy
      // is exactly window-sized (or larger) and this session has no paging
      // entry yet, offer the affordance optimistically with a null cursor;
      // loadEarlierMessages re-establishes the real cursor lazily on the
      // first click and clears the affordance if nothing older exists.
      const pagingKey = earlierPagingKey(threadId);
      if (!earlierPagingRef.current.has(pagingKey) && local.length >= THREAD_MESSAGES_PAGE_SIZE) {
        setEarlierPaging(threadId, { cursor: null, olderAvailable: true });
      }
      return;
    }

    const uid = getServerSyncUid();
    if (!uid) return;

    // QUOTA-DURABLE PROBE (2026-08-19): an LS-empty thread may still have its
    // full transcript in the IDB mirror (quota fallback). Prefer that copy —
    // and repair LS — instead of shadowing it with the newest 60-message
    // server window.
    const idbCopy = await rehydrateThreadMessages<ChatMessage>({ instanceId, threadId, uid: currentUid })
      .catch(() => undefined);
    // POST-AWAIT USER-CONTENT GUARD (2026-08-20 owner repro: first message
    // typed into a locally-empty draft vanished from view). This hydrate is
    // launched on landing in an empty draft; a user who types during the
    // fetch window has ALREADY written the bubble to THIS thread's storage
    // key (addMessage's sync persist) — an unguarded replace here swallows
    // it. The thread's own store is the ONLY signal consulted: the live
    // pane can legitimately still hold the PREVIOUS thread's messages while
    // this hydrate races the pane swap, so a pane check misfires. (A
    // greeting-only store is still replaceable — the legitimate case.)
    const fetchLostToUserContent = (): boolean =>
      threadHasUserContent(getFromLocalStorage<ChatMessage[]>(key, []));
    if (idbCopy && idbCopy.length > 0) {
      if (fetchLostToUserContent()) return;
      setToLocalStorage(key, idbCopy);
      if (idbCopy.length >= THREAD_MESSAGES_PAGE_SIZE) {
        setEarlierPaging(threadId, { cursor: null, olderAvailable: true });
      }
      if (activeThreadIdRef.current === threadId) {
        const normalizedIdb = idbCopy.map(normalizeTimestamp);
        dispatch({ type: 'SET_MESSAGES', payload: prepareRehydratedMessages(normalizedIdb) });
      }
      return;
    }

    // I4: PAGED first read — newest window only. A long thread restored on a
    // fresh device no longer downloads its entire history; the "Load earlier
    // messages" affordance walks older pages on demand. Pre-migration threads
    // (per-doc store empty) come back as mode:'legacy-full' — the whole legacy
    // array, unchanged behavior, nothing older to page.
    const page = await getThreadMessagesPage(uid, threadId);
    if (!page || page.messages.length === 0) return;
    if (fetchLostToUserContent()) return;
    const serverMessages = page.messages;
    setEarlierPaging(threadId, page.mode === 'paged'
      ? { cursor: page.cursor, olderAvailable: page.olderAvailable }
      : null);

    setToLocalStorage(key, serverMessages);

    if (activeThreadIdRef.current !== threadId) return;

    const normalizedServerMessages = serverMessages.map(normalizeTimestamp);
    const rehydrated = prepareRehydratedMessages(normalizedServerMessages);
    dispatch({ type: 'SET_MESSAGES', payload: rehydrated });
  }, [instanceId, currentUid, getServerSyncUid, dispatch, activeThreadIdRef, setEarlierPaging]);

  /**
   * Server-hydrate status for the ACTIVE thread ('error' is retry-able via
   * retryActiveThreadHydrate). Only meaningful while the pane is empty — a
   * local-full thread resolves to 'idle' immediately. Wraps every hydrate
   * call site so a failed server read surfaces as retry-able UI state
   * instead of a swallowed console.error (returning-tab reliability,
   * 2026-09-01).
   */
  const [activeHydrateStatus, setActiveHydrateStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const hydrateStatusThreadRef = useRef<string | null>(null);
  const runActiveThreadHydrate = useCallback(async (threadId: string): Promise<void> => {
    hydrateStatusThreadRef.current = threadId;
    setActiveHydrateStatus('loading');
    try {
      await hydrateThreadMessagesFromServerIfEmpty(threadId);
      if (hydrateStatusThreadRef.current === threadId) setActiveHydrateStatus('idle');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('chatThreadMessages.hydrate failed', { threadId, error });
      // Stamp 'error' only while the failed thread is still what the pane
      // shows — a later switch's hydrate owns the status by then.
      if (hydrateStatusThreadRef.current === threadId && activeThreadIdRef.current === threadId) {
        setActiveHydrateStatus('error');
      }
    }
  }, [hydrateThreadMessagesFromServerIfEmpty, activeThreadIdRef]);
  const retryActiveThreadHydrate = useCallback(() => {
    const tid = activeThreadIdRef.current;
    if (tid) void runActiveThreadHydrate(tid);
  }, [runActiveThreadHydrate, activeThreadIdRef]);
  // A thread switch orphans any status stamped for the PREVIOUS thread: a
  // freshly minted thread that never runs a hydrate must not inherit its
  // predecessor's 'error' (that painted a false retry row on an empty pane).
  useEffect(() => {
    if (activeThreadId && hydrateStatusThreadRef.current !== activeThreadId) {
      setActiveHydrateStatus('idle');
    }
  }, [activeThreadId]);

  /**
   * I4: fetch + prepend the next OLDER page for the active thread.
   *
   * Merge is `mergeLegacyAndPerDoc(current, olderPage)` — the same
   * anchor-keyed, never-downgrade union the dual-read uses — so a server
   * `prest0n_task_<id>` copy arriving in an older page CONVERGES with a local
   * bubble already rendered (Round-K invariant across the page boundary)
   * instead of duplicating, and ordering stays chronological.
   *
   * Frozen-snapshot lesson (Symptom 2 family) applied: the merge base is
   * resolved at RESOLUTION time — the live pane when the thread is still
   * active, its per-thread store when the user switched away mid-fetch (the
   * older page still lands in storage; the pane is left alone).
   */
  const loadEarlierMessages = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const threadId = activeThreadIdRef.current;
    if (!threadId) return;
    const uid = getServerSyncUid();
    if (!uid) return;
    const key = earlierPagingKey(threadId);
    const entry = earlierPagingRef.current.get(key);
    if (!entry || !entry.olderAvailable) return;
    if (loadingEarlierKeysRef.current.has(key)) return; // single-flight
    loadingEarlierKeysRef.current.add(key);
    setLoadingEarlierByKey((prev) => ({ ...prev, [key]: true }));
    try {
      // CONTIGUITY BELT (P0 thread-durability, 2026-09-02): the fresh newest
      // window read to re-establish the cursor is merged too — GAP-FILL ONLY
      // (rows the pane does not already hold). The cursor points past THAT
      // window; a local copy persisted by an older, smaller window would
      // otherwise sit with a gap between its oldest message and the older
      // page. Rows already local are left to the local copy, so a server
      // twin can never replace a pane row on a mere tie.
      let freshWindow: ChatMessage[] = [];
      // LAZY CURSOR (truncation recovery, 2026-08-19): an optimistic
      // affordance carries no cursor (it survived a reload the ref did not).
      // Re-establish it with a fresh newest-page read.
      //
      // NEVER DEAD-END THE CLICK (review 2026-09-02): "nothing older exists"
      // retires the affordance but must NOT discard the window just read —
      // a thread that fits INSIDE the open window (61–99 messages) and a
      // pre-migration legacy-full thread both answer with no cursor while
      // holding exactly the rows a truncated local copy is missing. Dropping
      // them made the click the D6 complaint itself: the control vanished and
      // no message appeared. The gap-fill merge below runs either way.
      let cursor = entry.cursor;
      if (!cursor) {
        const freshPage = await getThreadMessagesPage(uid, threadId).catch(() => null);
        freshWindow = Array.isArray(freshPage?.messages) ? (freshPage as { messages: ChatMessage[] }).messages : [];
        if (!freshPage || freshPage.mode !== 'paged' || !freshPage.olderAvailable || !freshPage.cursor) {
          setEarlierPaging(threadId, null);
          cursor = null;
        } else {
          cursor = freshPage.cursor;
          setEarlierPaging(threadId, { cursor, olderAvailable: true });
        }
      }
      const older = cursor
        ? await getOlderThreadMessages(uid, threadId, cursor)
        : null;
      if (older) setEarlierPaging(threadId, { cursor: older.cursor, olderAvailable: older.olderAvailable });
      const olderMessages = older ? older.messages : [];
      if (olderMessages.length === 0 && freshWindow.length === 0) return;
      // I4 review A2: an older page can carry the server's anchored copy of a
      // task whose stream is STILL LIVE in this tab (long turn created many
      // messages back, finalized server-side mid-stream). Merging it would
      // replace the streaming bubble under the anchor id and orphan the live
      // stream's by-id finalize — drop live-task copies, exactly like the
      // remote-listener path does.
      const notLiveTaskCopy = (m: ChatMessage): boolean => {
        const serverTaskId =
          typeof m.serverTaskId === 'string' ? m.serverTaskId.trim() : '';
        const anchor =
          typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
        const taskId = serverTaskId
          || (anchor.startsWith('prest0n_task_')
            ? anchor.slice('prest0n_task_'.length)
            : '');
        return !(taskId && isLiveSendInFlight(taskId));
      };
      const olderPageMessages = olderMessages.filter(notLiveTaskCopy);
      const freshWindowMessages = freshWindow.filter(notLiveTaskCopy);
      if (olderPageMessages.length === 0 && freshWindowMessages.length === 0) return;
      const normalizedOlder = olderPageMessages.map(normalizeTimestamp);

      const isActiveHere = activeThreadIdRef.current === threadId;
      const storageKey = threadMsgKey(instanceId, threadId, currentUid);
      const current: ChatMessage[] = isActiveHere
        ? (((getLatestMessagesRef.current ? getLatestMessagesRef.current() : latestMessagesRef.current)) || [])
        : getFromLocalStorage<ChatMessage[]>(storageKey, []);

      // Gap-fill from the fresh window: only rows the pane does not hold.
      const currentKeys = new Set(current.map((m) => perDocMessageId(m)));
      const gapFill = freshWindowMessages
        .filter((m) => !currentKeys.has(perDocMessageId(m)))
        .map(normalizeTimestamp);
      if (normalizedOlder.length === 0 && gapFill.length === 0) return;

      const merged = mergeLegacyAndPerDoc(current, [...gapFill, ...normalizedOlder]);
      setToLocalStorage(storageKey, merged);
      if (activeThreadIdRef.current !== threadId) return;
      // reconcileInterrupted:false — prepending history must never re-classify
      // a LIVE in-flight turn in the pane as interrupted (same opt-out the
      // live-listener merge path uses).
      const prepared = prepareRehydratedMessages(merged, { reconcileInterrupted: false });
      dispatch({ type: 'SET_MESSAGES', payload: prepared });
    } finally {
      loadingEarlierKeysRef.current.delete(key);
      setLoadingEarlierByKey((prev) => {
        if (!prev[key]) return prev;
        return { ...prev, [key]: false };
      });
    }
  }, [
    enabled,
    instanceId,
    currentUid,
    getServerSyncUid,
    dispatch,
    activeThreadIdRef,
    earlierPagingKey,
    setEarlierPaging,
  ]);

  /**
   * GREET-ON-FOCUS (owner report 2026-07-13: "no introductory custom message"
   * after signing into an account and opening a new thread).
   *
   * Mint-time seeding (newThread / resetAllThreads) misses every OTHER path
   * that lands the user in an empty thread: the SINGLE-EMPTY reuse branch
   * (focus, no mint), switching to an empty draft, and boot/uid-change
   * activation (the once-per-mount welcome injector is already spent when a
   * user signs into a different account mid-session). This effect converges
   * them all: whenever the ACTIVE thread is provably empty everywhere, seed
   * the same persisted greeting a minted thread gets.
   *
   * Server-emptiness proof: the local record CANNOT be trusted here —
   * reconcileThreadCounts() recomputes messageCount from the LOCAL per-thread
   * store, so a server-populated thread whose transcript never hydrated on
   * this device carries a locally-zeroed count. Seeding it would block
   * hydrateThreadMessagesFromServerIfEmpty (local.length > 0 → return) and
   * hide real history. Only threads this session knows are local-only drafts
   * (pendingServerCreateIds) skip the check; everything else is confirmed
   * empty via the hasAnyThreadMessages limit(1) existence probe (I4 — a
   * paged/full transcript read must never stand in for a boolean), with all
   * guards re-checked after the await. Verdicts are cached per uid+thread so
   * the effect's frequent re-runs (every threads-mirror bump) cost one cheap
   * probe per thread.
   *
   * Self-terminating: a successful seed bumps the record's messageCount
   * mirror to 1 and fills the store, so every subsequent run exits at the
   * store guard — no write loop (echo-loop incident 2026-07-12 lesson).
   */
  const greetServerCheckRef = useRef<Map<string, 'empty' | 'has-content'>>(new Map());
  const seedGreetingIntoEmptyActiveThread = useCallback(async (threadId: string): Promise<void> => {
    if (!enabled || !threadId) return;
    const record = latestThreadsRef.current.find((t) => t.id === threadId);
    if (!record || threadRecordHasUserContent(record)) return;
    const key = threadMsgKey(instanceId, threadId, currentUid);
    const stored = getFromLocalStorage<ChatMessage[]>(key, []);
    if (stored.length > 0) {
      // STALE-GREETING RECONCILIATION (2026-08-04 owner repro: a fresh trial
      // finished onboarding — personalized welcome + starters live in config —
      // yet the thread still showed the GENERIC bubble). The greeting is
      // seeded ONCE while the account's runtime config may not exist yet
      // (redemption seeds it seconds after signup; onboarding refines it
      // minutes later), and every later pass exited right here at the store
      // guard, freezing the stale copy forever. When the transcript holds
      // ONLY seeded welcome bubbles (nothing user-authored — replacement is
      // lossless by construction) and the composer's CURRENT output differs,
      // swap the persisted greeting for the current one. Converges in one
      // pass: after the swap the texts match and the effect exits at the
      // already-current check. Threads with any real content keep the
      // original exit.
      const greetingOnly = stored.every(
        (m) => !!m && m.isUser !== true && (m.sourceTag === 'welcome' || m.id === 'welcome'),
      );
      if (!greetingOnly) return;
      const freshSeed = composeGreetingSeed();
      if (freshSeed.length === 0) return; // config still loading — retry on composer change
      // RANKED, UPGRADE-ONLY (2026-08-09 multi-tab greeting parity): the old
      // swap trusted whatever THIS tab composed, so two tabs settled on
      // different configs replaced each other's copy symmetrically — the same
      // thread greeted differently per window, forever. Now the persisted
      // greeting is monotonic: replace only when this tab's copy is STRICTLY
      // better-ranked (see greetingRank.ts); ties and downgrades keep the
      // stored copy (which the pane renders verbatim in every tab).
      const storedRank = storedGreetingRank(stored[0]);
      const freshRank = storedGreetingRank(freshSeed[0]);
      // Same-rank replacement is allowed ONLY in convergent bands (2026-08-09
      // hotfix): copy that mirrors one shared truth (in-code deterministic
      // copy; the account's live-subscribed runtime doc) refines over time at
      // the SAME rank — onboarding's placeholder→refined welcome, a UI
      // language switch, refreshed memory suggestions. Freezing those ties
      // stranded the placeholder forever. The ambiguous tenant-vs-firm bands
      // (3/5) stay frozen — that symmetric swap war IS the original bug.
      const convergentTie = freshRank === storedRank && isConvergentGreetingRank(freshRank);
      if (stored.length === 1 && stored[0].text === freshSeed[0].text) {
        // Same copy — a higher-ranked recompose back-fills rank (and starters)
        // onto a pre-ranking stored greeting; a convergent same-rank recompose
        // back-fills CHANGED starters (onboarding can refine the chips while
        // keeping the welcome text).
        const startersChanged =
          JSON.stringify(freshSeed[0].starters ?? []) !== JSON.stringify(stored[0].starters ?? []);
        if (freshRank > storedRank || (convergentTie && startersChanged)) {
          safelyPersistThreadMessages({
            instanceId,
            threadId,
            uid: currentUid,
            messages: freshSeed,
            source: 'greet-on-focus',
            writerToken: threadSyncWriterTokenRef.current,
          });
          if (activeThreadIdRef.current === threadId) {
            dispatch({ type: 'SET_MESSAGES', payload: freshSeed });
          }
        }
        return;
      }
      // Never downgrade; a tie swaps only when convergent (see above) or when
      // it collapses a DUPLICATED greeting-only store (>1 welcome bubbles —
      // convergent by construction: once collapsed, ties no-op).
      if (freshRank < storedRank) return;
      if (freshRank === storedRank && stored.length === 1 && !convergentTie) return;
      safelyPersistThreadMessages({
        instanceId,
        threadId,
        uid: currentUid,
        messages: freshSeed,
        source: 'greet-on-focus',
        writerToken: threadSyncWriterTokenRef.current,
      });
      if (activeThreadIdRef.current === threadId) {
        dispatch({ type: 'SET_MESSAGES', payload: freshSeed });
      }
      return;
    }
    const live = getLatestMessagesRef.current ? getLatestMessagesRef.current() : null;
    if (Array.isArray(live) && live.length > 0) return;
    // Composer absent or yielding nothing (e.g. tenant config still loading):
    // return WITHOUT caching anything — the effect re-fires when the composer
    // identity changes and tries again.
    const seed = composeGreetingSeed();
    if (seed.length === 0) return;
    if (!pendingServerCreateIdsRef.current.has(threadId)) {
      const cacheKey = `${currentUid || 'anon'}::${threadId}`;
      let verdict = greetServerCheckRef.current.get(cacheKey);
      if (verdict === undefined) {
        const uid = getServerSyncUid();
        if (uid) {
          try {
            // I4: cheap limit(1) existence probe — NOT a transcript read. A
            // paged read must never stand in for emptiness here: an empty
            // newest page only proves the window is empty, and a full read
            // bills O(history) for a boolean.
            const serverHasContent = await hasAnyThreadMessages(uid, threadId);
            verdict = serverHasContent ? 'has-content' : 'empty';
          } catch {
            return; // unknown server state — never seed blind
          }
          greetServerCheckRef.current.set(cacheKey, verdict);
        } else {
          verdict = 'empty'; // no server sync — local store is the only truth
        }
      }
      if (verdict === 'has-content') return; // hydration owns this thread
      // Re-check every guard after the await — the user may have switched
      // away, typed, or a sync may have landed while the read was in flight.
      if (activeThreadIdRef.current !== threadId) return;
      if (getFromLocalStorage<ChatMessage[]>(key, []).length > 0) return;
      const liveNow = getLatestMessagesRef.current ? getLatestMessagesRef.current() : null;
      if (Array.isArray(liveNow) && liveNow.length > 0) return;
    }
    safelyPersistThreadMessages({
      instanceId,
      threadId,
      uid: currentUid,
      messages: seed,
      source: 'greet-on-focus',
      writerToken: threadSyncWriterTokenRef.current,
    });
    if (activeThreadIdRef.current === threadId) {
      dispatch({ type: 'SET_MESSAGES', payload: seed });
    }
  }, [enabled, instanceId, currentUid, composeGreetingSeed, getServerSyncUid, dispatch, activeThreadIdRef]);

  /**
   * PR #114 (Miles 2026-05-10) — Sub-fix D: track the activeThreadId
   * the centerpiece mirror effect last persisted under. When the
   * effect fires with a different activeThreadId than this ref, we
   * are in a thread-switch transition (SET_MESSAGES has already
   * swapped `messages` to the new thread's content). Emit a
   * telemetry warn so we can confirm the residual race is no longer
   * losing user bubbles in production. The defensive write is still
   * gated through `safelyPersistThreadMessages`'s empty-over-non-
   * empty guard, and PR #114 Sub-fix A makes new bubbles independent
   * of this effect's timing on the originating-thread side.
   */
  const prevMirroredActiveTidRef = useRef<string | null>(null);

  // GREET-ON-FOCUS driver: re-fires on activation, on threads-mirror changes
  // (a seed bumps messageCount → next run exits at the store guard), on
  // composer identity changes (tenant config finishing its async load turns
  // a null-yielding composer into a productive one — the retry is the whole
  // point), and on uid changes (account sign-in mid-session, the exact
  // "no intro after signing in" repro). All safety lives in the helper.
  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;
    void seedGreetingIntoEmptyActiveThread(activeThreadId).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('chatThreadMessages.greetOnFocus failed', error);
    });
  }, [enabled, ready, activeThreadId, threads, composeGreeting, currentUid, seedGreetingIntoEmptyActiveThread]);

  /**
   * Identity the hydrate effect last ran for. `undefined` until the first
   * hydrate of this mount — distinguishing "first boot" (adopt whatever the
   * live pane holds into the bootstrap, the legacy single-conversation
   * migration) from an AUTH-BOUNDARY re-hydrate (the pane belongs to the
   * departing identity and must never seed the next one's storage).
   */
  const lastHydratedUidRef = useRef<string | null | undefined>(undefined);

  // ── Hydrate from localStorage ────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return;
    }

    // G3 identity-transition detection: nothing clears the live pane at the
    // auth boundary, so on the re-hydrate right after sign-out/sign-in the
    // messages array still shows the DEPARTING identity's conversation.
    // Undefined = first hydrate of this mount (legacy adopt-live behavior).
    const prevHydratedUid = lastHydratedUidRef.current;
    const isIdentityTransition = prevHydratedUid !== undefined && prevHydratedUid !== currentUid;
    lastHydratedUidRef.current = currentUid;

    if (currentUid) {
      // FINDING-2 recovery FIRST: re-fire adoption upserts a previous load
      // recorded PENDING (the marker survives reloads; migration below will
      // find no legacy data on a recovery pass, so this is the only path
      // that can finish the push). Ordering matters — running before
      // adoptGuestThreadsToServer means a phase-1 marker written in THIS
      // hydrate is never read back and double-fired.
      resumePendingGuestAdoption();
      // G3: local migration first, then push what THIS run adopted into the
      // member's server store (index + transcripts + active pointer).
      adoptGuestThreadsToServer(migrateLegacyThreadStorageForUid(instanceId, currentUid));
    }

    const storedThreads = getFromLocalStorage<ChatThread[]>(storageKeys.THREAD_LIST, []);
    const storedActive = getFromLocalStorage<string | null>(storageKeys.ACTIVE_THREAD, null);

    if (storedThreads.length === 0) {
      // First run on this instance: bootstrap a single "default" thread from
      // whatever already lives in the live messages array so users don't
      // see their existing conversation disappear.
      //
      // EXCEPT at an identity transition (G3): the live array belongs to the
      // previous identity — seeding from it copied a member's transcript into
      // the fresh guest's UNSCOPED store at sign-out (leak: "all threads are
      // actively removed from sight"), and a guest/other-account pane into a
      // new uid's namespace at sign-in over empty storage. Bootstrap EMPTY
      // and clear the pane instead; greet-on-focus reseeds the greeting and
      // the account's index snapshot restores real threads.
      const seedFromLive = isIdentityTransition ? [] : latestMessagesRef.current;
      const bootstrap = createEmptyThread({
        title: deriveThreadTitle(seedFromLive),
        // SETTLED-ONLY (2026-08-01): same count discipline as the mirror
        // effect — a live in-flight placeholder never seeds the record count.
        messageCount: settledMessageCount(seedFromLive),
        hasUserContent: threadHasUserContent(seedFromLive),
      });
      pendingServerCreateIdsRef.current.add(bootstrap.id);
      sessionBootstrapDraftIdsRef.current.add(bootstrap.id);
      sessionMintedThreadIdsRef.current.add(bootstrap.id);
      setThreads([bootstrap]);
      setActiveThreadId(bootstrap.id);
      activeThreadIdRef.current = bootstrap.id;
      setToLocalStorage(storageKeys.THREAD_LIST, [bootstrap]);
      setToLocalStorage(storageKeys.ACTIVE_THREAD, bootstrap.id);
      safelyPersistThreadMessages({
        instanceId,
        threadId: bootstrap.id,
        uid: currentUid,
        messages: seedFromLive,
        source: 'bootstrap',
        writerToken: threadSyncWriterTokenRef.current,
      });
      if (isIdentityTransition) {
        dispatch({ type: 'SET_MESSAGES', payload: [] });
      }
    } else {
      // Re-arm the pending-create set for stored local DRAFTS (empty default
      // threads are deferred-create and never reached the server; the pending
      // set is in-memory, so after a reload their eventual first content
      // write must still be an isCreate — otherwise the create would go as an
      // update, omit the `archived` field, and produce an index-invisible doc.
      // "Empty" = no USER content (greeting-seeded drafts persist with
      // messageCount 1 + hasUserContent:false and are still drafts).
      for (const t of storedThreads) {
        if (!titleIsUserOwned(t) && !threadRecordHasUserContent(t) && isDefaultTitle(t.title)) {
          pendingServerCreateIdsRef.current.add(t.id);
        }
      }
      const reconciled = reconcileThreadCounts(storedThreads, instanceId, currentUid);
      setThreads(reconciled);
      // WORKSPACE CONTAINMENT AT BOOT (owner directive 2026-08-19: "ensure
      // they are entirely separated views even security etc." — after a
      // workspace switch the previously-active thread from ANOTHER
      // organization was restored here and its transcript rendered inside
      // the new workspace's portal, while the rail correctly showed none).
      // The restore validated only EXISTENCE; it now validates BELONGING
      // with the rail's own rule (threadAllowedInWorkspace). Workspace at
      // boot: the live publish when present, else the confirmed-workspace
      // cache (written only on successful strict resolution — fresh right
      // after a sealed switch); unresolved → allowed (the containment
      // effect below re-checks the moment resolution lands).
      const bootWorkspace = (
        groupReadAccountId
        || getActiveWorkspaceAccount()
        || readConfirmedWorkspace(currentUid)?.accountId
        || ''
      ).trim() || null;
      const allowedAtBoot = (t: ChatThread) => threadAllowedInWorkspace(t, bootWorkspace, currentUid);
      const storedActiveRecord = storedActive
        ? reconciled.find((t) => t.id === storedActive) || null
        : null;
      const validActive = storedActiveRecord && allowedAtBoot(storedActiveRecord)
        ? storedActiveRecord.id
        : orderThreads(reconciled).find(allowedAtBoot)?.id ?? null;
      if (!validActive) {
        // Every stored thread belongs to another workspace — never render a
        // foreign transcript here. Open a fresh greeted draft instead (the
        // workspace-resolution back-stamp effect binds it the moment the
        // workspace publishes).
        const containLang = getUserUiLanguage();
        const contained = createEmptyThread({
          title: localizedDefaultTitle(containLang),
          hasUserContent: false,
        });
        pendingServerCreateIdsRef.current.add(contained.id);
        sessionBootstrapDraftIdsRef.current.add(contained.id);
        sessionMintedThreadIdsRef.current.add(contained.id);
        const containSeed = composeGreetingSeed();
        const containedList = [contained, ...reconciled];
        setToLocalStorage(storageKeys.THREAD_LIST, containedList);
        setToLocalStorage(storageKeys.ACTIVE_THREAD, contained.id);
        safelyPersistThreadMessages({
          instanceId,
          threadId: contained.id,
          uid: currentUid,
          messages: containSeed,
          source: 'bootstrap',
          writerToken: threadSyncWriterTokenRef.current,
        });
        setThreads(containedList);
        activeThreadIdRef.current = contained.id;
        setActiveThreadId(contained.id);
        dispatch({ type: 'SET_MESSAGES', payload: containSeed });
        // eslint-disable-next-line no-console
        console.warn('[useChatThreads] boot containment: no stored thread belongs to this workspace — opened a fresh draft', {
          workspace: bootWorkspace,
        });
        setReady(true);
        return;
      }
      activeThreadIdRef.current = validActive;
      setActiveThreadId(validActive);
      // Hydrate the active-on-load thread's conversation pane from its
      // per-thread store. reconcileThreadCounts() above fixes the persisted
      // index counts, but the LIVE message pane is only populated by a
      // SET_MESSAGES dispatch — which previously fired only on a subsequent
      // switchThread/activation. Without this, the thread that is active at
      // page load rendered a BLANK pane (and its mirror-effect messageCount
      // stayed 0) until the user clicked away to another tab and back.
      // (Point 2 re-test 2026-06-02: active-on-load thread mc:0 / empty pane.)
      // On a fresh page load state.messages is empty, so loading the active
      // thread's stored transcript here is safe and never clobbers live
      // content; the empty-over-non-empty guard still protects the on-disk
      // copy. Mirrors switchThread's load exactly.
      const activeStored = getFromLocalStorage<ChatMessage[]>(
        threadMsgKey(instanceId, validActive, currentUid),
        [],
      );
      if (activeStored.length > 0) {
        const normalizedActiveStored = activeStored.map(normalizeTimestamp);
        const rehydrated = prepareRehydratedMessages(normalizedActiveStored);
        dispatch({ type: 'SET_MESSAGES', payload: rehydrated });
        void reconcileInterruptedServerBackedMessages(
          validActive,
          normalizedActiveStored,
          rehydrated,
        ).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadMessages.reconcile interrupted active-on-load failed', error);
        });
      } else {
        if (isIdentityTransition) {
          // The pane still shows the previous identity's conversation and the
          // incoming active thread has no local transcript — clear it before
          // the async server hydrate (which only dispatches on content).
          dispatch({ type: 'SET_MESSAGES', payload: [] });
        }
        // Status-wrapped hydrate: a failed server read surfaces as
        // retry-able state instead of a swallowed console.error.
        void runActiveThreadHydrate(validActive);
      }
    }
    setReady(true);
    // Only hydrate once per instance change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    currentUid,
    instanceId,
    storageKeys.THREAD_LIST,
    storageKeys.ACTIVE_THREAD,
    resumePendingGuestAdoption,
    adoptGuestThreadsToServer,
    runActiveThreadHydrate,
    activeThreadIdRef,
    dispatch,
  ]);

  // LOCAL back-stamp on workspace resolution (multi-org separation round 2,
  // 2026-08-19) — the client-side twin of the repository's INCIDENT-20260818
  // re-stamp: records this session minted BEFORE the workspace resolved
  // (bootstrap drafts, pending creates) are unbound in LOCAL state too, and
  // deferred drafts never get a server echo to heal them. Stamp exactly that
  // set — never pre-existing unbound threads, whose canonical binding belongs
  // to the M4 backfill.
  useEffect(() => {
    if (!enabled) return;
    return onActiveWorkspaceResolved((resolvedAccountId) => {
      setThreads((prev) => {
        let changed = false;
        const next = prev.map((t) => {
          if (t.accountId) return t;
          // 2026-08-20 scope fix: keyed on the session-mint LEDGER, not the
          // pending-create set — hydrate re-arms EVERY stored user-empty
          // draft into pending-create (so its first content write is still
          // an isCreate), which made this stamp adopt YESTERDAY'S drafts
          // into whatever workspace the user opened today.
          const mintedThisSession =
            sessionMintedThreadIdsRef.current.has(t.id)
            || sessionBootstrapDraftIdsRef.current.has(t.id);
          if (!mintedThisSession) return t;
          changed = true;
          return { ...t, accountId: resolvedAccountId };
        });
        return changed ? next : prev;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !ready) return;
    // COLD-BOOT FAIL-CLOSED (2026-08-21): while a signed-in user's workspace
    // identity is still resolving with no cache to seed it, DO NOT subscribe
    // — a subscription now would render the unfiltered cross-workspace union
    // and snap strict seconds later (the incognito appear-then-vanish churn),
    // and would burn the one-shot boot-dedupe on that wrong view. The flag
    // flip re-runs this effect and the first snapshot arrives workspace-true.
    if (workspaceIdentityPending) return;
    const uid = getServerSyncUid();
    if (!uid) return;

    // PER-WORKSPACE WINDOW MERGE (multi-org separation round 2, 2026-08-19):
    // the global subscription's newest-40 window spans EVERY workspace, so a
    // busy org could push the active org's threads out of the window and the
    // reconcile then dropped their local records ("threads keep changing").
    // A second, workspace-scoped subscription guarantees the ACTIVE org its
    // own full window; both streams merge by id before the one shared
    // pipeline below (identical docs — either copy serves).
    const globalSnapshotRef: { current: ChatThread[] | null } = { current: null };
    const workspaceSnapshotRef: { current: ChatThread[] | null } = { current: null };
    // Function declaration (hoisted) — first invoked only after a snapshot
    // arrives, by which point handleServerThreads below is initialized.
    function emitMergedSnapshots(): void {
      const byId = new Map<string, ChatThread>();
      for (const t of globalSnapshotRef.current ?? []) byId.set(t.id, t);
      for (const t of workspaceSnapshotRef.current ?? []) {
        if (!byId.has(t.id)) byId.set(t.id, t);
      }
      handleServerThreads(Array.from(byId.values()));
    }

    // Registered with the session listener registry (2026-07-14) so
    // resetSession detaches it at sign-out — previously React-effect-cleanup
    // only, which leaks the listener across the auth boundary when the
    // component survives the transition.
    const handleServerThreads = (serverThreads: ChatThread[]) => {
      pruneExpiredTombstones();
      const live = serverThreads.filter((t) => !isThreadTombstoned(t.id));
      const seenServerIds = new Set(live.map((t) => t.id));
      seenServerIds.forEach((id) => pendingServerCreateIdsRef.current.delete(id));
      // PARTIAL-VIEW MARKER (2026-08-20): with a workspace companion stream
      // expected, a merge fired by whichever stream lands first is a HALF
      // view. Shared by the reconcile's drop suppression AND the boot-dedupe
      // below — deciding the dedupe/adopt on a half view either dropped the
      // greeted draft against a workspace-skewed list or (empty first half)
      // consumed the one-shot marker and disabled dedupe for the session.
      const partialView = Boolean(groupReadAccountId)
        && (globalSnapshotRef.current === null || workspaceSnapshotRef.current === null);
      const wsForSnapshot = (groupReadAccountId || '').trim() || null;
      // WINDOW WATERMARK (2026-09-22) — what each stream PROVES.
      //
      // Read from the RAW stream arrays, never from `live`: `live` is the
      // tombstone-filtered copy, and filtering rows out of a full window makes
      // `length < limit`, which collapses the floor to -Infinity and silently
      // restores the lossy behaviour in exactly the case (a just-archived
      // thread) where it hurts most.
      const snapshotWindows = [
        windowCoverageOf(globalSnapshotRef.current, WORKING_SET_LIMIT, null),
        windowCoverageOf(workspaceSnapshotRef.current, WORKING_SET_LIMIT, wsForSnapshot),
      ];
      // A saturated window is the only evidence that older threads exist.
      setWindowSaturated(snapshotWindows.some((w) => !!w && w.floorMs !== -Infinity));

      // Remote-archive detection: if the ACTIVE thread was in the previous
      // snapshot and vanished from this one (and isn't a pending create), it
      // may have been archived from another window/device. Confirm against the
      // server doc before acting — absence alone can be working-set churn.
      // MUST run BEFORE the M1 repair probe: the probe marks vanished threads
      // as pending creates, and pending creates are (correctly) invisible to
      // this check — probing first consumed the vanish transition and
      // stranded the user typing into a remotely-archived conversation.
      const remoteArchiveCandidate = planRemoteArchiveCheck({
        prevServerIds: prevServerSnapshotIdsRef.current,
        currentServerIds: seenServerIds,
        activeThreadId: latestActiveThreadIdRef.current,
        pendingCreateIds: pendingServerCreateIdsRef.current,
      });
      const prevIdsForPassingNotice = prevServerSnapshotIdsRef.current;
      prevServerSnapshotIdsRef.current = seenServerIds;
      if (remoteArchiveCandidate) {
        void confirmRemoteArchiveRef.current?.(remoteArchiveCandidate);
      }

      // PASSING remote-archive notice (owner directive 2026-08-26: "same in
      // the thread summary view it should be clear to the other users"): a
      // SHARED thread that vanished from the snapshot while the viewer was
      // elsewhere gets a visible line naming who archived it, instead of a
      // silent disappearance from the rail. Absence alone proves nothing
      // (working-set churn), so each candidate is confirmed against its own
      // server doc — once per session, capped per snapshot, and only for
      // threads the server had marked shared.
      if (prevIdsForPassingNotice) {
        // Newly-vanished shared threads JOIN the pending set; ids already in it
        // stay eligible until a probe actually answers for them. Enrolment and
        // probing are separate because the "was in the previous snapshot" test
        // is consumed as soon as prevServerSnapshotIds is overwritten above.
        for (const t of latestThreadsRef.current) {
          if (t.id === remoteArchiveCandidate) continue;
          if (t.sharedThread !== true) continue;
          if (!prevIdsForPassingNotice.has(t.id)) continue;
          if (seenServerIds.has(t.id)) continue;
          if (pendingServerCreateIdsRef.current.has(t.id)) continue;
          if (remoteArchiveNoticeShownRef.current.has(t.id)) continue;
          pendingArchiveProbeIdsRef.current.add(t.id);
        }
        // A thread that reappeared is not a candidate at all (working-set churn).
        seenServerIds.forEach((id) => pendingArchiveProbeIdsRef.current.delete(id));
        // Probe a few per snapshot; the rest keep their place in the queue.
        const titleFor = (id: string): string =>
          latestThreadsRef.current.find((t) => t.id === id)?.title || '';
        const batch = [...pendingArchiveProbeIdsRef.current].slice(0, 3);
        for (const candidateId of batch) {
          const candidateTitle = titleFor(candidateId);
          void (async () => {
            try {
              const info = await getChatThreadArchiveInfo(uid, candidateId);
              // A NEGATIVE probe is an answer: it was working-set churn, so
              // stop asking — but never mark it "announced", or a genuine
              // archive of that thread later in the session would be silent.
              pendingArchiveProbeIdsRef.current.delete(candidateId);
              if (!info.archived) return;
              const by = (info.archivedBy || '').trim();
              if (!by || by === uid) return; // own archive from another device — no announcement
              remoteArchiveNoticeShownRef.current.add(candidateId);
              addArchivedTombstone(candidateId);
              beginThreadArchiveNotice({
                kind: 'passing',
                threadId: candidateId,
                threadTitle: candidateTitle,
                archivedByName: (info.archivedByName || '').trim(),
              });
            } catch {
              // A FAILED probe (offline) is not an answer — leave the id in the
              // queue so a later snapshot retries it.
            }
          })();
        }
      }

      // M1 REPAIR PROBE (INCIDENT-20260818, disappeared threads): a
      // content-bearing LOCAL thread absent from the snapshot is either
      // (a) aged out of the top-40 window (server doc exists — fine to drop)
      // or (b) an update-shaped-create victim whose index doc is missing or
      // `archived`-less — which the reconcile below would silently drop and
      // the persist effect would then flush out of localStorage FOREVER.
      // Mark candidates as pending (so this and future reconciles retain
      // them), then probe the server doc once per session (≤5 per snapshot):
      // exists → unmark (normal aging); missing + untombstoned → keep the
      // pending mark and re-send a FULL create-shaped write. The remote-
      // archive candidate above is NEVER probed here: its doc exists (M1
      // victims never appear in snapshots, so the two classes are disjoint)
      // and the confirm path owns moving the user off it.
      {
        const candidates = selectMissingDocRepairCandidates({
          localThreads: latestThreadsRef.current,
          serverIds: seenServerIds,
          pendingCreateIds: pendingServerCreateIdsRef.current,
        }).filter((t) => t.id !== remoteArchiveCandidate && !missingDocProbedIdsRef.current.has(t.id)).slice(0, 5);
        for (const candidate of candidates) {
          missingDocProbedIdsRef.current.add(candidate.id);
          pendingServerCreateIdsRef.current.add(candidate.id);
          void (async () => {
            try {
              const exists = await chatThreadIndexDocExists(uid, candidate.id);
              if (exists) {
                pendingServerCreateIdsRef.current.delete(candidate.id);
                return;
              }
              if (isThreadTombstoned(candidate.id)) return;
              console.warn('[useChatThreads] repairing missing thread index doc', { threadId: candidate.id });
              writeThreadToServer(candidate);
            } catch {
              // Probe failed (offline etc.) — the pending mark keeps the
              // thread visible; a later session retries.
            }
          })();
        }
      }

      // BUG-THREAD-RESURRECTION: tombstones are deliberately NOT cleared
      // here. The index query filters archived==false, so a tombstoned id
      // being absent from the snapshot proves nothing — it is absent both
      // BEFORE the archive write lands (doc still archived:false but
      // filtered out of `live` above) and after. The old clear-on-absence
      // loop could never see a tombstoned id in `seenServerIds` (it was
      // just filtered out of `live`), so it cleared every tombstone on the
      // FIRST snapshot after archiving — collapsing the protection window
      // to ~one snapshot and letting the still-unarchived doc resurrect
      // the chip. Tombstones now expire only via TTL
      // (pruneExpiredTombstones); a future restore-from-archive flow must
      // delete the id from archivedTombstonesRef and persist explicitly.

      setThreads((prev) => {
        let localThreads = prev;
        // BOOT-DEDUPE: on the FIRST snapshot of the session, if the server
        // already has threads, drop any still-empty local bootstrap drafts —
        // a fresh device/incognito signin must show the account's real
        // threads with ZERO extra "New thread" chips. The drafts are
        // local-only (deferred create), so dropping them loses nothing.
        // Scope (2026-07-13, personalized greeting): only drafts THIS
        // SESSION's hydrate minted speculatively (sessionBootstrapDraftIds)
        // qualify. A draft restored from storage was deliberately created by
        // the user pressing + — it may hold a persisted greeting the user is
        // looking at, and must survive reload (single-empty guarantees there
        // is at most one, so no chip accumulation is possible). "Still-empty"
        // = no USER content, so a greeting-seeded bootstrap (welcome injected
        // before the first snapshot) is still correctly dropped.
        if (!firstSnapshotConsumedRef.current && !partialView) {
          firstSnapshotConsumedRef.current = true;
          if (live.length > 0) {
            const drops = prev.filter((t) =>
              pendingServerCreateIdsRef.current.has(t.id)
              && sessionBootstrapDraftIdsRef.current.has(t.id)
              && !titleIsUserOwned(t)
              && !threadRecordHasUserContent(t)
              && isDefaultTitle(t.title));
            if (drops.length > 0) {
              // CONTAINMENT-AWARE ADOPT (2026-08-20 owner repro: the boot
              // guard's greeted draft was dropped here and the adopt landed
              // on yesterday's foreign empty draft). Only threads that
              // BELONG to this workspace are adoption candidates, a thread
              // with real content beats an empty one, and when the
              // workspace offers no candidate at all the greeted draft the
              // user is looking at is KEPT, not dropped.
              const activeId = latestActiveThreadIdRef.current;
              const activeDropped = Boolean(activeId && drops.some((t) => t.id === activeId));
              const allowedLive = live.filter((t) => threadAllowedInWorkspace(t, wsForSnapshot, uid));
              const preferredAdopt =
                allowedLive.find((t) => threadRecordHasUserContent(t)) || allowedLive[0] || null;
              const effectiveDrops = activeDropped && !preferredAdopt
                ? drops.filter((t) => t.id !== activeId)
                : drops;
              if (effectiveDrops.length > 0) {
                const dropIds = new Set(effectiveDrops.map((t) => t.id));
                localThreads = prev.filter((t) => !dropIds.has(t.id));
                effectiveDrops.forEach((t) => pendingServerCreateIdsRef.current.delete(t.id));
                // If the active pane was a dropped draft, adopt a server thread
                // (post-updater, via the ref-assigned handler — never dispatch
                // inside this updater). G3 sign-in restore: prefer the account's
                // REMEMBERED active thread (users/{uid}/chat_threads_meta/active,
                // written on every switch) over most-recent-activity, so a
                // re-sign-in lands exactly where the user left off — viewing an
                // older thread never bumps lastActiveAt, so recency alone can
                // pick the wrong thread. The meta read is best-effort; the
                // workspace-allowed fallback stays. The remembered pointer is
                // held to the same containment rule — it can point at another
                // workspace's thread after a switch.
                if (activeId && dropIds.has(activeId) && preferredAdopt) {
                  const fallback = preferredAdopt.id;
                  queueMicrotask(() => {
                    void (async () => {
                      let target = fallback;
                      try {
                        const remembered = await getActiveThread(uid, instanceId);
                        if (remembered && allowedLive.some((t) => t.id === remembered)) {
                          target = remembered;
                        }
                      } catch { /* keep workspace-allowed fallback */ }
                      if (latestActiveThreadIdRef.current === activeId) {
                        // eslint-disable-next-line no-console
                        console.warn('[useChatThreads] boot-dedupe: adopting server thread for dropped bootstrap draft', {
                          from: activeId, to: target, workspace: wsForSnapshot,
                        });
                        adoptServerThreadRef.current?.(target);
                      }
                    })();
                  });
                }
              }
            }
          }
        }
        return reconcileThreadListsWithServerSnapshot({
          localThreads,
          serverThreads: live,
          pendingCreateIds: pendingServerCreateIdsRef.current,
          // 2026-07-31 multi-device directive: keep the ACTIVE thread's chip
          // alive when it ages out of the 40-doc server window (convergent
          // stamps no longer re-stamp it to the top on every view).
          activeThreadId: latestActiveThreadIdRef.current,
          // PARTIAL-VIEW GUARD (2026-08-19): with a workspace companion
          // stream expected, a merge fired by whichever stream lands first
          // must not DROP local threads (the persist effect would make the
          // loss durable). Drops resume once both snapshots have delivered.
          suppressDrops: partialView,
          // What each live stream PROVES — see `snapshotWindows` above.
          windows: snapshotWindows,
        });
      });
    };

    const unsubscribe = registerFirestoreListener(
      subscribeChatThreadIndex(uid, instanceId, (serverThreads) => {
        globalSnapshotRef.current = serverThreads;
        emitMergedSnapshots();
      }),
    );
    const unsubscribeWorkspace = groupReadAccountId
      ? registerFirestoreListener(
          subscribeChatThreadIndexForWorkspace(uid, instanceId, groupReadAccountId, (serverThreads) => {
            workspaceSnapshotRef.current = serverThreads;
            emitMergedSnapshots();
          }),
        )
      : null;
    return () => {
      unsubscribe();
      unsubscribeWorkspace?.();
    };
  }, [
    enabled,
    ready,
    instanceId,
    getServerSyncUid,
    isThreadTombstoned,
    pruneExpiredTombstones,
    groupReadAccountId,
    workspaceIdentityPending,
    // Wake coordinator (2026-09-01): an epoch bump forces teardown+reattach
    // so a silently-dead index stream comes back with a fresh first snapshot.
    listenerEpoch,
  ]);

  // ── Persist thread list + active id whenever they change ────────────────
  const prevListPersistUidRef = useRef<string | null | undefined>(undefined);
  const prevActivePersistUidRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !ready) return;
    // M5 (INCIDENT-20260818): the bare setToLocalStorage swallowed
    // QuotaExceededError — with a full localStorage the thread RECORD list
    // silently stopped persisting while transcripts (which have their own
    // IDB fallback) kept landing; a reload then restored a stale list and
    // freshly created threads vanished. The list now always mirrors to IDB.
    //
    // G3 identity-boundary defer (2026-08-21 sign-out leak): on the commit
    // where `currentUid` flips, `threads` still holds the DEPARTING
    // identity's list while this key already points at the NEW identity's
    // namespace — persisting here wrote the member's thread list into the
    // guest key at sign-out (localStorage AND the IDB mirror, which the
    // recovery effect below then resurrected). Same one-commit defer the
    // message mirror uses.
    const prevListUid = prevListPersistUidRef.current;
    prevListPersistUidRef.current = currentUid;
    if (prevListUid !== undefined && prevListUid !== currentUid) return;
    void writeJsonWithIdbFallback(storageKeys.THREAD_LIST, threads);
  }, [enabled, ready, threads, storageKeys.THREAD_LIST, currentUid]);

  // M5 recovery (one-shot per storage key): if the IDB mirror holds threads
  // missing from state (a quota-starved session's creations), merge them
  // newest-wins after hydrate settles.
  const idbListRecoveryDoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !ready) return;
    if (idbListRecoveryDoneRef.current === storageKeys.THREAD_LIST) return;
    idbListRecoveryDoneRef.current = storageKeys.THREAD_LIST;
    void (async () => {
      try {
        const mirrored = await readJsonFromIdb<ChatThread[]>(storageKeys.THREAD_LIST);
        if (!Array.isArray(mirrored) || mirrored.length === 0) return;
        setThreads((prev) => {
          const known = new Set(prev.map((t) => t.id));
          const missing = mirrored.filter((t) => t && t.id && !known.has(t.id) && !isThreadTombstoned(t.id));
          if (missing.length === 0) return prev;
          console.warn('[useChatThreads] recovered threads from the IDB mirror', {
            count: missing.length,
          });
          return mergeThreadListsNewestWins(prev, missing);
        });
      } catch { /* best-effort recovery */ }
    })();
  }, [enabled, ready, storageKeys.THREAD_LIST, isThreadTombstoned]);

  useEffect(() => {
    if (!enabled || !ready) return;
    // Same identity-boundary defer as the list persist above (2026-08-21).
    const prevActiveUid = prevActivePersistUidRef.current;
    prevActivePersistUidRef.current = currentUid;
    if (prevActiveUid !== undefined && prevActiveUid !== currentUid) return;
    setToLocalStorage(storageKeys.ACTIVE_THREAD, activeThreadId);
  }, [enabled, ready, activeThreadId, storageKeys.ACTIVE_THREAD, currentUid]);

  // ── Cross-DEVICE real-time sync (owner report 2026-07-14) ───────────────
  //
  // Live Firestore listener on the ACTIVE thread's server transcript (the
  // legacy whole-array doc — written by every client mirror AND by the
  // server task-completion/failure writer). Before this, thread messages had
  // NO live listener anywhere: a device that held a local copy never
  // consulted the cloud again (hydrate hard-gates on local-non-empty), so a
  // browser could sit on an hour-old thread while an incognito session on
  // the SAME account raced ahead. Exactly ONE message listener exists at a
  // time (active thread only; resubscribes on switch), registered with the
  // session listener registry so sign-out detaches it.
  //
  // This also delivers durability audit #14 for free: the server's terminal
  // write lands in this doc, so an open tab receives completions push-style
  // instead of relying on the 35-minute poll loop.
  //
  // Declared BEFORE the mirror effect so the fail-open path (listener
  // unavailable) opens the stale-clobber gate in the same commit, ahead of
  // the mirror's first evaluation.
  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;
    const uid = getServerSyncUid();
    if (!uid) return;
    const threadId = activeThreadId;
    const markSeen = () => { serverSnapshotSeenThreadsRef.current.add(threadId); };

    let detach: (() => void) | null = null;
    try {
      const raw = subscribeThreadMessages(
        uid,
        threadId,
        (snap) => {
          // Wake-coordinator health stamp: any delivered snapshot (cache or
          // server) proves this stream is alive right now.
          lastTranscriptSnapshotAtRef.current.set(threadId, Date.now());
          const gateWasClosed = !serverSnapshotSeenThreadsRef.current.has(threadId);
          markSeen();
          if (activeThreadIdRef.current !== threadId) return;
          const localMessages = ((getLatestMessagesRef.current
            ? getLatestMessagesRef.current()
            : latestMessagesRef.current) || []) as ChatMessage[];
          if (snap.messages.length > 0) {
            const normalizedRemote = snap.messages.map(normalizeTimestamp);
            const prepared = prepareRehydratedMessages(normalizedRemote, { reconcileInterrupted: false });
            // I4: while this thread has UNLOADED older pages, a remote
            // snapshot may carry messages older than the loaded window (e.g.
            // another device's full-transcript mirror). Foreign messages below
            // the window boundary are DROPPED — adopting them against the
            // window's top edge could render old history with a phantom gap
            // where the unloaded pages belong; they surface converged when
            // their page is loaded (see mergeRemoteThreadMessagesIntoLocal
            // doc). In-window upgrades (server finalize of a live turn) are
            // untouched, so Round-K convergence on the newest window holds.
            const pagingEntry = earlierPagingRef.current.get(`${currentUid || 'anon'}::${threadId}`);
            const windowBoundaryTs = pagingEntry?.olderAvailable
              ? oldestLoadedMessageTs(localMessages)
              : undefined;
            const merged = mergeRemoteThreadMessagesIntoLocal(
              localMessages,
              prepared,
              typeof windowBoundaryTs === 'number' ? { dropForeignOlderThanMs: windowBoundaryTs } : {},
            );
            if (merged.changed) {
              // Belt-and-suspenders: never let a lagging snapshot downgrade a
              // just-finalized bubble (Symptom 2 family).
              const guarded = mergeSyncedMessagesPreservingFinalized(merged.messages, localMessages);
              dispatch({ type: 'SET_MESSAGES', payload: guarded });
              // H1 (owner report 2026-07-14, spinner-outlives-turn): when this
              // snapshot's merge just retired the thread's LAST in-flight
              // indication (the server's task-completion/failure write upgraded
              // the pending placeholder to the finalized report), release the
              // SAME per-thread processing flags the send path set. This is the
              // completion path for tasks delivered push-style / via the
              // hydrate-recovery poll — no live onDone exists to release them,
              // the per-message resume is skipped while the recovery poll runs,
              // and reconcileDiscoveryChips never touches the ACTIVE thread.
              // Message dispatch FIRST (above), release second — mirroring
              // finalizeStreamingMessageById's ordering. Reducer-state only; no
              // new persist path (echo-loop brakes intact).
              if (remoteFinalizeShouldReleaseProcessing(localMessages, guarded)) {
                dispatch({ type: 'SET_PROCESSING', payload: { isProcessing: false, threadId } });
              }
              // The mirror effect persists + uploads the merged transcript.
              return;
            }
          }
          if (gateWasClosed && localMessages.length > 0) {
            // First authoritative observation with nothing to adopt: flush
            // the local transcript the stale-clobber gate was holding back
            // (e.g. a message sent before the first snapshot arrived).
            void upsertThreadMessages(uid, instanceId, threadId, localMessages).catch((error) => {
              // eslint-disable-next-line no-console
              console.error('chatThreadMessages.gate-flush upsert failed', error);
            });
          }
        },
        (error) => {
          // FAIL-CLOSED (2026-09-01, supersedes the 2026-07-14 fail-open): an
          // errored listener has OBSERVED nothing — markSeen here authorized
          // the mirror's whole-array LWW upsert to overwrite newer turns from
          // other devices with a stale local rehydrate. The repository now
          // resubscribes on capped backoff, and sync-up durability rides the
          // one-shot authoritative read in verifyTranscriptGateOnce (only a
          // healthy observation may open the gate).
          // eslint-disable-next-line no-console
          console.warn('chatThreadMessages.subscribe error', error);
          transcriptListenerErrorAtRef.current.set(threadId, Date.now());
          void verifyTranscriptGateOnce(uid, threadId);
        },
      );
      if (typeof raw === 'function') {
        detach = registerFirestoreListener(raw);
      } else {
        // Deliberately STILL fail-open (unlike the error path above): a
        // non-function subscribe fires only in stubbed environments (tests,
        // assertCurrentUid boundary frames) with NO retry loop running —
        // fail-closed here would deadlock the mirror forever.
        markSeen();
      }
    } catch (error) {
      // Sync subscribe throw: same stubbed-environment reasoning — fail-open
      // is the only shape that cannot deadlock (no retry loop exists here).
      markSeen();
      // eslint-disable-next-line no-console
      console.warn('chatThreadMessages.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    instanceId,
    currentUid,
    getServerSyncUid,
    dispatch,
    activeThreadIdRef,
    verifyTranscriptGateOnce,
    // Wake coordinator: an epoch bump forces teardown+reattach (fresh
    // authoritative first snapshot) after a suspected-dead stream.
    listenerEpoch,
  ]);

  // ── WAKE COORDINATOR (returning-tab reliability, 2026-09-01) ─────────────
  //
  // One handler for visibilitychange + pageshow(persisted) + online. On
  // returning after >WAKE_MIN_HIDDEN_MS hidden: reattach any listener that
  // has not delivered a snapshot since we went hidden (Firestore's watch
  // stream can die WITHOUT firing the error callback across long sleeps —
  // the SDK's internal retry is what the error callback never sees ending),
  // and re-run the active thread's server hydrate (no-op when the local
  // store is populated). Idempotent and cheap when healthy: a fresh snapshot
  // stamp means no reattach, and the epoch bump itself is rate-limited
  // (WAKE_EPOCH_BUMP_MIN_INTERVAL_MS) so 'online' flaps never storm.
  // Follows the visibilitychange idiom of the active-task discovery effect
  // in useGlobalChat (authed gating handled by enabled/ready here).
  useEffect(() => {
    if (!enabled || !ready) return;
    if (typeof document === 'undefined') return;
    const bumpEpoch = (): void => {
      const now = Date.now();
      if (now - lastEpochBumpAtRef.current < WAKE_EPOCH_BUMP_MIN_INTERVAL_MS) return;
      lastEpochBumpAtRef.current = now;
      setListenerEpoch((n) => n + 1); // teardown+reattach every server stream
    };
    const onWake = (source: 'visibility' | 'pageshow' | 'online'): void => {
      const hiddenAt = lastHiddenAtRef.current;
      if (source === 'visibility' && (!hiddenAt || Date.now() - hiddenAt < WAKE_MIN_HIDDEN_MS)) return;
      const tid = activeThreadIdRef.current;
      if (!tid) return;
      const lastSnap = lastTranscriptSnapshotAtRef.current.get(tid) ?? 0;
      const staleSinceHidden = hiddenAt !== null && lastSnap < hiddenAt;
      const erroredSinceSnap = (transcriptListenerErrorAtRef.current.get(tid) ?? 0) > lastSnap;
      if (lastSnap === 0 || staleSinceHidden || erroredSinceSnap || source !== 'visibility') {
        bumpEpoch();
      }
      // Status-wrapped hydrate — no-op when the local store already has the
      // transcript, so a healthy wake pays one localStorage read.
      void runActiveThreadHydrate(tid);
    };
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      onWake('visibility');
    };
    const onPageShow = (e: PageTransitionEvent): void => {
      // Back/forward-cache restores resume a frozen page whose streams are
      // beyond suspicion — always treat as a wake.
      if (e.persisted) onWake('pageshow');
    };
    const onOnline = (): void => onWake('online');
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow as EventListener);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow as EventListener);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, ready, runActiveThreadHydrate, activeThreadIdRef]);

  // ── Cross-ACCOUNT real-time sync (group-threading delivery fix 2026-07-23) ──
  //
  // Live listener on the ACTIVE thread's SHARED group store
  // (group_thread_messages/{threadId}/messages — written server-side by the
  // turn mirror). The cross-device listener above covers the reader's OWN
  // tree; it can never deliver ANOTHER participant's turns, because the mirror
  // writes those only to the shared store and the one-shot overlay read runs
  // only inside thread-open hydrates (which hard-gate on local-empty).
  // Verified live 2026-07-23: the owner's turn mirrored at 12:54Z while the
  // member's open view of the SAME thread stopped at 11:44Z — nothing on her
  // device ever re-read the store.
  //
  // Each snapshot (including the first, which backfills anything missed while
  // the thread was closed) is merged through the SAME `mergeOwnAndGroupMessages`
  // contract as the one-shot overlay: own entries win by id/anchor, the
  // reader's own overlay turns are dropped, additions sort chronologically —
  // so a streaming local bubble is never downgraded. No additions → the merge
  // returns the own list by identity and nothing dispatches. The mirror effect
  // below persists the merged transcript, exactly as it does for the
  // cross-device listener.
  //
  // Fail-closed: the repository subscribe is a no-op unless the account-scoped
  // group READ flag is on, and detaches itself on rules-denial (solo /
  // unregistered threads), so non-QA accounts and non-shared threads pay
  // nothing.
  // Live-membership surfacing (2026-08-16): a thread the SERVER just pushed
  // into this user's index carries the server-owned `sharedThread` marker.
  // The membership snapshot (`sharedThreadIds`) is pull-based and may not
  // know the thread yet — the marker must open the overlay listener gate
  // immediately, or a freshly-added recipient opens the thread and sees no
  // transcript until the next membership read. Memoized to the ACTIVE thread
  // only, so ordinary thread-list churn (recency reorders, count updates)
  // never detaches/reattaches the listener.
  const activeThreadMarkedShared = useMemo(
    () => threads.some((t) => t.id === activeThreadId && t.sharedThread === true),
    [threads, activeThreadId],
  );

  // GT-01 floors are BAKED into the overlay listener's Firestore query at
  // attach time (where('orderTs','>=',floor)), and they fail closed to
  // MAX_SAFE_INTEGER until the membership read lands. A listener attached
  // inside that window matched NOTHING and — before 2026-08-26 — stayed that
  // way for the whole view. Tracking the floors version here re-runs the
  // attach effect the moment the real floor arrives.
  const [historyFloorsVersion, setHistoryFloorsVersion] = useState(
    () => getGroupThreadHistoryFloorsVersion(),
  );
  useEffect(() => subscribeGroupThreadHistoryFloors(setHistoryFloorsVersion), []);

  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;
    // RACE GUARD (2026-07-23 #2): the restored active thread mounts BEFORE the
    // async account resolution, so a subscribe fired then read the
    // account-scoped flag as OFF (no published account yet) and stayed a no-op
    // for the whole view — the owner's tab never received the member's
    // store-only turns while the assistant replies (account-tree listener,
    // unflagged) kept arriving. Gate on the RESOLVED account id and depend on
    // it: null → wait; when it lands this effect re-runs and attaches, and the
    // first snapshot backfills anything missed.
    if (!groupReadAccountId) return;
    // Scale guard (all-accounts rollout): skip the listen for threads this user
    // is NOT a registered participant of — those listens are rules-denied by
    // construction. Fail-OPEN when membership is unknown (null), so delivery is
    // never suppressed by a membership read miss. See opts.sharedThreadIds.
    // The server-owned index marker (activeThreadMarkedShared) also opens the
    // gate: a just-added recipient's membership snapshot lags the add.
    if (sharedThreadIds && !sharedThreadIds.has(activeThreadId) && !activeThreadMarkedShared) return;
    const uid = getServerSyncUid();
    if (!uid) return;
    const threadId = activeThreadId;
    let detach: (() => void) | null = null;
    try {
      const raw = subscribeGroupThreadMessages(
        threadId,
        (overlay) => {
          if (activeThreadIdRef.current !== threadId) return;
          const localMessages = ((getLatestMessagesRef.current
            ? getLatestMessagesRef.current()
            : latestMessagesRef.current) || []) as ChatMessage[];
          const normalizedOverlay = overlay.map(normalizeTimestamp);
          // Delivery ladder (PR-2): the sender's own turns echoed by the shared
          // store ARE delivery — record them BEFORE the merge drops them.
          //
          // Delivered means COMPLETE (2026-09-11): an echo that carries fewer
          // attachments than the sender sent is the shape Malaika's
          // screenshots took — text through, pictures dropped by the server
          // sanitizer, no heal path — and stamping it "delivered" is how the
          // bubble came to show ✓✓ over a photo nobody got. The expected
          // counts come from the sender's OWN copy of each turn.
          const expectedAttachmentCounts = new Map<string, number>();
          for (const own of (latestMessagesRef.current || []) as ChatMessage[]) {
            const ownId = typeof own?.sharedMessageId === 'string' ? own.sharedMessageId.trim() : '';
            const n = Array.isArray(own?.attachmentsSerialized) ? own.attachmentsSerialized.length : 0;
            if (ownId && n > 0) expectedAttachmentCounts.set(ownId, n);
          }
          const ownEchoes = collectOwnDeliveredIds(normalizedOverlay, uid, expectedAttachmentCounts);
          for (const echoedId of ownEchoes.delivered) {
            deliveryLedger.markDelivered(deliveryKey(threadId, echoedId));
          }
          for (const short of ownEchoes.incomplete) {
            deliveryLedger.markAttachmentsMissing(deliveryKey(threadId, short.id), short.missing);
          }
          // Reactions (PR-4): remember the store's instant for every id it
          // carries — the rules anchor a reaction to the message doc's orderTs.
          setActiveThreadSharedOrderTs((prev) => {
            let next: Record<string, number> | null = null;
            for (const m of normalizedOverlay) {
              const id = typeof m.id === 'string' ? m.id.trim() : '';
              if (!id || typeof m.orderTs !== 'number' || !Number.isFinite(m.orderTs)) continue;
              if (prev[id] === m.orderTs) continue;
              if (!next) next = { ...prev };
              next[id] = m.orderTs;
            }
            return next ?? prev;
          });
          const merged = mergeOwnAndGroupMessages(localMessages, normalizedOverlay, uid);
          if (merged === localMessages) return; // no additions — identity exit
          dispatch({ type: 'SET_MESSAGES', payload: merged });
          // The mirror effect persists + uploads the merged transcript.
        },
        // Explicit account: flag resolution no longer depends on the publish
        // effect having run first.
        { accountId: groupReadAccountId },
      );
      if (typeof raw === 'function') detach = registerFirestoreListener(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('groupThreadMessages.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    groupReadAccountId,
    sharedThreadIds,
    activeThreadMarkedShared,
    // Floor resolved/changed → re-attach with the correct query (see above).
    historyFloorsVersion,
    getServerSyncUid,
    dispatch,
    activeThreadIdRef,
    // Wake coordinator (2026-09-01): epoch bump = full reattach, same as the
    // cross-device listener (the overlay's rules-denial self-detach tolerates
    // rapid detach/reattach — it re-evaluates at attach time).
    listenerEpoch,
  ]);

  // READ RECEIPTS (PR-3, 2026-09-08): one collection listener per open shared
  // thread — reader id → high-water mark. Same gates and lifecycle as the
  // overlay listener above (resolved account, membership scale guard, floors,
  // wake epoch); reset to {} the moment the thread changes so a stale thread's
  // readers never paint under the next thread's bubbles.
  const [activeThreadReceipts, setActiveThreadReceipts] = useState<ReceiptsByReader>({});
  useEffect(() => {
    setActiveThreadReceipts({});
    if (!enabled || !ready || !activeThreadId) return;
    if (!groupReadAccountId) return;
    if (sharedThreadIds && !sharedThreadIds.has(activeThreadId) && !activeThreadMarkedShared) return;
    const threadId = activeThreadId;
    let detach: (() => void) | null = null;
    try {
      const raw = subscribeThreadReceipts(
        threadId,
        (receipts) => {
          if (activeThreadIdRef.current !== threadId) return;
          setActiveThreadReceipts(receipts);
        },
        { accountId: groupReadAccountId },
      );
      if (typeof raw === 'function') detach = registerFirestoreListener(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('threadReceipts.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    groupReadAccountId,
    sharedThreadIds,
    activeThreadMarkedShared,
    historyFloorsVersion,
    activeThreadIdRef,
    listenerEpoch,
  ]);

  // TYPING PRESENCE (T-R15, Miles 2026-09-21): one collection listener per open
  // shared thread — uid → the server's instant, and nothing else. Same gates and
  // lifecycle as the receipts listener above, and the same reset-on-thread-change
  // rule so a previous room's typists never appear under this one's composer.
  //
  // It NEVER lights a processing flag and NEVER dispatches: a colleague's
  // keystroke is not this tab's work, and lighting the flag would spin the
  // room's pending widget for a draft nobody has sent. (The same property the
  // live-turn listener is pinned on — streamingFlag.ts, the greyed-out-refresh
  // class.) The repository's own flag check means that with the lever off this
  // effect attaches nothing at all.
  const [activeThreadTyping, setActiveThreadTyping] = useState<TypingView[]>([]);
  useEffect(() => {
    setActiveThreadTyping([]);
    if (!enabled || !ready || !activeThreadId) return;
    if (!groupReadAccountId) return;
    if (sharedThreadIds && !sharedThreadIds.has(activeThreadId) && !activeThreadMarkedShared) return;
    const threadId = activeThreadId;
    let detach: (() => void) | null = null;
    try {
      const raw = subscribeThreadTyping(
        threadId,
        (docs) => {
          if (activeThreadIdRef.current !== threadId) return;
          setActiveThreadTyping(docs);
        },
        { accountId: groupReadAccountId },
      );
      if (typeof raw === 'function') detach = registerFirestoreListener(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('threadTyping.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    groupReadAccountId,
    sharedThreadIds,
    activeThreadMarkedShared,
    historyFloorsVersion,
    activeThreadIdRef,
    listenerEpoch,
  ]);

  // EMOJI REACTIONS (PR-4, 2026-09-09): one floored collection listener per
  // open thread — shared, or solo with a registry row (listenGate.ts) — same
  // lifecycle as the receipts above. Both maps belong to ONE thread and reset
  // only when it changes: resetting on every re-attach (a floor or membership
  // bump) used to wipe the store instants the overlay had taught, leaving every
  // message non-reactable until the next snapshot (2026-09-09).
  const [activeThreadReactions, setActiveThreadReactions] = useState<ReactionsByMessage>({});
  const [activeThreadSharedOrderTs, setActiveThreadSharedOrderTs] = useState<Record<string, number>>({});
  const reactionMapsThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (reactionMapsThreadRef.current !== (activeThreadId ?? null)) {
      reactionMapsThreadRef.current = activeThreadId ?? null;
      setActiveThreadReactions({});
      setActiveThreadSharedOrderTs({});
    }
    if (!enabled || !ready || !activeThreadId) return;
    if (!groupReadAccountId) return;
    if (!shouldListenForReactions({ threadId: activeThreadId, sharedThreadIds, activeThreadMarkedShared, registeredThreadIds })) return;
    const threadId = activeThreadId;
    let detach: (() => void) | null = null;
    try {
      const raw = subscribeThreadReactions(
        threadId,
        (reactions) => {
          if (activeThreadIdRef.current !== threadId) return;
          setActiveThreadReactions(reactions);
        },
        { accountId: groupReadAccountId },
      );
      if (typeof raw === 'function') detach = registerFirestoreListener(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('threadReactions.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    groupReadAccountId,
    sharedThreadIds,
    registeredThreadIds,
    activeThreadMarkedShared,
    historyFloorsVersion,
    activeThreadIdRef,
    listenerEpoch,
  ]);

  // LIVE-TURN CARDS (§11 WS-2, 2026-09-20): one floored listener on the open
  // SHARED thread's `live/` docs — the receipts' gates, the reactions' reset rule
  // (the list belongs to ONE thread and resets only when it changes; a floor or
  // membership re-attach must not blank the lane mid-turn — the next snapshot
  // replaces the whole set anyway). It NEVER calls startProcessing and never
  // takes a streaming lease: a card is what someone ELSE is doing.
  const [activeThreadLiveTurns, setActiveThreadLiveTurns] = useState<LiveTurnView[]>([]);
  const liveTurnsThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (liveTurnsThreadRef.current !== (activeThreadId ?? null)) {
      liveTurnsThreadRef.current = activeThreadId ?? null;
      setActiveThreadLiveTurns([]);
    }
    if (!enabled || !ready || !activeThreadId) return;
    if (!groupReadAccountId) return;
    if (sharedThreadIds && !sharedThreadIds.has(activeThreadId) && !activeThreadMarkedShared) return;
    const threadId = activeThreadId;
    let detach: (() => void) | null = null;
    try {
      const raw = subscribeThreadLiveTurns(
        threadId,
        (docs) => {
          if (activeThreadIdRef.current !== threadId) return;
          setActiveThreadLiveTurns(docs);
        },
        { accountId: groupReadAccountId },
      );
      if (typeof raw === 'function') detach = registerFirestoreListener(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('threadLiveTurns.subscribe unavailable', error);
    }
    return () => {
      if (detach) {
        try { detach(); } catch { /* already detached by session reset */ }
      }
    };
  }, [
    enabled,
    ready,
    activeThreadId,
    groupReadAccountId,
    sharedThreadIds,
    activeThreadMarkedShared,
    historyFloorsVersion,
    activeThreadIdRef,
    listenerEpoch,
  ]);

  /**
   * Identity the mirror effect last persisted under (G3). `undefined` until
   * the first mirror pass of this mount.
   */
  const prevMirroredUidRef = useRef<string | null | undefined>(undefined);

  // ── ONE NAME PER THREAD (2026-09-18, Miles: a new thread's name changes
  //    "once, straight to the final name") ───────────────────────────────────
  //
  // An unnamed thread used to be renamed TWICE during its first turn —
  // "New thread" -> the first prompt's text -> the model's short label — and
  // each hop fanned a write out to every device. The label lane is the namer
  // now; the placeholder HOLDS while it works.
  //
  // Two per-thread facts, and the reason each is a ref rather than state: they
  // are read inside the mirror effect's updater and must not re-fire it.
  //   • RELEASED — the label lane cannot name this thread, so the heuristic may
  //     fill the placeholder instead. Set by the deadline below, which covers
  //     EVERY reason the label never lands (empty result, parse failure,
  //     network error, abort, the attempt cap, a request that simply hangs —
  //     inferThreadLabel has no timeout of its own) with one mechanism instead
  //     of six branches. A release only ever ALLOWS the heuristic; rule 3 of
  //     resolveMirrorThreadTitle still refuses to overwrite a real name, so a
  //     release that arrives after the label did is inert.
  //   • SETTLED — this thread's one automatic name has been written. Locks the
  //     labeler and the backfill pass for the rest of the session, so a label
  //     landing after the heuristic filled in cannot be the second change.
  const titleSettleReleasedRef = useRef<Set<string>>(new Set());
  const autoTitleSettledRef = useRef<Set<string>>(new Set());
  const titleSettleTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // A release must reach the mirror effect, which is driven by `messages` and
  // would otherwise not re-run until the next token. One counter, bumped once
  // per release.
  const [titleSettleTick, setTitleSettleTick] = useState(0);
  const titleSettleOnce = useMemo(() => resolveThreadTitleSettleEnabled(), []);

  const releaseTitleSettle = useCallback((threadId: string) => {
    if (!threadId || titleSettleReleasedRef.current.has(threadId)) return;
    titleSettleReleasedRef.current.add(threadId);
    const timer = titleSettleTimerRef.current.get(threadId);
    if (timer) {
      clearTimeout(timer);
      titleSettleTimerRef.current.delete(threadId);
    }
    setTitleSettleTick((n) => n + 1);
  }, []);

  // Clear the deadlines on unmount only — same rule as the label debounce
  // timers below: a thread switch must not cancel another thread's clock.
  useEffect(() => {
    const timers = titleSettleTimerRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // ── Continuously mirror active thread's live messages to its per-thread
  //    key + keep title/counts/processing flag in sync. ────────────────────
  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;

    // G3 identity-boundary defer: on the commit where `currentUid` changes,
    // `activeThreadId`/`messages` still hold the DEPARTING identity's pane —
    // the same-commit hydrate's state updates land one commit later.
    // Persisting here wrote that conversation into the NEW identity's
    // namespace: the member transcript into the fresh guest's UNSCOPED key
    // at sign-out, or a guest/other-account pane into uid-B's scoped key
    // (and, once the stale-clobber gate opened, uid-B's SERVER store) on a
    // shared device. Defer exactly one commit — the boundary hydrate always
    // dispatches SET_MESSAGES, so the effect re-fires with an aligned
    // pairing. Lossless: the departing identity's content is already in its
    // own storage/cloud, and its local keys are being purged anyway.
    const prevMirroredUid = prevMirroredUidRef.current;
    prevMirroredUidRef.current = currentUid;
    if (prevMirroredUid !== undefined && prevMirroredUid !== currentUid) {
      return;
    }

    // PR #114 (Miles 2026-05-10) — Sub-fix D: detect mid-transition
    // mirror effect fires. If activeThreadId differs from the last
    // value we mirrored under, we just crossed a thread switch in
    // this same react commit; `messages` may still be propagating
    // (SET_MESSAGES on switch). Emit telemetry so we can confirm in
    // production whether this race ever occurs after Sub-fix A
    // landed.
    const prevMirroredActiveTid = prevMirroredActiveTidRef.current;
    if (shouldDeferMirrorPersist(prevMirroredActiveTid, activeThreadId)) {
      // ────────────────────────────────────────────────────────────────
      // Point 2 T3 fix (Malaika 2026-06-02): thread label↔content
      // mismatch after archive.
      //
      // We just crossed a thread switch in THIS commit (activeThreadId
      // changed since the last mirror), but `messages` is a SEPARATE
      // reducer store updated via `dispatch(SET_MESSAGES)`. On the
      // archive→fallback and switchThread paths, `setActiveThreadId`
      // and that dispatch are not guaranteed to land in the same React
      // commit — so on this render `activeThreadId` is already the NEW
      // (fallback/target) thread while `messages` may still hold the
      // OUTGOING thread's content. Persisting here would write the old
      // thread's messages under the NEW thread's per-thread key
      // (`prest0_thread_msgs__<instance>__<newId>`) and upsert them to
      // the new thread's server doc — permanently corrupting it so the
      // new thread's chip shows the wrong conversation (survives reload,
      // because it's written to localStorage + Firestore).
      //
      // This effect is guaranteed to re-fire once `messages` catches up
      // (every switch/archive/fallback path explicitly dispatches
      // SET_MESSAGES for the new thread), at which point activeThreadId
      // and messages are aligned and the persist runs correctly. So we
      // DEFER all mirror work for exactly one commit here: advance the
      // tracking ref but do NOT persist messages under a possibly-stale
      // pairing. The previous (now-archived/outgoing) thread already had
      // its messages snapshotted by snapshotActive()/the remove path's
      // explicit safelyPersistThreadMessages before the switch, so no
      // data is lost by skipping this transitional commit.
      // eslint-disable-next-line no-console
      console.warn(
        '[thread-iso:persist] thread.history.mirror-effect-thread-changed (deferring persist to next commit)',
        {
          prevActiveTid: prevMirroredActiveTid,
          currActiveTid: activeThreadId,
          count: messages.length,
        }
      );
      prevMirroredActiveTidRef.current = activeThreadId;
      return;
    }
    prevMirroredActiveTidRef.current = activeThreadId;

    // Persist the active thread's messages under its own key.
    // Routed through `safelyPersistThreadMessages` so the empty-
    // over-non-empty guard catches any stale `state.messages = []`
    // dispatch (e.g. from a tab-resume rehydrate path) before it
    // wipes the on-disk transcript. See `threadPersist.ts`.
    safelyPersistThreadMessages({
      instanceId,
      threadId: activeThreadId,
      uid: currentUid,
      messages,
      source: 'mirror-effect',
      writerToken: threadSyncWriterTokenRef.current,
    });

    const uid = getServerSyncUid();
    // STALE-CLOBBER GUARD (2026-07-14): defer the server upsert until the
    // live listener has observed the thread's authoritative server state at
    // least once — the whole-array write is LWW, and upserting a transcript
    // rehydrated from stale localStorage used to erase every newer turn made
    // on other devices. The listener's first snapshot merges server content
    // in and flushes any held-back local transcript, so nothing is lost.
    if (uid && messages.length > 0 && serverSnapshotSeenThreadsRef.current.has(activeThreadId)) {
      void upsertThreadMessages(uid, instanceId, activeThreadId, messages).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('chatThreadMessages.upsert failed', error);
      });
    }

    // THE TITLE IS A FUNCTION OF THE FIRST USER MESSAGE, NOT OF THE STREAM
    // (2026-09-18). Resolved once per commit, here, and handed to
    // resolveMirrorThreadTitle below — which never receives the array, so a
    // streaming token cannot reach the decision at all.
    const firstUserMessage = messages.find((m) => m.isUser) ?? null;
    // THE ONE NAME'S FLOOR. The label lane gets TITLE_SETTLE_FALLBACK_MS from
    // the moment this thread has something to name; after that the heuristic
    // fills the placeholder instead, so a dead label lane can never leave the
    // row reading "New thread" forever. Armed once per thread.
    //
    // A thread whose labeler is SUPPRESSED is deliberately never armed: a
    // participant in someone else's shared thread is named by its creator's
    // canonical title, and a local heuristic fill would show the same
    // conversation under a different name to each person — the very thing
    // threadTitleAuthority exists to prevent.
    if (
      titleSettleOnce
      && firstUserMessage
      && !autoTitleSuppressed(activeThreadId)
      && !titleSettleReleasedRef.current.has(activeThreadId)
      && !titleSettleTimerRef.current.has(activeThreadId)
    ) {
      const armedThreadId = activeThreadId;
      titleSettleTimerRef.current.set(
        armedThreadId,
        setTimeout(() => {
          titleSettleTimerRef.current.delete(armedThreadId);
          releaseTitleSettle(armedThreadId);
        }, TITLE_SETTLE_FALLBACK_MS),
      );
    }
    // `titleSettleTick` is in this effect's deps so a release re-runs it; read
    // here only to make that dependency honest rather than incidental.
    void titleSettleTick;
    const settleReleased = !titleSettleOnce || titleSettleReleasedRef.current.has(activeThreadId);

    setThreads(prev => {
      let dirty = false;
      // Did THIS pass turn a placeholder into a name? That, and only that, is
      // the thread's one automatic name being written here.
      let filledThePlaceholder = false;
      const next = prev.map(t => {
        if (t.id !== activeThreadId) return t;
        // ONE automatic titling decision, shared with catchUpOutgoingThreadRecord
        // (resolveMirrorThreadTitle). It replaced a four-clause `shouldRederive`
        // that asked whether a PERSON had named the thread on ONE of its four
        // conditions — which is how a name somebody typed came to be overwritten
        // by their own first prompt on every streaming token.
        const title = resolveMirrorThreadTitle(t, firstUserMessage, settleReleased);
        if (title !== t.title && isPlaceholderThreadTitle(t.title) && !isPlaceholderThreadTitle(title)) {
          filledThePlaceholder = true;
        }
        // Thread-isolation (2026-07-10 Phase 2): the global `isProcessing`
        // flag is NO LONGER stamped onto the thread record. It was documented
        // as a "legacy backup," but nothing reads `thread.isProcessing` for
        // the chip anymore (ThreadSwitcherBar drives every chip from
        // `state.processingByThread`), and writing the global bit here
        // (a) polluted the server thread doc with a stale/global value that
        // could smear across devices, and (b) forced a server write on every
        // global-flag flip. The per-thread spinner is the sole source of truth.
        const updated: ChatThread = {
          ...t,
          title,
          // SETTLED-ONLY count (owner feedback, Miles 2026-08-01): the row
          // meta must not read "3 messages" while the third is still
          // generating. `settledMessageCount` excludes in-flight assistant
          // placeholders via the shared isInFlightPlaceholderMessage
          // predicate — the SAME predicate the lastActiveAt derivation below
          // uses — so during a turn the count holds at N and steps to N+1
          // exactly at finalize. This stamp is also the flood-era
          // 'Group thread' messageCount HEAL site (2026-07-31): opened
          // flood-clobbered records heal to the settled transcript length.
          messageCount: settledMessageCount(messages),
          // USER-CONTENT SEMANTICS (personalized greeting, 2026-07-13): stamp
          // whether the live transcript holds any USER message, so a seeded
          // assistant greeting (messageCount 1) never masquerades as content
          // for reuse / deferred-create / empty-delete decisions.
          hasUserContent: threadHasUserContent(messages),
          // 2026-07-31 multi-device directive: CONVERGENT stamp, never
          // wall-clock now. The old `new Date().toISOString()` made every
          // non-empty messages dispatch (including pure VIEWING) dirty the
          // record and fan a server write out to all devices — the
          // top-of-rail leapfrog. Every device now derives the identical
          // value from the identical transcript, so view-only runs go
          // clean (no write, no reorder) and the doc comment above
          // ("viewing bumps only lastViewedAt") is finally true. Genuine
          // new messages still bump: their timestamps are newer.
          lastActiveAt: deriveConvergentLastActiveAt(t.lastActiveAt, messages),
          // PR-3 (2026-09-08): having the thread OPEN no longer zeroes the count
          // or stamps lastViewedAt — "read" is a fact about attention, stamped
          // by markThreadViewed after the 3 s dwell (useThreadViewDwell). A
          // message that lands while the thread is open therefore shows its
          // badge until it, too, has been on screen for the dwell.
        };
        if (
          updated.title !== t.title ||
          updated.messageCount !== t.messageCount ||
          updated.hasUserContent !== t.hasUserContent ||
          updated.lastActiveAt !== t.lastActiveAt
        ) {
          dirty = true;
        }
        return updated;
      });
      const nextActive = next.find((t) => t.id === activeThreadId);
      // A PLACEHOLDER JUST BECAME A NAME — that was this thread's ONE automatic
      // name, so lock the label lane and the backfill pass out for the rest of
      // the session. Without this a label landing a second after the heuristic
      // filled in would be the second visible change the policy exists to
      // remove. Stamped beside the server write because that is where this
      // updater already performs its side effects (idempotent: a Set add).
      //
      // Keyed on the TRANSITION, never on "the title is not a placeholder":
      // that reading stamped every thread the moment it was opened, which
      // would have frozen the UI-language backfill's re-label on any thread
      // the viewer had visited this session.
      if (titleSettleOnce && filledThePlaceholder) {
        autoTitleSettledRef.current.add(activeThreadId);
      }
      if (dirty && nextActive) writeThreadToServer(nextActive);
      return dirty ? next : prev;
    });
  }, [
    enabled,
    ready,
    activeThreadId,
    messages,
    instanceId,
    currentUid,
    writeThreadToServer,
    getServerSyncUid,
    // The settle policy's inputs. `autoTitleSuppressed` and `releaseTitleSettle`
    // are stable useCallbacks reading through refs, so listing them cannot
    // re-fire this effect; `titleSettleTick` is exactly what must.
    titleSettleOnce,
    titleSettleTick,
    autoTitleSuppressed,
    releaseTitleSettle,
  ]);

  // ── Multi-tab same-thread sync (PR fix/thread-history-loss-and-multi-tab-sync)
  //
  // When another tab in the same browser writes the active thread's
  // per-thread storage, surface the change locally without waiting
  // for the (debounced, dormant-tab-unreliable) `storage` event.
  // We re-read storage and dispatch SET_MESSAGES so the chat UI
  // shows the foreign tab's update immediately.
  //
  // The signal source is `safelyPersistThreadMessages`'s
  // BroadcastChannel post (see threadPersist.ts). We deliberately
  // do NOT subscribe to localStorage `storage` events here — those
  // are already debounced + bypassed under threading mode in
  // useGlobalChat.ts (PR #97), and re-introducing them would
  // re-open the original cross-tab smear bug. BroadcastChannel
  // gives us same-browser fan-out without that risk.
  //
  // Per-thread guards in `safelyPersistThreadMessages` mean the
  // re-read here cannot end up with a stale-empty payload that
  // wipes the thread — the on-disk content is already past the
  // empty-over-non-empty filter by the time we observe the
  // BroadcastChannel signal.
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;
    const surfaceStoredThreadMessages = () => {
        // Re-read the thread's stored messages and surface them.
        const storageKey = threadMsgKey(instanceId, activeThreadId, currentUid);
        let rawStored: string | null = null;
        try { rawStored = localStorage.getItem(storageKey); } catch { rawStored = null; }
        const loaded = getFromLocalStorage<ChatMessage[]>(storageKey, []);
        // Defensive: if the foreign-tab content is empty AND our
        // local view is non-empty, ignore the signal. The persist
        // helper's empty-over-non-empty guard should prevent this
        // case from ever happening on the producer side, but we
        // double-check on the consumer side too.
        const localMessages = (getLatestMessagesRef.current
          ? getLatestMessagesRef.current()
          : latestMessagesRef.current) as ChatMessage[];
        // Nothing stored ⇒ nothing to surface, PERIOD (2026-08-09 CI fix):
        // dispatching SET_MESSAGES([]) here is never a no-op — the record
        // mirror effect would stamp messageCount:0 over a freshly-hydrated
        // index record. The replay-on-subscribe pass below made this branch
        // reachable with an empty store (real broadcasts only ever follow a
        // non-empty persist).
        if (loaded.length === 0) return;
        // ECHO GUARD (OOM incident, Miles 2026-07-12): when the stored
        // content is byte-identical to what this pane already shows, there is
        // nothing to surface — dispatching anyway would mint a fresh messages
        // identity, re-fire the mirror-persist effect, and (cross-tab) bounce
        // the same bytes back over BroadcastChannel forever. The stored string
        // is exactly JSON.stringify of a message array, so comparing it to the
        // live array's serialization is content equality; any mismatch merely
        // falls through to the (convergent) normal path.
        try {
          if (rawStored !== null && rawStored === JSON.stringify(localMessages)) return;
        } catch { /* serialization failure → fall through to the normal path */ }
        const normalizedLoaded = loaded.map(normalizeTimestamp);
        const rehydrated = prepareRehydratedMessages(normalizedLoaded, { reconcileInterrupted: false });
        // Symptom 2 (report appears-then-vanishes): a lagging foreign-tab
        // snapshot must never downgrade a just-finalized bubble back to
        // its pending/placeholder version for the same id. Preserve the
        // finalized in-memory copy per-id; ids/order are untouched.
        const guarded = mergeSyncedMessagesPreservingFinalized(rehydrated, localMessages);
        dispatch({ type: 'SET_MESSAGES', payload: guarded });
        void reconcileInterruptedServerBackedMessages(
          activeThreadId,
          normalizedLoaded,
          guarded,
        ).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadMessages.reconcile interrupted thread-sync failed', error);
        });
    };
    const unsubscribe = subscribeToThreadSync(
      { instanceId, threadId: activeThreadId, token: threadSyncWriterTokenRef.current },
      surfaceStoredThreadMessages,
    );
    // Replay-on-subscribe (2026-08-09 multi-tab greeting parity): a foreign
    // tab's persist broadcast landing in the hydrate→subscribe gap used to be
    // LOST — never replayed — so a pane could render a greeting that no
    // longer existed on disk indefinitely. One immediate pass converges it.
    // Scoped to an EMPTY or GREETING-ONLY pane: a real transcript is already
    // owned by the hydrate/merge paths, and replaying over one could race a
    // debounced persist (the byte-identical echo guard can't judge staleness).
    const localNow = ((getLatestMessagesRef.current
      ? getLatestMessagesRef.current()
      : latestMessagesRef.current) || []) as ChatMessage[];
    const greetingOnlyLocal = localNow.every(
      (m) => !!m && m.isUser !== true && (m.sourceTag === 'welcome' || m.id === 'welcome'),
    );
    if (localNow.length === 0 || greetingOnlyLocal) {
      surfaceStoredThreadMessages();
    }
    return unsubscribe;
  }, [
    enabled,
    ready,
    activeThreadId,
    instanceId,
    currentUid,
    dispatch,
    reconcileInterruptedServerBackedMessages,
  ]);

  // ── Dynamic chip name + topic summary inference ────────────────
  //
  // After every change to the active thread's user-message *content*, fire a
  // single cheap LLM call to infer a short chip label and a 1–2 sentence
  // topic summary. Updates the thread record on resolve. Strictly
  // fire-and-forget — never blocks the reply path.
  //
  // Trigger model (revised 2026-05-09 in PR fix/thread-isolation-per-thread-state):
  //   The trigger is "is the latest user message's id different from the
  //   last-incorporated-id we recorded for this thread?". Tracking by id
  //   instead of fingerprint fixes the deep-research-retry regression
  //   Miles caught on the live demo: when the original send was
  //   silently dropped by the cross-thread interference (and any
  //   in-flight inference call resolved with empty content because no
  //   assistant reply ever landed), the fingerprint-based tracker
  //   considered the message "already incorporated". Asking the same
  //   text again produced the same fingerprint and the effect early-
  //   returned — the chip label / topic summary never refreshed.
  //   Every user message now gets a fresh stable id at addMessage()
  //   time, so the retry is unambiguously "new" and inference fires.
  //
  // Abort discipline (revised 2026-07-02, request-storm incident):
  //   We track in-flight controllers per-thread. An in-flight call for the
  //   SAME user-message id is left alone — churn ticks (streaming deltas,
  //   reducer normalization, cross-tab sync) must NOT abort-and-refire it.
  //   Only a genuinely NEW user message id aborts the older same-thread
  //   call (latest user intent wins). Switching threads does NOT abort
  //   in-flight calls for the previous thread; those resolve and write
  //   into the (still-mounted) thread record so chips for unfocused
  //   threads keep getting labelled.
  //
  //   The label-inference's AbortController is OWNED HERE — it is NOT
  //   the same controller as the chat-send orchestrator's. A misfire
  //   on the chat-send path (cross-thread interference, network drop)
  //   does NOT cascade-abort the cheap label call.
  //
  // Storm guards (2026-07-02: stale-tab loops machine-gunned the labeler
  // model at ~750 req/min for hours — see INCIDENT-20260702 follow-up):
  //   • DEBOUNCE: inference fires only after the messages array has been
  //     quiet for LABEL_INFER_DEBOUNCE_MS. Continuous churn keeps
  //     resetting the timer and emits ZERO requests.
  //   • ATTEMPT CAP: at most LABEL_INFER_MAX_ATTEMPTS fired calls per
  //     user-message id. After that the id is marked incorporated (the
  //     heuristic deriveThreadTitle chip stands) so the gate closes even
  //     if the model keeps returning unusable output.
  // ────────────────────────────────────────────────────────────────────

  /** Per-thread in-flight inference controllers. */
  const inferenceAbortByThreadRef = useRef<Map<string, AbortController>>(new Map());
  /** Per-thread user-message id the in-flight inference call is labelling. */
  const inferenceInFlightMsgIdRef = useRef<Map<string, string>>(new Map());
  /** Per-thread pending debounce timers (cleared on reschedule + unmount). */
  const labelDebounceTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /** Per-thread fired-attempt counter for the current user-message id. */
  const labelAttemptsRef = useRef<Map<string, { msgId: string; attempts: number }>>(new Map());
  /**
   * Per-thread *id* of the last user message we successfully labelled.
   * Replaces the previous fingerprint-based tracker. Switching from
   * fingerprint to id makes retries reliably re-trigger inference even
   * when the same text is sent again — the new addMessage() id is
   * guaranteed unique.
   */
  const lastIncorporatedUserMsgIdRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!enabled || !ready || !activeThreadId) return;

    // Find the latest user message and compute a stable fingerprint.
    let latestUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) { latestUserIdx = i; break; }
    }
    if (latestUserIdx === -1) {
      // No user messages yet — nothing to label.
      maybeLogThreadLabel(activeThreadId, 'no-user-msg', 0,
        inferenceAbortByThreadRef.current.has(activeThreadId));
      return;
    }

    const latestUserMsg = messages[latestUserIdx];
    const latestUserMsgId = (latestUserMsg && typeof latestUserMsg.id === 'string' && latestUserMsg.id)
      || userMessageFingerprint(latestUserMsg, latestUserIdx);
    const incorporated = lastIncorporatedUserMsgIdRef.current.get(activeThreadId);

    // Gate via the pure decision core (skip when incorporated / same-id
    // in flight; close the gate on attempt-cap; otherwise schedule).
    const attemptState = labelAttemptsRef.current.get(activeThreadId);
    const decision = decideLabelInference({
      latestUserMsgId,
      incorporatedMsgId: incorporated,
      attemptMsgId: attemptState?.msgId,
      attemptCount: attemptState?.attempts ?? 0,
      inFlightMsgId: inferenceInFlightMsgIdRef.current.get(activeThreadId),
      hasInFlightController: inferenceAbortByThreadRef.current.has(activeThreadId),
    });
    if (decision.action === 'skip') {
      return;
    }
    if (decision.action === 'close-gate') {
      // ATTEMPT CAP: this message id has burned all its label attempts.
      // The heuristic deriveThreadTitle chip stands. Without this, a model
      // that persistently returns unusable output keeps the retry gate
      // open forever (2026-07-02 storm).
      lastIncorporatedUserMsgIdRef.current.set(activeThreadId, latestUserMsgId);
      maybeLogThreadLabel(activeThreadId, 'attempt-cap', attemptState?.attempts ?? 0, false);
      return;
    }
    if (decision.abortInFlight) {
      // A NEWER user message obsoletes an in-flight call for an older one
      // (only — don't disturb other threads' inferences).
      const existing = inferenceAbortByThreadRef.current.get(activeThreadId);
      if (existing) existing.abort();
    }

    const userCount = messages.reduce((n, m) => n + (m.isUser ? 1 : 0), 0);

    // Snapshot the thread id and language so a later switchThread
    // can't cause us to overwrite a different thread's title.
    const targetThreadId = activeThreadId;
    // Bug G (Miles 2026-05-11): chip labels MUST render in the user's
    // UI language regardless of conversation-content language. We
    // resolve the UI language at inference-trigger time so a fresh
    // English browser session never gets a Spanish auto-title (and
    // vice-versa) just because the first user message was typed in
    // the other language. Source: `services/threadMetadata/uiLanguage.ts`.
    const language: UiLanguage = getUserUiLanguage();
    const messagesSnapshot = messages.slice();

    // DEBOUNCE: (re)schedule the label call. Every churn tick lands here
    // and pushes the timer back, so the call fires only once the messages
    // array has been quiet for LABEL_INFER_DEBOUNCE_MS — with the freshest
    // snapshot (the one from the run that armed the final timer).
    const pendingTimer = labelDebounceTimerRef.current.get(activeThreadId);
    if (pendingTimer) clearTimeout(pendingTimer);
    maybeLogThreadLabel(activeThreadId, 'debounce-scheduled', userCount, false);

    const timer = setTimeout(() => {
      labelDebounceTimerRef.current.delete(targetThreadId);

      // Re-check every gate at fire time — state may have moved while
      // the timer was pending (label incorporated, same-id call fired,
      // attempts exhausted by another path).
      const prevAttempts = labelAttemptsRef.current.get(targetThreadId);
      const fired = (prevAttempts && prevAttempts.msgId === latestUserMsgId)
        ? prevAttempts.attempts
        : 0;
      const fireDecision = decideLabelInference({
        latestUserMsgId,
        incorporatedMsgId: lastIncorporatedUserMsgIdRef.current.get(targetThreadId),
        attemptMsgId: prevAttempts?.msgId,
        attemptCount: prevAttempts?.attempts ?? 0,
        inFlightMsgId: inferenceInFlightMsgIdRef.current.get(targetThreadId),
        hasInFlightController: inferenceAbortByThreadRef.current.has(targetThreadId),
      });
      if (fireDecision.action === 'skip') return;
      if (fireDecision.action === 'close-gate') {
        lastIncorporatedUserMsgIdRef.current.set(targetThreadId, latestUserMsgId);
        maybeLogThreadLabel(targetThreadId, 'attempt-cap', fired, false);
        // The label lane has given up on this thread — hand the one name to the
        // heuristic now rather than making the row wait out the deadline.
        releaseTitleSettle(targetThreadId);
        return;
      }
      labelAttemptsRef.current.set(targetThreadId, { msgId: latestUserMsgId, attempts: fired + 1 });

      // A live inference always wins over a stray earlier call still
      // registered for this thread (e.g. the Bug G backfill pass).
      const stray = inferenceAbortByThreadRef.current.get(targetThreadId);
      if (stray) stray.abort();

      const controller = new AbortController();
      inferenceAbortByThreadRef.current.set(targetThreadId, controller);
      inferenceInFlightMsgIdRef.current.set(targetThreadId, latestUserMsgId);
      maybeLogThreadLabel(targetThreadId, 'user-msg-changed', userCount, true);

      // Billing identity for the label decision (2026-09-07): the thread's deterministic
      // session id + the resolved workspace account — never a route name or a storage list.
      void inferThreadLabel(messagesSnapshot, language, {
        signal: controller.signal,
        sessionId: deterministicThreadSessionId(targetThreadId),
        accountId: getActiveWorkspaceAccount() ?? undefined,
      })
        .then(result => {
          if (controller.signal.aborted) {
            maybeLogThreadLabel(targetThreadId, 'aborted', userCount, false);
            // Aborted because a fresher user message arrived: that message's own
            // call will name the thread, so do NOT release here — the deadline
            // is the backstop if none of them ever lands.
            return;
          }
          const { shortName, topicSummary } = result;
          // Skip when the model returned nothing usable. Falling back to
          // the heuristic deriveThreadTitle is the right behaviour here,
          // and we deliberately do NOT mark the message as incorporated —
          // the next change gets to try again, up to the attempt cap.
          if (!shortName && !topicSummary) {
            maybeLogThreadLabel(targetThreadId, 'empty-result', userCount, false);
            // Nothing usable came back. The attempt cap will retry on the next
            // user message; meanwhile the heuristic may have the one name.
            releaseTitleSettle(targetThreadId);
            return;
          }

          // Mark the user message id as successfully incorporated *before*
          // the setThreads dispatch so a same-tick re-fire of this effect
          // (e.g. an assistant streaming token landing immediately after)
          // sees the id as already absorbed.
          lastIncorporatedUserMsgIdRef.current.set(targetThreadId, latestUserMsgId);

          setThreads(prev2 => prev2.map(t => {
            if (t.id !== targetThreadId) return t;
            // Bug G: NEVER overwrite a user-renamed title. The
            // `userRenamed` flag is set by `renameThread()` and the
            // backfill pass also respects it. We still allow the
            // topicSummary (tooltip) to refresh so hover preview keeps
            // tracking the conversation, but the chip label is
            // immutable once the user has chosen one.
            // Normalize labeler output: a default-title response (any casing
            // era, e.g. "New Thread") must never overwrite the canonical
            // default — otherwise the empty thread acquires a "real"-looking
            // title that evades default-title reuse and janitor logic.
            const isLabelerDefault = shortName && isDefaultTitle(shortName);
            // A shared thread this user did not create is named by its creator,
            // whose canonical title the server propagates here. Labeling it
            // locally would show the same conversation under a different name
            // to each participant. The topicSummary still refreshes — that is
            // per-user hover context, not the shared name.
            // ONE AUTOMATIC NAME PER THREAD (2026-09-18). The third conjunct is
            // the settle: this thread's one automatic name has already been
            // written — by an earlier label, or by the heuristic after this lane
            // gave up — so a later label is the SECOND visible change the
            // one-name policy exists to remove. `topicSummary` is deliberately
            // outside the lock and keeps tracking the conversation.
            const titleLocked = titleIsUserOwned(t)
              || autoTitleSuppressed(targetThreadId)
              || (titleSettleOnce && autoTitleSettledRef.current.has(targetThreadId));
            const nextTitle = (!titleLocked && shortName && !isLabelerDefault)
              ? boundThreadSummaryName(shortName, { max: TITLE_MAX_LEN })
              : t.title;
            const nextSummary = topicSummary || t.topicSummary;
            if (nextTitle !== t.title) {
              // This WAS the one name — close the gate before anything else can
              // write, including the heuristic if its deadline fires later.
              autoTitleSettledRef.current.add(targetThreadId);
              // Publish upward so every participant converges on this name.
              // Host decides whether this user has the authority to.
              try { onAutoTitleResolvedRef.current?.(targetThreadId, nextTitle); } catch { /* best-effort */ }
            }
            if (nextTitle === t.title && nextSummary === t.topicSummary) return t;
            // Personal names (2026-09-09): a replaced auto label is a FORMER name — kept for search only.
            return { ...t, title: nextTitle, topicSummary: nextSummary, ...(nextTitle !== t.title ? { titleHistory: appendFormerTitle(t.titleHistory, t.title, nextTitle) } : {}) };
          }));
          maybeLogThreadLabel(targetThreadId, 'updated', userCount, false);
        })
        .catch(() => {
          // Swallow — fire-and-forget. Because we did NOT advance the
          // per-thread fingerprint, the next user message will trigger a retry
          // (bounded by the attempt cap). The heuristic may take the one name in
          // the meantime — before 2026-09-18 it had already taken it on the
          // first token, which is the hop the settle policy removed.
          maybeLogThreadLabel(targetThreadId, 'error', userCount, false);
          releaseTitleSettle(targetThreadId);
        })
        .finally(() => {
          // Clean up the per-thread controller entry if it's still us.
          if (inferenceAbortByThreadRef.current.get(targetThreadId) === controller) {
            inferenceAbortByThreadRef.current.delete(targetThreadId);
            inferenceInFlightMsgIdRef.current.delete(targetThreadId);
          }
        });
    }, LABEL_INFER_DEBOUNCE_MS);
    labelDebounceTimerRef.current.set(activeThreadId, timer);

    // NOTE: We deliberately do NOT return a cleanup that aborts the
    // in-flight controller or clears the timer. This effect runs on every
    // messages change (including every assistant streaming-token tick),
    // and an indiscriminate cleanup would cancel cross-thread work on a
    // thread switch. The debounce map handles same-thread rescheduling
    // explicitly; same-thread aborts happen explicitly when a *new* user
    // message overrides an in-flight call. Unmount-time cleanup is
    // handled by a separate effect below.
    // `autoTitleSuppressed` is a stable useCallback([]) reading through a ref,
    // so listing it cannot re-fire (and re-infer) this effect.
  }, [enabled, ready, activeThreadId, messages, autoTitleSuppressed, titleSettleOnce, releaseTitleSettle]);

  // Abort any in-flight thread-label inferences and cancel pending
  // debounce timers on unmount only.
  useEffect(() => {
    const aborts = inferenceAbortByThreadRef.current;
    const timers = labelDebounceTimerRef.current;
    const inFlight = inferenceInFlightMsgIdRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
      aborts.forEach(c => c.abort());
      aborts.clear();
      inFlight.clear();
    };
  }, []);

  // ── Bug G backfill (Miles 2026-05-11) ────────────────────────────
  //
  // Re-translate auto-generated thread titles whose detected language
  // doesn't match the user's UI language. This catches threads that
  // were created on a previous session where the title summarizer was
  // pinned to the conversation-content language instead of the UI
  // language, and would otherwise leave a Spanish chip in an English
  // session (or vice-versa) until the user sent another message.
  //
  // 2026-07-31 flood-title healing rides the same pass: threads whose
  // title is the flood-era 'Group thread' clobber fingerprint (and not
  // user-renamed) with a real local transcript re-title through the same
  // labeler pipeline, bounded to the most-recent
  // FLOOD_TITLE_BACKFILL_MAX_PER_PASS per pass (see
  // selectTitleBackfillCandidates).
  //
  // Discipline:
  //   • Skip user-renamed titles (`userRenamed === true`).
  //   • Skip titles whose language is `'unknown'` (don't guess).
  //   • Skip the active thread — the live inference effect already
  //     owns it; firing a second inference here would race.
  //   • Run at most ONE backfill per (instanceId + uiLanguage) so a
  //     thread that the labeller couldn't fix stays quiet until the
  //     UI language changes again.
  //   • Per-thread inference is gated on the same
  //     `inferenceAbortByThreadRef` map the live effect uses, so a
  //     concurrent live update wins automatically.
  // ────────────────────────────────────────────────────────────────────────────
  const lastBackfilledUiLanguageRef = useRef<UiLanguage | null>(null);
  useEffect(() => {
    if (!enabled || !ready) return;
    if (typeof window === 'undefined') return;
    const uiLanguage = getUserUiLanguage();
    if (lastBackfilledUiLanguageRef.current === uiLanguage) return;

    // Find candidate threads (pure core — see selectTitleBackfillCandidates):
    //   • Bug G: auto-generated, language-mismatched titles;
    //   • flood-title (2026-07-31): 'Group thread' clobber fingerprints,
    //     bounded to the most-recent FLOOD_TITLE_BACKFILL_MAX_PER_PASS.
    // Both classes: not the active thread, never user-renamed, and at least
    // one user message in the on-disk per-thread snapshot to feed the
    // labeller. The loop below stays strictly serial (one awaited call at a
    // time), so a burst of qualifying threads can never storm the labeler.
    const candidates = selectTitleBackfillCandidates({
      threads,
      activeThreadId,
      uiLanguage,
      readStoredMessages: (threadId) =>
        getFromLocalStorage<ChatMessage[]>(threadMsgKey(instanceId, threadId, currentUid), []),
    });

    if (candidates.length === 0) {
      lastBackfilledUiLanguageRef.current = uiLanguage;
      return;
    }

    // Mark this UI language as backfilled BEFORE firing inferences
    // so a fast re-mount can't double-fire the pass.
    lastBackfilledUiLanguageRef.current = uiLanguage;

    let cancelled = false;
    void (async () => {
      for (const { thread, messages: stored } of candidates) {
        if (cancelled) return;
        // Skip if a live inference is already in flight for this
        // thread — the live effect wins.
        if (inferenceAbortByThreadRef.current.has(thread.id)) continue;
        const controller = new AbortController();
        inferenceAbortByThreadRef.current.set(thread.id, controller);
        try {
          maybeLogThreadLabel(thread.id, 'backfill-ui-language', stored.length, true);
          // The thread's OWN workspace binding first: this loop walks unfiltered
          // threads, so a thread born in another organization is a candidate while
          // this one is active — declaring the active workspace would bill org A for
          // org B's label. The backfill may also run before any workspace resolves;
          // then no account is sent and the fleet proxy's recorder stays loud rather
          // than mis-filing (2026-09-07).
          const result = await inferThreadLabel(stored, uiLanguage, {
            signal: controller.signal,
            sessionId: deterministicThreadSessionId(thread.id),
            accountId: thread.accountId ?? getActiveWorkspaceAccount() ?? undefined,
          });
          if (cancelled || controller.signal.aborted) continue;
          const { shortName, topicSummary } = result;
          if (!shortName && !topicSummary) {
            maybeLogThreadLabel(thread.id, 'backfill-empty-result', stored.length, false);
            continue;
          }
          setThreads(prev => prev.map(t => {
            if (t.id !== thread.id) return t;
            // Re-check ownership at apply time — the user may have renamed the
            // thread while the backfill awaited.
            if (titleIsUserOwned(t)) return t;
            // Same authority rule as the live labeler: a shared thread this
            // user did not create keeps its creator's canonical name — and the
            // same one-name settle, so a backfill label cannot be a thread's
            // second automatic name either (2026-09-18).
            const titleLocked = autoTitleSuppressed(thread.id)
              || (titleSettleOnce && autoTitleSettledRef.current.has(thread.id));
            const nextTitle = (!titleLocked && shortName) ? boundThreadSummaryName(shortName, { max: TITLE_MAX_LEN }) : t.title;
            const nextSummary = topicSummary || t.topicSummary;
            if (nextTitle !== t.title) {
              // That was this thread's one automatic name (2026-09-18).
              autoTitleSettledRef.current.add(thread.id);
              try { onAutoTitleResolvedRef.current?.(thread.id, nextTitle); } catch { /* best-effort */ }
            }
            if (nextTitle === t.title && nextSummary === t.topicSummary) return t;
            // Personal names (2026-09-09): a replaced auto label is a FORMER name — kept for search only.
            return { ...t, title: nextTitle, topicSummary: nextSummary, ...(nextTitle !== t.title ? { titleHistory: appendFormerTitle(t.titleHistory, t.title, nextTitle) } : {}) };
          }));
          maybeLogThreadLabel(thread.id, 'backfill-updated', stored.length, false);
        } catch {
          maybeLogThreadLabel(thread.id, 'backfill-error', stored.length, false);
        } finally {
          if (inferenceAbortByThreadRef.current.get(thread.id) === controller) {
            inferenceAbortByThreadRef.current.delete(thread.id);
          }
        }
      }
    })();

    return () => { cancelled = true; };
    // We INTENTIONALLY do not depend on `threads` here — the backfill
    // is one-shot per (instance, uiLanguage). If we depended on
    // `threads` the effect would re-fire after every chip update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ready, instanceId, currentUid, activeThreadId]);

  // ── Actions ─────────────────────────────────────────────────────────────

  /**
   * Internal: snapshot the outgoing thread's messages before a switch.
   *
   * PR #97 fix: prefer the synchronous `getLatestMessages` accessor over
   * the React-effect-tracked `latestMessagesRef` when it's available.
   * The effect-tracked ref lags one commit behind dispatches that just
   * happened in the same tick (Bug #1 — messages disappearing on rapid
   * send+switch), so reading from the sync accessor captures the
   * post-dispatch messages array. Falls back to the effect-tracked ref
   * for tests / non-bridged callers.
   */
  const snapshotActive = useCallback((threadId: string | null) => {
    if (!threadId) return;
    const sync = getLatestMessagesRef.current;
    const messagesToSnapshot = sync ? sync() : latestMessagesRef.current;
    safelyPersistThreadMessages({
      instanceId,
      threadId,
      uid: currentUid,
      messages: messagesToSnapshot,
      source: 'snapshot-active',
      writerToken: threadSyncWriterTokenRef.current,
    });
  }, [instanceId, currentUid]);

  /**
   * Internal: stamp the OUTGOING thread's record with the live pane state
   * before switching away (mirror-lag catch-up, Miles 2026-07-12).
   *
   * The record's `messageCount`/title are mirrored from live state one commit
   * AFTER a message lands, and the mirror effect only ever touches the ACTIVE
   * thread — so switching away inside that window (via + or a chip click)
   * strands the outgoing record at `messageCount: 0` + default title forever.
   * A stranded record is a live bug factory: findReusableEmptyThread would
   * hand it back on the next + (cross-threading, one press later), and
   * shouldDeferServerCreate would keep a content-bearing conversation off the
   * server. Catch the record up synchronously at the switch boundary, exactly
   * mirroring what the mirror effect would have written.
   */
  const catchUpOutgoingThreadRecord = useCallback((
    outgoingId: string | null,
    liveMessagesArg?: ChatMessage[] | null,
  ) => {
    if (!outgoingId) return;
    const sync = getLatestMessagesRef.current;
    const live = Array.isArray(liveMessagesArg)
      ? liveMessagesArg
      : (sync ? sync() : latestMessagesRef.current);
    if (!Array.isArray(live) || live.length === 0) return;
    // SETTLED-ONLY count (owner feedback, Miles 2026-08-01): the catch-up
    // mirrors what the mirror effect would have written, and the mirror now
    // stamps the settled count — so the caught-up guard and the stamp both
    // use the settled length (an outgoing thread's in-flight placeholder
    // must not bump its record count either).
    const liveSettledCount = settledMessageCount(live);
    if (liveSettledCount === 0) return;
    // THE SWITCH BOUNDARY IS THE OUTGOING THREAD'S LAST CHANCE (2026-09-18).
    //
    // The mirror effect runs for the ACTIVE thread only, so a thread left
    // holding its placeholder would keep it until somebody opened it again —
    // and the label lane may already have missed its window. "the old record
    // reflects its content" is an invariant the lifecycle suite has asserted
    // since this catch-up existed, and the rail reading "New thread" on a
    // conversation you just typed in is the same complaint from the other end.
    //
    // So the placeholder is filled HERE and stamped as this thread's ONE
    // automatic name, which is what keeps a label landing a second later from
    // being a second change. The exception is a SUPPRESSED labeler: a
    // participant in someone else's shared thread is named by its creator's
    // canonical title, and a local heuristic fill would show one conversation
    // under a different name to each person.
    const settleReleased = !autoTitleSuppressed(outgoingId);
    setThreads(prev => {
      let updated: ChatThread | null = null;
      let filledThePlaceholder = false;
      const next = prev.map(t => {
        if (t.id !== outgoingId) return t;
        if ((t.messageCount || 0) >= liveSettledCount) return t; // record already caught up
        const nextTitle = resolveMirrorThreadTitle(t, live.find((m) => m.isUser) ?? null, settleReleased);
        if (nextTitle !== t.title && isPlaceholderThreadTitle(t.title) && !isPlaceholderThreadTitle(nextTitle)) {
          filledThePlaceholder = true;
        }
        updated = {
          ...t,
          // LITERALLY what the mirror effect would have written (2026-09-18):
          // the same function, so the promise this comment has always made can
          // no longer drift.
          title: nextTitle,
          messageCount: liveSettledCount,
          // Catch the user-content flag up alongside the count, exactly like
          // the mirror effect would have (a lagging record must not keep a
          // typed-in thread classified as an empty draft).
          hasUserContent: threadHasUserContent(live),
          // 2026-07-31 multi-device directive: same CONVERGENT derivation as
          // the mirror effect — the catch-up mirrors what the mirror would
          // have written, and a wall-clock stamp here would reintroduce the
          // divergent-stamp leapfrog on the switch boundary.
          lastActiveAt: deriveConvergentLastActiveAt(t.lastActiveAt, live),
        };
        return updated;
      });
      if (titleSettleOnce && filledThePlaceholder) autoTitleSettledRef.current.add(outgoingId);
      if (updated) writeThreadToServer(updated);
      return updated ? next : prev;
    });
  }, [writeThreadToServer, titleSettleOnce, autoTitleSuppressed]);

  /**
   * NAVIGATION HYDRATION (routine list parity, 2026-08-02). Opening a
   * routine from the rail/manager can target a thread OUTSIDE the capped
   * listen window: switchThread activates the id and hydrates its transcript
   * (hydrateThreadMessagesFromServerIfEmpty), but with no LOCAL THREAD
   * RECORD there was no rail row and no header title — and nothing else in
   * the app fetches a single index doc on demand. Fetch the doc
   * (getChatThread, through the shared parser so mirror fields flow) and
   * insert it LOCAL-ONLY:
   *   • found & not archived → the real record (unread cleared, viewed now);
   *   • missing doc          → a minimal record titled by `fallbackTitle`
   *     (the routine's name) so the destination stays coherent.
   * Nothing is written back, so the server copy is untouched either way.
   * Reconcile keeps the record while the thread is ACTIVE (the explicit
   * activeThreadId exemption in reconcileThreadListsWithServerSnapshot);
   * the insert re-checks both activity and absence when the fetch resolves
   * so a racing snapshot/switch can never be clobbered or duplicated.
   *
   * ARCHIVED / TOMBSTONED → NO RECORD AT ALL (adversarial review finding 4,
   * 2026-08-03). The original code minted the SAME minimal record for a
   * missing doc and for an ARCHIVED one, and consulted neither
   * `archivedTombstonesRef` nor `isThreadTombstonedInStorage`. That record is
   * inserted into `threads` and persisted to localStorage by the list-persist
   * effect — so a thread the user had just archived came back as a rail row
   * titled with the routine name. (Firestore stayed clean: writeThreadToServer's
   * tombstone guard holds and non-create upserts omit `archived`. This was
   * purely LOCAL/UI resurrection — precisely what tombstones exist to prevent.)
   * Both cases now early-return: navigation may still land on the transcript,
   * it simply must not mint a rail row. The not-archived hydration path below
   * is unchanged, including its no-clobber / no-duplicate / no-write-back
   * guarantees.
   */
  const hydrateMissingThreadRecord = useCallback(async (
    threadId: string,
    fallbackTitle?: string,
  ): Promise<void> => {
    // Locally archived here (in-memory map or the durable persisted map — the
    // latter also covers another tab's archive and a reload). Checked BEFORE
    // the fetch: no reason to read a doc we will refuse to mint from.
    if (
      archivedTombstonesRef.current.has(threadId)
      || isThreadTombstonedInStorage(instanceId, currentUid, threadId)
    ) {
      return;
    }
    let fetched: ChatThread | null = null;
    const uid = getServerSyncUid();
    if (uid) {
      try {
        fetched = await getChatThread(uid, threadId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('chatThreadIndex.getChatThread navigation hydrate failed', { threadId, error });
        fetched = null;
      }
    }
    // Archived server-side → no rail row (see the finding 4 note above).
    //
    // THE ONE CASE THE DOOR CANNOT CATCH, and it is announced here rather than
    // left silent (2026-09-21). canOpenThread refuses from facts this browser
    // already holds — a tombstone, or a local record saying archived — which
    // is what keeps it synchronous and free. A thread archived for everyone
    // while this browser was closed has NEITHER: the rail query is
    // `archived == false` so there is no local record, and the tombstone that
    // the live remote-archive probe would have written expired or was never
    // written. Only the server knows, and it is this read that asks it.
    //
    // So: announce, never bounce. The navigation has already landed by the
    // time this resolves, and yanking it back is the flash-then-yank the door
    // lives at the top of switchThread specifically to avoid. A toast saying
    // what happened beats an empty pane that explains nothing.
    if (fetched && fetched.archived === true) {
      beginThreadArchiveNotice({ kind: 'refused', threadId, threadTitle: fetched.title || '' });
      return;
    }
    // Re-check the tombstone after the await: the user can archive the thread
    // while the single-doc read is in flight.
    if (
      archivedTombstonesRef.current.has(threadId)
      || isThreadTombstonedInStorage(instanceId, currentUid, threadId)
    ) {
      return;
    }
    // FOREIGN-THREAD GUARD, async half (multi-org separation round 2,
    // 2026-08-19): a server-only row (no local record) reveals its workspace
    // binding only via this fetch. A foreign binding routes to the host's
    // sealed switch flow instead of minting a local record in the wrong
    // workspace; the momentary empty pane is immediately covered by the
    // transition overlay.
    {
      const activeWorkspaceForGuard = groupReadAccountId ?? getActiveWorkspaceAccount();
      if (
        onForeignThreadSwitch
        && fetched?.accountId
        && activeWorkspaceForGuard
        && fetched.accountId !== activeWorkspaceForGuard
      ) {
        onForeignThreadSwitch(fetched);
        return;
      }
    }
    // Only insert while the user is still ON the thread — reconcile keeps
    // active-thread records; a since-abandoned hydrate must not add rows.
    if (latestActiveThreadIdRef.current !== threadId) return;
    const nowIso = new Date().toISOString();
    const hydrateWorkspace = groupReadAccountId ?? getActiveWorkspaceAccount();
    const record: ChatThread = fetched
      ? { ...fetched, unreadCount: 0, lastViewedAt: nowIso }
      : {
          id: threadId,
          title: (fallbackTitle || '').trim() || 'Routine',
          createdAt: nowIso,
          lastActiveAt: nowIso,
          lastViewedAt: nowIso,
          unreadCount: 0,
          isProcessing: false,
          messageCount: 0,
          // 2026-08-20 churn audit: this synth was the hook's only
          // hasUserContent:undefined producer, and with no accountId the row
          // could never render in an org rail. Bind to the workspace the
          // user is opening it in (the foreign guard above already vetoed
          // records bound elsewhere).
          hasUserContent: false,
          ...(hydrateWorkspace ? { accountId: hydrateWorkspace } : {}),
        };
    if (!fetched) {
      // 2026-08-22 (verified regression follow-up): the synth's binding above
      // is a GUESS — the server holds no record, and during an identity blip
      // getActiveWorkspaceAccount() can answer with the WRONG workspace. The
      // workspace-containment effect would then close this ACTIVE pane out
      // from under the user the moment identity settles ("conversation
      // vanished"). Exempt the synth exactly like a session-minted thread:
      // it stays visible in the org rail (the reason the stamp exists) and
      // the pane can never be yanked over a binding this code invented. The
      // foreign-thread guards above still veto SERVER-confirmed foreign
      // records — those are real bindings, not guesses.
      sessionMintedThreadIdsRef.current.add(threadId);
    }
    setThreads((prev) => (prev.some((t) => t.id === threadId) ? prev : [...prev, record]));
  }, [getServerSyncUid, instanceId, currentUid, groupReadAccountId, onForeignThreadSwitch]);

  /**
   * THE DOOR (2026-09-21 owner directive). Can this person still open this
   * thread at all?
   *
   * Every navigating surface in the app funnels through switchThread — the
   * rail, search, a mention row, a bell row, the deep-link consumer — so one
   * check here closes the dead end for all of them at once, including the ones
   * nobody has written yet. The mentions tab and the bell refuse the press
   * BEFORE it happens, which is the experience; this is the belt behind them.
   *
   * It FAILS OPEN, like every other step of this feature and for the same
   * reason: a thread the store has not loaded yet has no local record, and
   * refusing it would break opening a thread from a cold start. Only two
   * things refuse — a durable archive tombstone, and a local record that says
   * `archived` — and both are facts this browser already holds, so the check
   * costs nothing and cannot be wrong in the direction that strands somebody.
   */
  const canOpenThread = useCallback((threadId: string): boolean => {
    const id = String(threadId || '').trim();
    if (!id) return false;
    if (isThreadTombstoned(id)) return false;
    return latestThreadsRef.current.find((t) => t.id === id)?.archived !== true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThreadTombstoned]);

  const switchThread = useCallback((targetId: string, options?: SwitchThreadOptions) => {
    if (!enabled) return;
    if (targetId === activeThreadId) return;
    // THE DOOR, before any state mutation (2026-09-21). Deliberately NOT in
    // hydrateMissingThreadRecord: by the time that runs switchThread has
    // already set activeThreadIdRef, dispatched SET_MESSAGES from storage and
    // fired the hydrate, so a bounce there is a flash-then-yank; it only runs
    // when there is NO local record, which is simultaneously too narrow for
    // the goal and too broad for safety; and it early-returns on the tombstone
    // map before its own fetch, so the commonest case — "I archived this ten
    // minutes ago and clicked an old link" — never reaches its archived branch.
    //
    // The refusal is ANNOUNCED, never silent: a press that appears to do
    // nothing is the failure this replaces. The words say only what is true of
    // every way a thread closes to one person, because a ChatThread record
    // carries `archived` and no attribution — see threadClosureCopy's
    // `unknown` case, which is exactly this situation.
    if (!canOpenThread(targetId)) {
      beginThreadArchiveNotice({
        kind: 'refused',
        threadId: targetId,
        threadTitle: latestThreadsRef.current.find((t) => t.id === targetId)?.title || '',
      });
      return;
    }
    // CENTRAL FOREIGN-THREAD GUARD (multi-org separation round 2,
    // 2026-08-19): a target bound to ANOTHER workspace never activates in
    // place — the host's sealed switch flow takes over (invariant 1: one
    // workspace at a time). See the onForeignThreadSwitch option doc.
    const activeWorkspaceForGuard = groupReadAccountId ?? getActiveWorkspaceAccount();
    const targetRecord = latestThreadsRef.current.find((t) => t.id === targetId) || null;
    if (
      onForeignThreadSwitch
      && targetRecord?.accountId
      && activeWorkspaceForGuard
      && targetRecord.accountId !== activeWorkspaceForGuard
    ) {
      onForeignThreadSwitch(targetRecord);
      return;
    }
    // 1. Snapshot outgoing thread (messages → storage, record → catch-up).
    snapshotActive(activeThreadId);
    catchUpOutgoingThreadRecord(activeThreadId);
    // 2. Load target thread's stored messages.
    const loaded = getFromLocalStorage<ChatMessage[]>(threadMsgKey(instanceId, targetId, currentUid), []);
    // Rehydrate Date objects — timestamps roundtrip as ISO strings in storage.
    // Strip dead `attachments: [{}, {}]` File shells that pre-fix snapshots
    // may carry: `JSON.stringify(File)` is `{}`, and the renderer's
    // `hasFiles` check used to treat truthy length as "render File
    // objects" — shadowing the durable `attachmentsSerialized` fallback
    // and producing the empty-bubble repro on thread-switch (Miles
    // 2026-05-11, thread `C0AM8U804KZ/1778281880.499349`).
    const normalizedLoaded = loaded.map(normalizeTimestamp);
    // LIVE-TURN SAFE (owner report 2026-08-20, "the spinning widget
    // disappeared when I left the thread and came back"): a same-session
    // switch is NOT a hydrate boundary — the interrupted-turn reconciler's
    // own contract says it may only run where no live client run can exist,
    // and every sibling live-sync path already opts out. Running it here
    // rewrote a LIVE pending widget (taskless windows: pre-task-queue wait,
    // the connect pre-route) into a dead Interrupted card and persisted the
    // loss. Refresh-orphans still heal at the true boundaries (boot hydrate)
    // and via the server-backed reconcile below.
    const rehydrated = prepareRehydratedMessages(normalizedLoaded, { reconcileInterrupted: false });
    dispatch({ type: 'SET_MESSAGES', payload: rehydrated });
    // 3. Mark active. Since PR-3 (2026-09-08) opening a thread does NOT
    //    zero its count or stamp lastViewedAt — markThreadViewed does, once
    //    the thread has been on screen for the 3 s dwell (useThreadViewDwell).
    activeThreadIdRef.current = targetId;
    setActiveThreadId(targetId);
    // Same rule for the server-backed replay poll: it deliberately admits a
    // healthy pending+serverTaskId row, so against a LIVE turn it is a
    // second uncoordinated writer for ~2.5 minutes. The registry keys on
    // TASK ids — derive the thread's live task from its own messages; a
    // stale prior-session thread has no registry entry and still heals here.
    const targetLiveTaskId = liveServerTaskIdOf(normalizedLoaded);
    if (!targetLiveTaskId || !isLiveSendInFlight(targetLiveTaskId)) {
      void reconcileInterruptedServerBackedMessages(targetId, normalizedLoaded, rehydrated).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('chatThreadMessages.reconcile interrupted switchThread failed', error);
      });
    }
    // QUOTA-DURABLE REHYDRATE (owner report 2026-08-19 "messages start
    // disappearing"): under localStorage quota pressure the persist layer
    // falls back to the IDB mirror — but this read was LS-only, so a
    // quota-starved profile opened threads empty/short while the fuller
    // copy sat in IDB. rehydrateThreadMessages (built for exactly this,
    // previously unwired) picks the better mirror; when it beats what we
    // painted, swap it in (only while this thread is still active) and
    // repair the LS copy.
    void rehydrateThreadMessages<ChatMessage>({ instanceId, threadId: targetId, uid: currentUid })
      .then((better) => {
        if (!better || better.length <= loaded.length) return;
        if (activeThreadIdRef.current !== targetId) return;
        const normalizedBetter = better.map(normalizeTimestamp);
        // Same live-turn safety as the paint above.
        dispatch({ type: 'SET_MESSAGES', payload: prepareRehydratedMessages(normalizedBetter, { reconcileInterrupted: false }) });
        setToLocalStorage(threadMsgKey(instanceId, targetId, currentUid), better);
      })
      .catch(() => { /* best-effort — the LS paint above stands */ });
    if (loaded.length === 0) {
      // Status-wrapped hydrate (never rejects): failures surface as
      // retry-able state for the empty pane.
      void runActiveThreadHydrate(targetId);
    }
    // NAVIGATION HYDRATION (routine list parity, 2026-08-02): an id with no
    // local record (server-only routine row) gets its record fetched and
    // inserted so the rail row + header title work — see the helper's doc.
    if (!latestThreadsRef.current.some((t) => t.id === targetId)) {
      void hydrateMissingThreadRecord(targetId, options?.fallbackTitle);
    }
    writeActiveThreadToServer(targetId);
  }, [
    enabled,
    activeThreadId,
    snapshotActive,
    catchUpOutgoingThreadRecord,
    instanceId,
    currentUid,
    dispatch,
    writeActiveThreadToServer,
    runActiveThreadHydrate,
    hydrateMissingThreadRecord,
    reconcileInterruptedServerBackedMessages,
    activeThreadIdRef,
    groupReadAccountId,
    onForeignThreadSwitch,
    canOpenThread,
  ]);

  /**
   * ROUTINE HANDOFF AUTO-NAVIGATION (Routine Threads plan §Frontend.6,
   * 2026-07-31). When the agent creates a routine in an OCCUPIED thread, the
   * server mints a NEW routine thread (seeded with a welcome message) and
   * stamps it `routineHandoffFromThreadId` = the origin thread +
   * `routineHandoffAt` = now. The user asked from the origin thread and is
   * still looking at it, so when that thread's index snapshot arrives here we
   * take them to the new routine thread automatically — exactly once:
   *   • only while the viewer is STILL on the origin thread (they navigated
   *     away → respect it; the new thread stays in the rail);
   *   • only within ROUTINE_HANDOFF_WINDOW_MS of the stamp AND with no
   *     `routineHandoffAckAt` — together these make a hydrate/reload long
   *     after the fact (or a second device days later) a no-op;
   *   • ref-guarded per thread id for THIS session, so the effect re-running
   *     on snapshot churn before the ack write echoes back cannot re-switch.
   * After switching, `acknowledgeRoutineHandoff` persists the ack (merge
   * write) so every other device/session sees the handoff as consumed.
   */
  const consumedRoutineHandoffIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!enabled || !ready) return;
    const uid = getServerSyncUid();
    if (!uid) return;
    const activeId = activeThreadId;
    if (!activeId) return;
    const nowMs = Date.now();
    for (const thread of threads) {
      if (!thread.routineHandoffFromThreadId) continue;
      if (thread.routineHandoffFromThreadId !== activeId) continue;
      if (thread.id === activeId) continue;
      if (thread.routineHandoffAckAt) continue;
      if (thread.archived === true) continue;
      const handoffMs = thread.routineHandoffAt ? new Date(thread.routineHandoffAt).getTime() : NaN;
      if (!Number.isFinite(handoffMs)) continue;
      if (nowMs - handoffMs > ROUTINE_HANDOFF_WINDOW_MS) continue; // stale — never yank
      if (consumedRoutineHandoffIdsRef.current.has(thread.id)) continue;
      consumedRoutineHandoffIdsRef.current.add(thread.id);
      switchThread(thread.id);
      void acknowledgeRoutineHandoff(uid, thread.id).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('chatThreadIndex.acknowledgeRoutineHandoff failed', { threadId: thread.id, error });
      });
      break; // at most one navigation per snapshot
    }
  }, [enabled, ready, threads, activeThreadId, switchThread, getServerSyncUid]);

  /**
   * STALE-DRAFT TIDY (owner directive 2026-08-22 — "the dead thread issue
   * keeps coming back until I clear all the threads"): auto-archive aged
   * empty drafts that the reuse scan skipped. Same idiom as the per-chip
   * archive and resetAllThreads: tombstone first (blocks instant
   * re-adoption), durable-persist the tombstones, server-archive real
   * records (a never-server-created deferred draft needs no server write),
   * drop the local transcript cache, filter the rail. Archive is soft and
   * reversible — nothing is deleted. The caller mints/activates the fresh
   * draft BEFORE this runs, so the user never sees an empty rail.
   */
  const tidyStaleEmptyDrafts = useCallback((stale: ReadonlyArray<ChatThread>): void => {
    if (stale.length === 0) return;
    const ids = stale.map((t) => t.id);
    // eslint-disable-next-line no-console
    console.warn('[useChatThreads] auto-archiving stale empty draft(s) — "+" never resurrects an aged blank thread', { ids });
    for (const id of ids) {
      archivedTombstonesRef.current.set(id, Date.now() + ARCHIVED_TOMBSTONE_TTL_MS);
    }
    persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
    const uid = getServerSyncUid();
    for (const t of stale) {
      if (uid && !pendingServerCreateIdsRef.current.has(t.id)) {
        void archiveChatThread(uid, instanceId, t.id).catch((error) => {
          // eslint-disable-next-line no-console
          console.warn('chatThreads.tidyStaleEmptyDrafts server archive failed', { threadId: t.id, error });
        });
      }
      pendingServerCreateIdsRef.current.delete(t.id);
      try { removeFromLocalStorage(threadMsgKey(instanceId, t.id, currentUid)); } catch { /* ignore */ }
    }
    const idSet = new Set(ids);
    setThreads((prev) => prev.filter((t) => !idSet.has(t.id)));
  }, [instanceId, currentUid, getServerSyncUid]);

  const newThread = useCallback((): string => {
    if (!enabled) return '';
    // SINGLE-EMPTY invariant (bootstrap dedupe): never mint a second empty
    // default thread — reuse the existing one. If the ACTIVE thread is already
    // an empty default, "+" is a focus no-op; if an empty default exists
    // elsewhere in the list, activate it. Every creation path funnels through
    // here (+ buttons, avatar-bridge stale-id recovery), so this guard is the
    // client-side end of the five-chip fix. The LIVE pane count (not the
    // record's lagging mirror) decides whether the ACTIVE thread is genuinely
    // empty — see findReusableEmptyThread's mirror-lag guard. USER messages
    // only: a seeded greeting in the pane must not disqualify the draft from
    // reuse (else + would mint unbounded greeted threads — five-chip class).
    const liveMessages = getLatestMessagesRef.current ? getLatestMessagesRef.current() : null;
    const liveUserMessageCount = Array.isArray(liveMessages)
      ? liveMessages.reduce((n, m) => n + (m && m.isUser ? 1 : 0), 0)
      : 0;
    // WORKSPACE-SCOPED CANDIDATES (2026-08-20 owner repro: + silently
    // activated a stale draft the org rail does not even show). Reuse scans
    // only threads VISIBLE in this workspace by the rail's own rule —
    // bound-here, unbound-in-home — plus this session's own mints (being
    // bound-at-birth here while the module mirror publishes). The M9 guard
    // inside findReusableEmptyThread stays as the second fence.
    const wsForReuse = groupReadAccountId ?? getActiveWorkspaceAccount();
    const reuseCandidates = latestThreadsRef.current.filter((t) =>
      threadAllowedInWorkspace(t, wsForReuse, currentUid)
      || sessionMintedThreadIdsRef.current.has(t.id));
    // STALE-DRAFT RULE (2026-08-22): aged empties are skipped by the scan and
    // collected here; every exit path below tidies them away (auto-archive)
    // AFTER the fresh draft is active — the recurrence engine ("+" handing
    // back yesterday's wedged blank thread forever) is structurally gone.
    const staleEmptiesOut: ChatThread[] = [];
    const reusable = findReusableEmptyThread(reuseCandidates, {
      activeThreadId: latestActiveThreadIdRef.current,
      activeThreadLiveMessageCount: liveUserMessageCount,
      nowMs: Date.now(),
      staleEmptiesOut,
      // M9 (INCIDENT-20260818): never reuse a draft bound to another org.
      // #925 addition: groupReadAccountId first — it is seeded from the
      // confirmed-workspace cache at boot, so the guard also holds during
      // the frames before the module publish lands.
      activeWorkspaceAccountId: groupReadAccountId ?? getActiveWorkspaceAccount(),
      // POLLUTED-DRAFT GUARD (owner incognito repro 2026-08-16): a
      // "user-empty" default thread whose transcript holds any NON-welcome
      // assistant content (a misdelivered answer, an Interrupted card, a
      // system notice) is a conversation, not a fresh draft — "+" must mint
      // a genuinely new thread instead of dropping the user back into it.
      // Active pane reads live state (mirror-lag guard discipline); other
      // candidates read their stored transcript.
      threadHasForeignContent: (threadId) => {
        const candidateMessages =
          threadId === latestActiveThreadIdRef.current && Array.isArray(liveMessages)
            ? liveMessages
            : getFromLocalStorage<ChatMessage[]>(threadMsgKey(instanceId, threadId, currentUid), []);
        return threadHasForeignAssistantContent(candidateMessages);
      },
    });
    if (reusable) {
      if (reusable.id === latestActiveThreadIdRef.current) {
        // + on the already-active empty draft is a focus no-op for the LIST,
        // but not for the PANE (2026-08-20 owner repro: "+ in an org showed
        // no greeting"): the greet-on-focus driver only re-fires on
        // dependency changes and + changes none — seed here, idempotently
        // (store-guarded, rank-gated, never-seed-blind inside the seeder).
        void seedGreetingIntoEmptyActiveThread(reusable.id).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadMessages.greet reuse-noop failed', error);
        });
        tidyStaleEmptyDrafts(staleEmptiesOut);
        return reusable.id;
      }
      switchThread(reusable.id);
      // A REUSED draft opens greeted like a minted one. The driver usually
      // covers this on the active-id change; the direct call closes the
      // gaps where it cannot (same idempotent seeder).
      void seedGreetingIntoEmptyActiveThread(reusable.id).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('chatThreadMessages.greet reuse-switch failed', error);
      });
      tidyStaleEmptyDrafts(staleEmptiesOut);
      return reusable.id;
    }
    // 1. Snapshot outgoing thread (messages → storage, record → catch-up so
    //    the outgoing conversation can never be mistaken for an empty draft).
    snapshotActive(activeThreadId);
    catchUpOutgoingThreadRecord(latestActiveThreadIdRef.current ?? activeThreadId, liveMessages);
    // 2. Create thread record. Bug G (Miles 2026-05-11): the
    //    localized default title ("New thread" / "Nuevo hilo") must
    //    follow the user's UI language, not the language of the
    //    outgoing thread's content. The chip list belongs to the UI,
    //    not to any individual conversation.
    const sync = getLatestMessagesRef.current;
    void sync; // kept for future use; outgoing-content lang no longer drives the default
    const lang = getUserUiLanguage();
    const created = createEmptyThread({
      title: localizedDefaultTitle(lang),
      // Stamped from birth so a seeded greeting (which bumps messageCount to
      // 1 via the mirror) can never flip the record into "has content" via
      // the legacy messageCount fallback.
      hasUserContent: false,
    });
    pendingServerCreateIdsRef.current.add(created.id);
    sessionMintedThreadIdsRef.current.add(created.id);
    // 3. Update list.
    //
    // NO EVICTION (2026-09-22). This used to drop every thread past a
    // 24-per-workspace cap on EVERY "+", and to delete the evicted threads'
    // local transcripts outright — a permanent, silent, local data loss
    // triggered by pressing a button, which the persist effect below then
    // wrote to localStorage AND the IDB mirror. It also destroyed exactly what
    // the window watermark and the load-older page exist to preserve, so the
    // three only work together.
    //
    // Nothing is lost by removing it: the cap's stated purpose — bounding
    // localStorage — is already served, and served better, by machinery
    // written for the job. `storageQuota.ts` evicts transcripts COLD and
    // LARGEST-FIRST, never touches protected keys, and self-heals on
    // QuotaExceededError; this cap evicted by recency of RECORD, could not see
    // a byte size, and could not be undone. A thread RECORD is ~400 bytes, so
    // 500 of them is ~200 KB against a multi-megabyte budget.
    setThreads(prev => [created, ...prev]);
    // 4. Seed the new thread's persisted message store. When the host wired a
    // greeting composer (personalized welcome, 2026-07-13) the thread OPENS
    // with the greeting as its persisted first assistant message — so it
    // stays in the thread across reloads; otherwise the legacy empty array.
    // The thread has no prior on-disk content, so the empty-over-non-empty
    // guard is a no-op here — we still route through
    // `safelyPersistThreadMessages` for consistency and to emit the
    // BroadcastChannel signal so other tabs can pick up the new thread
    // without polling. GREETING ≠ USER CONTENT: the record above carries
    // hasUserContent:false, so the greeted thread still counts as an EMPTY
    // draft (SINGLE-EMPTY reuse, LOCAL-UNTIL-TOUCHED deferred create,
    // one-click empty delete).
    const seedMessages = composeGreetingSeed();
    safelyPersistThreadMessages({
      instanceId,
      threadId: created.id,
      uid: currentUid,
      messages: seedMessages,
      source: 'new-thread',
      writerToken: threadSyncWriterTokenRef.current,
    });
    // 5. Show the seed (greeting or empty pane) in state + set active.
    //    Sync the active-thread ref BEFORE setActiveThreadId (matching
    //    switchThread @ ~1958 and removeThreadAndReturnFallback @ ~2171/2193).
    //    newThread was the only transition path that left the ref pointing
    //    at the outgoing thread until a later post-render effect bridged it.
    //    That desync window let in-flight async ops keyed on the OLD thread
    //    pass the `activeThreadIdRef.current !== threadId` guards (lines
    //    1167/1169/1187/1206) and write stale content + a stuck processing
    //    state into the freshly created thread (New-thread carryover repro).
    dispatch({ type: 'SET_MESSAGES', payload: seedMessages });
    activeThreadIdRef.current = created.id;
    setActiveThreadId(created.id);
    writeThreadToServer(created);
    writeActiveThreadToServer(created.id);
    // Fresh greeted draft is active — NOW tidy the aged blanks the scan
    // skipped (auto-archive; reversible; rail stays single-empty).
    tidyStaleEmptyDrafts(staleEmptiesOut);
    return created.id;
  }, [
    enabled,
    snapshotActive,
    catchUpOutgoingThreadRecord,
    composeGreetingSeed,
    activeThreadId,
    activeThreadIdRef,
    instanceId,
    currentUid,
    dispatch,
    switchThread,
    writeActiveThreadToServer,
    writeThreadToServer,
    groupReadAccountId,
    seedGreetingIntoEmptyActiveThread,
    tidyStaleEmptyDrafts,
  ]);

  // Remote-archive fallback (cross-window UX): when the ACTIVE thread is
  // archived from another window/device, the reconcile drops its chip but the
  // open pane would keep showing (and letting the user type into) an archived
  // conversation. Once the snapshot-absence is CONFIRMED as a real archive
  // (never on absence alone — working-set churn must not close a live
  // conversation), move the user off it exactly like a local archive would:
  // most-recent remaining thread, or a fresh one when none remain. Assigned
  // via ref because the subscription effect is declared above these callbacks.
  useEffect(() => {
    confirmRemoteArchiveRef.current = async (threadId: string) => {
      const uid = getServerSyncUid();
      if (!uid) return;
      const info = await getChatThreadArchiveInfo(uid, threadId).catch(
        () => ({ archived: false as const }),
      );
      if (!info.archived) return;
      if (latestActiveThreadIdRef.current !== threadId) return; // user already moved on
      // Block any queued local upsert from echoing the thread back.
      addArchivedTombstone(threadId);
      // WORKSPACE-SCOPED fallback (2026-08-20 churn audit): latestThreadsRef
      // is the CROSS-ORG store — picking its first row could hand a foreign
      // thread to switchThread, whose central guard then kicks off a sealed
      // workspace transition (a hard navigation) with no user action. Only
      // threads allowed in the CURRENT workspace are candidates; none left →
      // a fresh thread in this workspace, same as a local archive.
      const navigate = () => {
        // The user may have moved on during the hold; never navigate them a
        // second time off wherever they deliberately went.
        if (latestActiveThreadIdRef.current === threadId) {
          // The fallback is computed HERE, at fire time — not before the hold.
          // Five seconds is long enough for a bulk archive (which the
          // shared-archive event makes easy) to take out the thread that was
          // "next" when the hold started; a stale list would walk the viewer
          // straight into another archived, already-tombstoned conversation.
          const wsForFallback = getActiveWorkspaceAccount();
          const remaining = latestThreadsRef.current.filter(
            (t) => t.id !== threadId
              && !isThreadTombstoned(t.id)
              // …and not archived on its own record. Latent before the door
              // existed (this walked the viewer into an archived thread that
              // simply had no tombstone yet); now it is also what keeps the
              // door from stranding them ON the thread they were being moved
              // off, since a refused switch goes nowhere.
              && t.archived !== true
              && threadAllowedInWorkspace(t, wsForFallback, uid),
          );
          if (remaining.length > 0) {
            switchThread(remaining[0].id);
          } else {
            newThread();
          }
        }
        clearThreadArchiveNotice(threadId);
      };
      // Someone ELSE archived the thread this user is looking at (owner
      // directive 2026-08-26): hold five seconds under a clear full-screen
      // announcement — the workspace-change treatment — then take them to the
      // next best thread. Only the shared-archive fanout stamps an archiver;
      // the same user's OTHER device archiving (no stamp) keeps the immediate
      // silent hop, which for one person's own action is the right feel.
      const archiverName = (info.archivedByName || '').trim();
      const archivedByAnother = Boolean(
        (info.archivedBy || '').trim() && (info.archivedBy || '').trim() !== uid,
      );
      if (archivedByAnother) {
        const title = latestThreadsRef.current.find((t) => t.id === threadId)?.title || '';
        beginThreadArchiveNotice({
          kind: 'blocking',
          threadId,
          threadTitle: title,
          archivedByName: archiverName,
        });
        // The handle is tracked so teardown can cancel it. An uncancelled
        // 5s timer outlives the machinery that armed it: on a sign-out /
        // sign-in inside the hold, the stale closure (switchThread + uid from
        // the departing identity) would fire against the arriving one.
        if (archiveHoldTimerRef.current !== null) {
          window.clearTimeout(archiveHoldTimerRef.current);
        }
        archiveHoldTimerRef.current = window.setTimeout(() => {
          archiveHoldTimerRef.current = null;
          navigate();
        }, THREAD_ARCHIVE_NOTICE_HOLD_MS);
      } else {
        navigate();
      }
    };
    return () => {
      confirmRemoteArchiveRef.current = null;
      if (archiveHoldTimerRef.current !== null) {
        window.clearTimeout(archiveHoldTimerRef.current);
        archiveHoldTimerRef.current = null;
        // The hold is over without a navigation — drop the announcement so a
        // torn-down overlay cannot outlive its own machinery on screen.
        clearThreadArchiveNotice();
      }
    };
  }, [getServerSyncUid, addArchivedTombstone, switchThread, newThread, isThreadTombstoned]);

  // BOOT-DEDUPE adopt handler: when the first server snapshot replaces a
  // still-empty local bootstrap draft, activate the account's most recent
  // real thread (loads its conversation exactly like a user-initiated switch).
  useEffect(() => {
    adoptServerThreadRef.current = (threadId: string) => {
      switchThread(threadId);
    };
    return () => {
      adoptServerThreadRef.current = null;
    };
  }, [switchThread]);

  const removeThreadAndReturnFallback = useCallback((threadId: string): string | null => {
    const activeAtCall = latestActiveThreadIdRef.current;
    if (!enabled) return activeAtCall;
    if (!latestThreadsRef.current.some((t) => t.id === threadId)) {
      return activeAtCall;
    }

    // 1. Snapshot latest messages into per-thread key so the archive
    //    captures the most recent state even for the currently-active
    //    thread (latestMessagesRef may be newer than the last mirrored write).
    //    PR #97 — prefer sync accessor over effect-tracked ref.
    const syncForRemove = getLatestMessagesRef.current;
    if (threadId === activeAtCall) {
      safelyPersistThreadMessages({
        instanceId,
        threadId,
        uid: currentUid,
        messages: syncForRemove ? syncForRemove() : latestMessagesRef.current,
        source: 'remove-thread',
        writerToken: threadSyncWriterTokenRef.current,
      });
    }

    let fallbackThreadId: string | null = threadId === activeAtCall ? null : activeAtCall;
    let removed = false;
    let explicitFallbackDispatch: ChatMessage[] | null = null;
    let explicitFallbackOriginal: ChatMessage[] | null = null;
    let fallbackNeedsServerHydration = false;
    let emptyFallbackToCreate: ChatThread | null = null;

    // 2. Archive the thread's metadata + messages under THREAD_ARCHIVE so
    //    nothing is lost from general memory. Archive is newest-first.
    setThreads(prev => {
      // WORKSPACE-SCOPED FALLBACK (multi-org separation round 2, 2026-08-19):
      // never silently activate another workspace's thread after an archive.
      const planned = planThreadRemoval(
        prev,
        threadId,
        activeAtCall,
        groupReadAccountId ?? getActiveWorkspaceAccount(),
      );
      const target = planned.removedThread;
      if (!target) {
        fallbackThreadId = latestActiveThreadIdRef.current;
        return prev;
      }

      removed = true;
      const archived: ArchivedThread = {
        thread: target,
        messages: getFromLocalStorage<ChatMessage[]>(
          threadMsgKey(instanceId, threadId, currentUid),
          [],
        ),
        archivedAt: new Date().toISOString(),
      };
      const existing = getFromLocalStorage<ArchivedThread[]>(
        storageKeys.THREAD_ARCHIVE,
        [],
      );
      setToLocalStorage(
        storageKeys.THREAD_ARCHIVE,
        [archived, ...existing],
      );
      // BUG-THREAD-RESURRECTION: tombstone BEFORE the (debounced) server
      // archive so racing snapshots and queued upserts can't resurrect it.
      addArchivedTombstone(threadId);
      archiveThreadOnServer(threadId);

      // 3. Drop the thread from the visible list.
      const remaining = planned.remaining;

      // 4. If we removed the active one, pick a new active (most-recent)
      //    and load its messages; otherwise leave the active thread alone.
      if (threadId === activeAtCall) {
        const fallback = planned.fallbackThreadId
          ? remaining.find((thread) => thread.id === planned.fallbackThreadId) || null
          : null;
        if (fallback) {
          const loaded = getFromLocalStorage<ChatMessage[]>(
            threadMsgKey(instanceId, fallback.id, currentUid),
            [],
          );
          const normalizedLoaded = loaded.map(normalizeTimestamp);
          explicitFallbackOriginal = normalizedLoaded;
          explicitFallbackDispatch = prepareRehydratedMessages(normalizedLoaded);
          fallbackThreadId = fallback.id;
          if (loaded.length === 0) fallbackNeedsServerHydration = true;
        } else {
          // No threads left → spin up an empty, localized default. Bug
          // G: the localized default tracks the UI language, not the
          // archived thread's content language. The chip the user is
          // about to see is part of the UI; the UI's language wins.
          void syncForRemove; // archived-thread content no longer drives the default
          const lang = getUserUiLanguage();
          emptyFallbackToCreate = createEmptyThread({
            title: localizedDefaultTitle(lang),
            hasUserContent: false,
          });
          pendingServerCreateIdsRef.current.add(emptyFallbackToCreate.id);
          fallbackThreadId = emptyFallbackToCreate.id;
          // Keep `remaining` — with the workspace-scoped fallback it can
          // still hold OTHER workspaces' local records; the old bare
          // `[emptyFallbackToCreate]` return silently deleted them all.
          return [emptyFallbackToCreate, ...remaining];
        }
      }
      return remaining;
    });

    if (!removed) {
      return latestActiveThreadIdRef.current;
    }

    // 4b. Apply explicit state updates outside of the `setThreads` updater
    //     so React batches them predictably across parent (dispatch) and
    //     child (setActiveThreadId) boundaries without tearing.
    if (explicitFallbackDispatch !== null && fallbackThreadId) {
      dispatch({ type: 'SET_MESSAGES', payload: explicitFallbackDispatch });
      activeThreadIdRef.current = fallbackThreadId;
      setActiveThreadId(fallbackThreadId);
      if (explicitFallbackOriginal) {
        void reconcileInterruptedServerBackedMessages(
          fallbackThreadId,
          explicitFallbackOriginal,
          explicitFallbackDispatch,
        ).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('chatThreadMessages.reconcile interrupted fallback failed', error);
        });
      }
      writeActiveThreadToServer(fallbackThreadId);
      if (fallbackNeedsServerHydration) {
        // Status-wrapped hydrate (never rejects): failures surface as
        // retry-able state for the empty pane.
        void runActiveThreadHydrate(fallbackThreadId);
      }
    } else if (emptyFallbackToCreate) {
      const created = emptyFallbackToCreate as ChatThread;
      // ARCHIVE-ALWAYS (owner directive 2026-08-20): archiving the LAST
      // thread lands the user in a fresh GREETED pane — welcome + starter
      // chips, never a blank void. Same seed path as newThread; when the
      // composer is not ready yet the seed is [] and greet-on-focus heals
      // the pane the moment it can. Session-mint registration keeps the
      // new draft exempt from the live containment effect while unbound.
      sessionMintedThreadIdsRef.current.add(created.id);
      const fallbackSeed = composeGreetingSeed();
      dispatch({ type: 'SET_MESSAGES', payload: fallbackSeed });
      activeThreadIdRef.current = created.id;
      setActiveThreadId(created.id);
      writeActiveThreadToServer(created.id);
      safelyPersistThreadMessages({
        instanceId,
        threadId: created.id,
        uid: currentUid,
        messages: fallbackSeed,
        source: 'remove-thread',
        writerToken: threadSyncWriterTokenRef.current,
      });
      writeThreadToServer(created);
    }

    // 5. Drop the live per-thread message key — the archive now owns
    //    the only copy of that history.
    removeFromLocalStorage(threadMsgKey(instanceId, threadId, currentUid));
    const sessionSuffix = `_${threadId}`;
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!key.startsWith('prest0_session_')) continue;
      if (!key.endsWith(sessionSuffix)) continue;
      removeFromLocalStorage(key);
    }
    return fallbackThreadId;
  }, [
    enabled,
    instanceId,
    currentUid,
    dispatch,
    storageKeys.THREAD_ARCHIVE,
    archiveThreadOnServer,
    addArchivedTombstone,
    writeActiveThreadToServer,
    writeThreadToServer,
    runActiveThreadHydrate,
    reconcileInterruptedServerBackedMessages,
    activeThreadIdRef,
    composeGreetingSeed,
  ]);

  const removeThread = useCallback((threadId: string) => {
    void removeThreadAndReturnFallback(threadId);
  }, [removeThreadAndReturnFallback]);

  const getArchivedThreads = useCallback((): ArchivedThread[] => {
    return getFromLocalStorage<ArchivedThread[]>(storageKeys.THREAD_ARCHIVE, []);
  }, [storageKeys.THREAD_ARCHIVE]);

  const markUnread = useCallback((threadId: string, delta = 1) => {
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        changed = { ...t, unreadCount: Math.max(0, t.unreadCount + delta) };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return next;
    });
  }, [writeThreadToServer]);

  /**
   * THE ONE CLEARER (PR-3, 2026-09-08): the viewer has looked at this thread
   * for the dwell — zero the count and stamp lastViewedAt (the comparator
   * every badge predicate reads), write the record through the index upsert
   * chokepoint, and for a SHARED thread also zero the server-owned unseen
   * counter on this viewer's index doc (its own update; the upsert never
   * writes that field). Idempotent: an already-clear record is left alone.
   */
  const markThreadViewed = useCallback((threadId: string, opts?: { shared?: boolean }) => {
    const nowIso = new Date().toISOString();
    // Read the record OUTSIDE the state updater (React may run updaters
    // lazily), so the server-counter zero below never depends on when it ran.
    const sharedRecord = latestThreadsRef.current.some((t) => t.id === threadId && t.sharedThread === true);
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        const activeMs = threadMillis(t.lastActiveAt);
        const viewedMs = threadMillis(t.lastViewedAt);
        const alreadyViewed = Number.isFinite(viewedMs) && (!Number.isFinite(activeMs) || viewedMs >= activeMs);
        if (t.unreadCount === 0 && alreadyViewed) return t;
        changed = { ...t, unreadCount: 0, lastViewedAt: nowIso };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return next;
    });
    if (opts?.shared || sharedRecord) {
      const uid = getServerSyncUid();
      if (uid) void clearThreadUnseen(uid, threadId);
    }
  }, [writeThreadToServer, getServerSyncUid, latestThreadsRef]);

  // Per-thread bell mirror (2026-09-08): same shape as markUnread — patch the
  // local record, write it through the ONE index upsert chokepoint.
  const setThreadNotifyMode = useCallback((threadId: string, mode: ChatThread['notifyMode']) => {
    if (!mode) return;
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId || t.notifyMode === mode) return t;
        changed = { ...t, notifyMode: mode };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return changed ? next : prev;
    });
  }, [writeThreadToServer]);

  // T1 (thread-workflow UX): toggle a thread's pinned state. Pinned threads
  // order FIRST in every renderer (orderThreads); pin order = pinnedAt asc.
  // Persisted on the thread index doc (additive merge), so pins survive
  // reloads and sync across devices via the existing snapshot.
  const togglePinThread = useCallback((threadId: string) => {
    if (!enabled) return;
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        changed = t.pinned === true
          ? { ...t, pinned: false, pinnedAt: undefined }
          : { ...t, pinned: true, pinnedAt: new Date().toISOString() };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return next;
    });
  }, [enabled, writeThreadToServer]);

  /**
   * OPTIMISTIC routine-mirror patch (routine header chip + manager panel,
   * 2026-08-01). LOCAL-ONLY by design: the routine mirror stays server-owned
   * — `toChatThreadDoc` deliberately omits every routine* field, so this can
   * never write routine state to the index. It exists because the mirror's
   * echo path is best-effort twice over: the server's applyRoutineMirror
   * write is fire-and-forget, and the index subscription only covers the
   * capped most-recently-active window (a paused thread stops advancing
   * lastActiveAt and ages out — the 2026-07-31 "Pause shown for a paused
   * routine" production drift). A successful pause/resume (or the manager's
   * authoritative listRoutines() snapshot) patches the local record so every
   * surface (header, sidebar, manager) flips together; the next index
   * snapshot that covers the thread reconciles server-side truth on top
   * (carryServerRoutineMirror / the tie rule).
   *
   * Guards: only threads that CURRENTLY carry `routineId` accept a patch — a
   * cancel that already cleared the mirror must not be resurrected by a late
   * success callback. No server write, no lastActiveAt bump.
   */
  const patchThreadRoutineMirror = useCallback((threadId: string, patch: ThreadRoutineMirrorPatch) => {
    if (!enabled) return;
    setThreads(prev => {
      let changed = false;
      const next = prev.map(t => {
        if (t.id !== threadId || !t.routineId) return t;
        const merged: ChatThread = { ...t };
        const mergedRecord = merged as unknown as Record<string, unknown>;
        let rowChanged = false;
        for (const [field, value] of Object.entries(patch)) {
          if (value === undefined) continue; // absent patch field = leave as-is
          if (mergedRecord[field] !== value) {
            mergedRecord[field] = value;
            rowChanged = true;
          }
        }
        if (!rowChanged) return t;
        changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
  }, [enabled]);

  const renameThread = useCallback((threadId: string, title: string, opts?: { byUid?: string | null }) => {
    const clean = (title || '').trim().slice(0, TITLE_MAX_LEN) || DEFAULT_TITLE;
    // Bug G: stamp `userRenamed=true` so the UI-language backfill
    // pass and the inference effect both leave the user's chosen
    // chip name alone forever after.
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        if (t.title === clean && t.userRenamed === true) return t;
        // PR-5 (2026-09-09): the rename joins the record's name history
        // (oldest first, capped) so the former name stays searchable.
        const entry: ThreadTitleHistoryEntry = {
          title: clean,
          at: Date.now(),
          ...(t.title && t.title !== clean ? { previousTitle: t.title } : {}),
          ...(opts?.byUid ? { byUid: opts.byUid } : {}),
        };
        const history = [...(Array.isArray(t.titleHistory) ? t.titleHistory : []), entry];
        changed = {
          ...t,
          title: clean,
          userRenamed: true,
          titleHistory: history.length > THREAD_TITLE_HISTORY_MAX
            ? history.slice(history.length - THREAD_TITLE_HISTORY_MAX)
            : history,
        };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return next;
    });
  }, [writeThreadToServer]);

  // PR-5 (2026-09-09): personal organization — the category id rides the
  // viewer's own index doc through the ordinary upsert. Clearing writes an
  // empty string (a merge write needs a value to overwrite the stored id);
  // the parser reads '' back as "no category".
  const setThreadCategory = useCallback((threadId: string, categoryId: string | null) => {
    if (!enabled) return;
    const nextCategory = (categoryId ?? '').trim();
    setThreads(prev => {
      let changed: ChatThread | null = null;
      const next = prev.map(t => {
        if (t.id !== threadId) return t;
        if ((t.category ?? '') === nextCategory) return t;
        changed = { ...t, category: nextCategory };
        return changed;
      });
      if (changed) writeThreadToServer(changed);
      return next;
    });
  }, [enabled, writeThreadToServer]);

  /**
   * Hard-reset the thread store. See interface doc on `UseChatThreadsResult`.
   *
   * Sequence:
   *   1. Wipe live React state (threads + activeThreadId) so the UI
   *      flips to "no threads" momentarily.
   *   2. Purge every per-thread message key for this instance from
   *      localStorage + IndexedDB. This is the step the regular
   *      mirror-effect cannot do because `safelyPersistThreadMessages`
   *      blocks empty-over-non-empty writes.
   *   3. Drop the THREAD_LIST, ACTIVE_THREAD, and THREAD_ARCHIVE keys.
   *   4. Cancel any in-flight inference controllers (otherwise a
   *      pending label inference resolves into a setThreads on a now-
   *      reset list, re-introducing a phantom).
   *   5. Bootstrap a fresh empty thread (localized default title) so
   *      the user lands on a clean rail rather than a "no threads"
   *      empty state.
   *   6. Dispatch SET_MESSAGES([]) so React state mirrors the wipe.
   */
  const resetAllThreads = useCallback((): string | null => {
    if (!enabled) return null;
    // Snapshot in-flight controllers before clearing — abort each one so
    // pending inferences don't race a setThreads back onto a now-reset list.
    const controllers = Array.from(inferenceAbortByThreadRef.current.values());
    inferenceAbortByThreadRef.current.clear();
    lastIncorporatedUserMsgIdRef.current.clear();
    pendingServerCreateIdsRef.current.clear();
    // ARCHIVE-ALL SEMANTICS (Miles 2026-07-12): reset is "archive everything
    // and start fresh", NOT a local wipe. The old code CLEARED the tombstones
    // and never touched the server docs — so the index snapshot re-delivered
    // every unarchived thread moments later and "old threads popped back"
    // (owner repro). Tombstone every current thread (blocks re-adoption
    // instantly) and archive each server-side (sticky one-way, same as the
    // per-chip × path). Prest0n can still reference archived conversations.
    // WORKSPACE-SCOPED RESET (multi-org separation round 2, 2026-08-19):
    // "Clear chat history" clears the workspace the user is IN — threads
    // bound to it plus unbound legacy — never another organization's
    // conversations (the cross-org wipe was Fernando's "only one empty
    // thread" amplifier). Foreign-workspace records ride through untouched.
    const activeWorkspaceForReset = groupReadAccountId ?? getActiveWorkspaceAccount();
    const allCurrentThreads = latestThreadsRef.current;
    const inScopeThreads = activeWorkspaceForReset
      ? allCurrentThreads.filter((t) => !t.accountId || t.accountId === activeWorkspaceForReset)
      : allCurrentThreads;
    const keptForeignThreads = activeWorkspaceForReset
      ? allCurrentThreads.filter((t) => t.accountId && t.accountId !== activeWorkspaceForReset)
      : [];
    const idsToArchive = inScopeThreads.map((t) => t.id);
    for (const id of idsToArchive) {
      // (2026-07-13 fix: this was `Map.add`, which doesn't exist — a runtime
      // TypeError that aborted resetAllThreads whenever any thread existed.)
      archivedTombstonesRef.current.set(id, Date.now() + ARCHIVED_TOMBSTONE_TTL_MS);
    }
    persistDurableTombstones(instanceId, currentUid, archivedTombstonesRef.current);
    {
      const uid = getServerSyncUid();
      if (uid) {
        for (const id of idsToArchive) {
          void archiveChatThread(uid, instanceId, id).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('chatThreads.resetAll server archive failed', { threadId: id, error });
          });
        }
      }
    }
    for (const c of controllers) {
      try { c.abort(); } catch { /* ignore */ }
    }

    // Purge per-thread storage. No foreign threads kept → the full sweep
    // (localStorage + IDB, exact legacy behavior); otherwise only the
    // in-scope transcripts go (foreign conversations stay intact).
    if (keptForeignThreads.length === 0) {
      purgeAllThreadStorage(instanceId, currentUid);
    } else {
      for (const id of idsToArchive) {
        try { removeFromLocalStorage(threadMsgKey(instanceId, id, currentUid)); } catch { /* ignore */ }
      }
    }

    // Drop the per-instance thread metadata keys.
    try { removeFromLocalStorage(storageKeys.THREAD_LIST); } catch { /* ignore */ }
    try { removeFromLocalStorage(storageKeys.ACTIVE_THREAD); } catch { /* ignore */ }
    try { removeFromLocalStorage(storageKeys.THREAD_ARCHIVE); } catch { /* ignore */ }

    // Bootstrap a fresh empty thread keyed off the user's UI language.
    const lang = getUserUiLanguage();
    const created = createEmptyThread({
      title: localizedDefaultTitle(lang),
      hasUserContent: false,
    });
    pendingServerCreateIdsRef.current.add(created.id);
    // Session-mint registration (2026-08-20 churn audit): every other mint
    // site registers — without it, a reset that lands before the workspace
    // mirror publishes leaves this thread permanently unbound (invisible in
    // an org rail: never back-stamped, and the containment exemption misses).
    sessionMintedThreadIdsRef.current.add(created.id);

    // Persist the thread record + active id + seed IMMEDIATELY (before
    // setThreads/setActiveThreadId state propagates) so a fast re-read
    // (cross-tab BroadcastChannel listener, snapshot-active call from
    // a queued switch) sees a coherent post-reset on-disk shape. Like
    // newThread, the bootstrap thread opens with the persisted personalized
    // greeting when the host wired a composer (still an EMPTY draft:
    // hasUserContent:false above).
    const seedMessages = composeGreetingSeed();
    setToLocalStorage(storageKeys.THREAD_LIST, [created, ...keptForeignThreads]);
    setToLocalStorage(storageKeys.ACTIVE_THREAD, created.id);
    safelyPersistThreadMessages({
      instanceId,
      threadId: created.id,
      uid: currentUid,
      messages: seedMessages,
      source: 'bootstrap',
      writerToken: threadSyncWriterTokenRef.current,
    });

    setThreads([created, ...keptForeignThreads]);
    setActiveThreadId(created.id);
    // Reset the mirror-effect tracker so the next mirror pass doesn't
    // log a "thread changed mid-mirror" telemetry warn.
    prevMirroredActiveTidRef.current = created.id;

    dispatch({ type: 'SET_MESSAGES', payload: seedMessages });
    writeThreadToServer(created);
    writeActiveThreadToServer(created.id);
    return created.id;
  }, [
    enabled,
    instanceId,
    currentUid,
    dispatch,
    composeGreetingSeed,
    storageKeys.THREAD_LIST,
    storageKeys.ACTIVE_THREAD,
    storageKeys.THREAD_ARCHIVE,
    writeActiveThreadToServer,
    writeThreadToServer,
    groupReadAccountId,
  ]);

  // I4: active-thread paging signals (uid+thread keyed, see earlierPagingRef).
  const activeEarlierKey = activeThreadId ? earlierPagingKey(activeThreadId) : null;
  const earlierMessagesAvailable = !!(activeEarlierKey && earlierAvailableByKey[activeEarlierKey]);
  const isLoadingEarlierMessages = !!(activeEarlierKey && loadingEarlierByKey[activeEarlierKey]);

  // MULTI-ORG P4 → STRICT (owner directive 2026-08-18): the exposed list is
  // workspace-separated — a thread only ever RENDERS inside its own
  // organization's portal. The 2026-08-19 owner directive brings the
  // extended ("all workspaces") view back as a DISCOVERY surface on top of
  // that containment: `allWorkspaceThreads` rides along for it, and opening
  // a foreign row runs the sealed organization switch (overlay → verified
  // switch → reload into the thread's own org) — never an in-place render.
  // WORKSPACE CONTAINMENT, live half (owner directive 2026-08-19: "entirely
  // separated views even security etc."): the boot guard picks a contained
  // active thread from what it can see SYNCHRONOUSLY, but the workspace can
  // resolve after boot (or the cache can be stale after rapid switches), and
  // a thread's binding can land from the server later. Whenever the resolved
  // workspace or the list changes, a foreign-BOUND active thread is closed in
  // place: switch to this workspace's newest thread, else mint a fresh draft
  // (newThread — reuse-aware, workspace-guarded). Unbound threads are left
  // alone here — session-minted drafts are being bound-at-birth to THIS
  // workspace, and the boot guard already scoped stored ones.
  useEffect(() => {
    if (!enabled || !ready) return;
    const ws = (groupReadAccountId || '').trim();
    if (!ws) return;
    const activeId = activeThreadIdRef.current;
    if (!activeId) return;
    const record = latestThreadsRef.current.find((t) => t.id === activeId) || null;
    const bound = ((record?.accountId) || '').trim();
    if (!bound || bound === ws) return;
    if (
      pendingServerCreateIdsRef.current.has(activeId)
      || sessionBootstrapDraftIdsRef.current.has(activeId)
      // Durable half of the exemption (2026-08-20): the pending-create entry
      // is deleted by the first send's own create echo, which used to strip
      // the exemption exactly when the send promoted the draft — the yank
      // below then swallowed the just-typed message mid-turn.
      || sessionMintedThreadIdsRef.current.has(activeId)
    ) {
      return; // this session is minting it INTO this workspace
    }
    // SEND-SAFETY (2026-08-20 owner repro): never close the active thread
    // out from under an in-flight turn — the pane swap orphans the typed
    // message (it titles the abandoned thread) and the reply then follows
    // the NEW active pane. A turn is in flight when the newest real pane
    // message is the user's, or an assistant message still streaming. The
    // effect re-fires on the next threads change, after the turn settles.
    const liveForGuard = getLatestMessagesRef.current ? getLatestMessagesRef.current() : null;
    if (Array.isArray(liveForGuard)) {
      for (let i = liveForGuard.length - 1; i >= 0; i--) {
        const m = liveForGuard[i] as ChatMessage & { isSystem?: boolean };
        if (!m || m.isSystem) continue;
        // Status read is deliberately loose: the pane carries transient
        // streaming shapes beyond ChatMessage's persisted status union.
        const status = (m as unknown as { status?: string }).status;
        const inFlight = m.isUser === true
          || status === 'streaming' || status === 'processing' || status === 'pending';
        if (inFlight) {
          // eslint-disable-next-line no-console
          console.warn('[useChatThreads] containment: deferred — active thread has a turn in flight', {
            threadId: activeId, boundTo: bound, workspace: ws,
          });
          return;
        }
        break;
      }
    }
    // eslint-disable-next-line no-console
    console.warn('[useChatThreads] containment: active thread is bound to another workspace — closing it here', {
      threadId: activeId, boundTo: bound, workspace: ws,
    });
    const fallback = orderThreads(latestThreadsRef.current)
      // `archived !== true` and the tombstone check for the same reason as the
      // archive hold's own pick above: containment must land somewhere the
      // door will actually open, or it closes the foreign thread and leaves
      // the viewer nowhere.
      .find((t) => threadAllowedInWorkspace(t, ws, currentUid)
        && t.id !== activeId
        && t.archived !== true
        && !isThreadTombstoned(t.id));
    if (fallback) {
      switchThread(fallback.id);
    } else {
      newThread();
    }
  }, [enabled, ready, groupReadAccountId, threads, currentUid, switchThread, newThread, isThreadTombstoned]);

  const workspaceThreads = useMemo(
    // HOME-WORKSPACE EXCEPTION (2026-08-19): unbound threads surface in the
    // user's own uid-keyed workspace (and only there) instead of vanishing
    // from every rail until a sweep binds them — see workspaceThreadFilter.
    () => filterThreadsForWorkspace(threads, groupReadAccountId, { homeAccountId: currentUid }),
    [threads, groupReadAccountId, currentUid],
  );

  /**
   * OLDER THREADS (2026-09-22) — the page BELOW the live window.
   *
   * The listeners deliver the newest WORKING_SET_LIMIT threads and nothing
   * else, and only real activity re-stamps `lastActiveAt`, so a thread that
   * ages out can never come back on its own. This is the door back to it.
   *
   * Scoped to the active workspace so a quiet org does not have to page through
   * a busy one's recency; foreign rows keep arriving on the global stream and
   * `filterThreadsForWorkspace` keeps them off the rail as before.
   */
  const olderThreadsScope = `${currentUid || ''}|${instanceId}|${(groupReadAccountId || '').trim()}`;
  useEffect(() => {
    // A cursor is a position inside ONE ordered query. Changing the identity,
    // the surface or the workspace changes the query, so the old cursor would
    // page through a sequence that no longer exists.
    if (olderThreadsScopeRef.current === olderThreadsScope) return;
    olderThreadsScopeRef.current = olderThreadsScope;
    olderThreadsCursorRef.current = null;
    setOlderThreadsExhausted(false);
  }, [olderThreadsScope]);

  const loadOlderThreads = useCallback(async (): Promise<number> => {
    const uid = getServerSyncUid();
    if (!uid || !enabled || !ready) return 0;
    if (loadingOlderRef.current || olderThreadsExhausted) return 0;
    loadingOlderRef.current = true;
    setIsLoadingOlderThreads(true);
    const pageScope = olderThreadsScopeRef.current;
    try {
      const page = await loadOlderThreadsPage(
        uid,
        instanceId,
        olderThreadsCursorRef.current,
        THREAD_PAGE_SIZE,
        { accountId: (groupReadAccountId || '').trim() || null },
      );
      // The scope changed while the read was in flight: this page describes a
      // workspace the user has left. Drop it rather than merging it in.
      if (olderThreadsScopeRef.current !== pageScope) return 0;
      olderThreadsCursorRef.current = page.nextCursor ?? null;
      if (page.items.length < THREAD_PAGE_SIZE) setOlderThreadsExhausted(true);
      const fresh = page.items.filter((t) => t.archived !== true && !isThreadTombstoned(t.id));
      if (fresh.length === 0) return 0;
      let added = 0;
      setThreads((prev) => {
        const known = new Set(prev.map((t) => t.id));
        added = fresh.reduce((n, t) => (known.has(t.id) ? n : n + 1), 0);
        // The IDB-recovery idiom: the local copy always wins field-wise, the
        // page only contributes records the list does not already hold.
        return mergeThreadListsNewestWins(prev, fresh);
      });
      return added;
    } catch (error) {
      // LOUD on purpose. If a composite index is declared but was never built
      // in the live project, this is where FAILED_PRECONDITION surfaces — and
      // a silent catch here would look exactly like "there is nothing older".
      // eslint-disable-next-line no-console
      console.error('[useChatThreads] could not load older threads', {
        instanceId,
        workspace: (groupReadAccountId || '').trim() || null,
        error: error instanceof Error ? error.message : String(error),
      });
      return 0;
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlderThreads(false);
    }
  }, [enabled, ready, instanceId, groupReadAccountId, olderThreadsExhausted, isThreadTombstoned]);

  // RAIL-EMPTY TRIPWIRE (owner report 2026-08-20: "saw threads disappear for
  // a hot second"): the visible rail emptied while the store still held
  // threads — either the workspace identity flapped for a frame or a
  // reconcile/back-stamp pass briefly reclassified every thread as foreign.
  // Attribution breadcrumb only (one warn per transition, cheap breakdown
  // computed only when tripping) — the repair, if one is needed, follows the
  // evidence this collects in production.
  const railEmptyTripRef = useRef(false);
  useEffect(() => {
    if (workspaceThreads.length === 0 && threads.length > 0) {
      if (!railEmptyTripRef.current) {
        railEmptyTripRef.current = true;
        const byBinding: Record<string, number> = {};
        for (const t of threads) {
          const key = t.accountId ? String(t.accountId) : '(unbound)';
          byBinding[key] = (byBinding[key] || 0) + 1;
        }
        // eslint-disable-next-line no-console
        console.warn('[useChatThreads] rail-empty tripwire: 0 visible while store has threads', {
          stored: threads.length,
          workspace: groupReadAccountId || null,
          homeUid: currentUid || null,
          byBinding,
        });
      }
    } else {
      railEmptyTripRef.current = false;
    }
  }, [workspaceThreads, threads, groupReadAccountId, currentUid]);

  return {
    threads: workspaceThreads,
    /** Every workspace's threads — extended-view discovery ONLY (org-grouped
     *  sections; foreign rows switch organizations, never open in place). */
    allWorkspaceThreads: threads,
    /**
     * Older threads exist BELOW the live window and can be paged in. False for
     * an account whose window was never full — there is nothing below it.
     */
    olderThreadsAvailable: windowSaturated && !olderThreadsExhausted,
    isLoadingOlderThreads,
    loadOlderThreads,
    activeThreadId,
    getActiveThreadIdSync,
    ready,
    threadSyncWriterToken: threadSyncWriterTokenRef.current,
    newThread,
    switchThread,
    removeThread,
    removeThreadAndReturnFallback,
    getArchivedThreads,
    markUnread,
    markThreadViewed,
    activeThreadReceipts,
    activeThreadReactions,
    activeThreadSharedOrderTs,
    activeThreadLiveTurns,
    activeThreadTyping,
    setThreadNotifyMode,
    togglePinThread,
    renameThread,
    setThreadCategory,
    patchThreadRoutineMirror,
    resetAllThreads,
    earlierMessagesAvailable,
    isLoadingEarlierMessages,
    loadEarlierMessages,
    /** Server-hydrate status for the ACTIVE thread — the view renders a
     *  retry affordance only when the pane is empty AND status is 'error'
     *  ('loading' may flash on local-full switches; ignore it unless the
     *  pane is empty). */
    activeThreadHydrateStatus: activeHydrateStatus,
    retryActiveThreadHydrate,
  };
}
